import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '@/lib/api';
import { cx } from '@/lib/cx';
import { formatDateOnly, formatPending } from '@/lib/format';
import { preferencesStore } from '@/lib/preferences-store';
import { useSavedFilterApply, useSavedFilters } from '@/lib/use-saved-filters';
import type {
  BillingStatus,
  ClientDeliverable,
  ClientDeliverableListParams,
  ClientDeliverableSort,
  ClientDeliverableSource,
  EngagementRole,
  LaeSpecialist,
  ListOrder,
  Paginated,
} from '@/lib/types';
import { useCycleOptions } from '@/lib/use-cycle-options';
import { NAV_SURFACE_MODULE, denialReason } from '@/lib/permissions';
import type { Ability } from '@/lib/permissions';
import { useAuth } from '@/lib/use-auth';
import {
  DEFAULT_PROJECT_CYCLE_SCOPE,
  PROJECT_CYCLE_SCOPES,
  PROJECT_CYCLE_SCOPE_LABELS,
  projectOptionsByScope,
  projectScopeFootnote,
  type ProjectCycleScope,
} from '@/lib/project-cycle-scope';
import { useCycleScopeIds } from '@/lib/use-line-project';
import type { Project } from '@/lib/types';
import { splitByCompanyGroup } from '@/lib/company-group';
import { inclusionsLabel } from '@/lib/project-capacity';

/**
 * Every ACTIVE project, all pages — for the Project filter.
 *
 * ⚠️ *SUPERSEDED: `useProjectOptions()`*, which reads ONE page of 100 while
 * production holds more active projects than that, so the end of the alphabet
 * was missing from this filter with nothing saying so. A failed page throws and
 * the filter shows its load error rather than a list that silently stops short.
 */
function useAllActiveProjects(): { projects: Project[]; loading: boolean; failed: boolean } {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    api
      .listAllProjects(controller.signal, { isActive: true, sort: 'name', order: 'asc' })
      .then((rows) => {
        if (controller.signal.aborted) return;
        setProjects(rows);
        setFailed(false);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setProjects([]);
        setFailed(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, []);
  return { projects, loading, failed };
}
import { useReferenceOptions } from '@/lib/use-reference-options';
import { useUserOptions } from '@/lib/use-user-options';
import { useTableFilters, type ColumnSpec } from '@/lib/use-table-filters';
import { useRememberedState } from '@/lib/filter-memory/use-filter-memory';
import {
  useColumnLayout,
  useFrozenOffsets,
  type ColumnLayoutController,
} from '@/lib/use-column-layout';
import {
  Badge,
  Button,
  ColumnManager,
  Combobox,
  ConfirmDialog,
  EmptyState,
  Field,
  FilterableTH,
  FilterSheet,
  Input,
  MultiCombobox,
  PageHeader,
  Panel,
  PanelFooter,
  PanelHeader,
  Skeleton,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
  ValuePills,
  SegmentedControl,
  Tooltip,
} from '@/components/ui';
import type { ComboboxOption, FilterableTHProps } from '@/components/ui';
import { fetchAllPages } from '@/lib/use-show-all';
import {
  AdsPlanField,
  BillingNoteField,
  BookingSpecField,
  CompletedField,
  PromisedField,
  DeliveryLeadField,
  EndDateField,
  EngagementRoleField,
  LineNoteTrackerField,
  ReadOnlySpecialist,
  SpecialistField,
  StartDateField,
  TravelField,
  travelLabel,
  useTrackerRowWriter,
  type TrackerRowWriter,
} from '@/components/client-deliverables/InlineTrackerFields';
import { DeliverableFormModal } from '@/components/client-deliverables/DeliverableFormModal';
import { SplitDeliverableModal } from '@/components/client-deliverables/SplitDeliverableModal';
import { LinkDeliverableModal } from '@/components/client-deliverables/LinkDeliverableModal';
import { useLaeSpecialists } from '@/lib/use-lae-specialists';
import { TrashIcon } from '@/components/catalog/icons';
import { Notice } from '@/components/ui/Notice';
import { TextLink } from '@/components/ui/TextLink';
import { ViewToggle as ViewTogglePrimitive } from '@/components/ui/ViewToggle';
/*
 * ⚠️ **TWO FILES, ONE SHAPE — and the shape is documented in the invoice one.**
 * `InvoiceNumberLink.tsx` is the specification both halves follow; the quote
 * counterpart mirrors it deliberately rather than merging into it, so a change
 * to one document's form cannot break the other. Read the invoice file's header
 * before touching either.
 *
 * ⚠️ **Each pair is a LINK (per number) and a HOST (per surface).** The host
 * owns the fetch, the failure notice, the Xero report and the single mounted
 * form; the link holds no state and reaches it through context. **A link with no
 * host above it navigates** — which is also what a reader without the write
 * permission gets — so there is one code path for "this is an ordinary link".
 */
import { InvoiceNumberLink, InvoiceQuickEdit } from '@/components/invoices/InvoiceNumberLink';
import { QuoteNumberLink, QuoteQuickEdit } from '@/components/quotes/QuoteNumberLink';
import {
  DeliverablesTabToggle,
  EventCapacityPanel,
  deliverablesTabOf,
} from '@/components/client-deliverables/EventCapacityPanel';
import { OpenTrackerSheetButton } from '@/components/tracker-sheet/OpenTrackerSheet';
import {
  TrackerSheetPanel,
  trackerSheetSortValueOf,
} from '@/components/tracker-sheet/TrackerSheetPanel';
import { useTrackerSheetLink } from '@/lib/use-tracker-sheet';
import { useTrackerSheetSort } from '@/lib/use-tracker-sheet-sort';

/*
 * THE CLIENT DELIVERABLES TRACKER — a query over quote and invoice LINES.
 *
 * This replaces a hand-kept workbook, and it replaces the hand-entered
 * `/deliverables` entity beside it. Its columns are the workbook's columns and
 * nothing else, which is why this page does NOT reuse `DeliverablesPage`'s set:
 * the narrower column list is the whole point of the change, not an omission.
 *
 * ⚠️ EVERYTHING ABOVE `completed` IS DERIVED and this app never writes it. A row
 * is a line item; a section is a project; "Promised" is the line quantity. There
 * is no company, product or intake picker here because there is nothing to pick
 * — those are read through the line, and a stored copy would quietly disagree
 * with the invoice the customer was sent. `pending` is derived too, and it is
 * signed: it moves when `completed` is saved, and it comes back recomputed on
 * the response rather than being worked out here.
 *
 * 🔴 **`travel` IS NO LONGER IN THAT SENTENCE — 2026-09-09, owner's decision.**
 * *SUPERSEDED: "`travel` and `pending` are derived too".* The derivation still
 * runs and is still what most rows show; what is new is that a person may
 * overrule it, on `travelOverride`, and hand the row back by clearing. See
 * `TravelField` — and note that `pending` did NOT move with it: it is computed
 * from two counts and there is nothing there for anybody to override.
 *
 * ✅ THE TYPED FIELDS *ARE* EDITED HERE, in place, since 2026-08-31 — the keys of
 * `ClientDeliverableTypedFields` and no others; count that interface rather than
 * a number here, which has been wrong twice. The write half lives in
 * `@/components/client-deliverables/InlineTrackerFields`; read its header before
 * adding a control, because the verb depends on `row.id` and getting that wrong
 * is silent. Nothing on this page mounts a form modal: a row is a line item, not
 * a record somebody creates here.
 *
 * 🔴 **AND NOT EVERY ROW MAY BE EDITED — `rowIsEditable` decides, on `source`.**
 * The two INCLUSION populations derive from a campaign inclusion sitting on a
 * line rather than from the line itself, and they have no overlay row to write
 * to: `id` is `null` permanently and `completed` is a structural `0`. They still
 * carry a `line`, though, so the writer's own `row.id === null` verb test would
 * send a save down the `PUT` upsert path naming that line — landing the figure
 * on the PARENT LINE's deliverable with a `200` and nothing failing. Every typed
 * column therefore renders a read-only value on those rows. ⛔ **A ninth typed
 * column must be gated the same way**; `trackerCell` reads the gate once so
 * there is one place to put it.
 *
 * ⚠️ **THE "POC" COLUMN IS GONE — removed 2026-08-31 on the owner's
 * instruction, in the same pass that retired Owner on companies, contacts,
 * deals, quotes and invoices.** It rendered `accountOwner`, and
 * `accountOwnerId` has left `ClientDeliverableTypedFields` with it, so the
 * typed set is SEVEN keys and no `PATCH` or `PUT` upsert built here can name
 * it. The column and the read survive server-side; this app stopped rendering
 * and writing them.
 *
 * ⚠️ The warning that stood here is worth keeping in one respect, because it
 * describes a mistake somebody would make bringing the column BACK:
 * `accountOwner` is SEED STAFF (a `d_users` row), while the old entity's
 * `pointOfContact` is a `d_contacts` row — a person at the client. One word
 * apart, different people, and mislabelling one as the other files an employee
 * as a customer contact on every row with nothing failing.
 */

/** `perPage` is 1..100 and the server defaults to 50 — the tracker is read whole. */
const PER_PAGE = 50;
/** Fewer than a full page: enough to read as a list, not a wall of shimmer. */
const SKELETON_ROWS = 6;
/** One page of picker options. Companies and products search server-side beyond it. */
const OPTION_PER_PAGE = 100;

/**
 * The columns, and the `colSpan` of a project's group header row.
 *
 * Derived from the header list below rather than written as a literal, so a
 * column added or removed cannot leave the group header spanning the wrong
 * width — which does not fail, it just draws a rule that stops short. ⚠️ Count
 * the list; the number that used to be written in this sentence went stale the
 * moment Ads plan arrived, which is the second thing this derivation prevents.
 *
 * ⚠️ **Billing status sits SECOND, out of the workbook's order, deliberately.**
 * This table's `min-w` sits in a content area of viewport minus a ~256px
 * sidebar; in the workbook's own order Billing status began roughly 1584px in,
 * so at 1440 it was off the right edge behind a horizontal scroll nothing
 * signalled — and it is the column the tracker exists to answer. Pinning it was
 * the alternative and does not fit: `TH`/`TD`'s `pinned` is `sticky right-0`,
 * which can only hold the LAST column, and Billing status is not last however
 * the rest are arranged. So it moves left, next to the Client that identifies
 * the row.
 *
 * ⚠️ **Ads plan is LAST, which is the workbook's own place for it** — column Q,
 * the final column, on the live "New 2026-27" sheet. Moving Billing status left
 * is the ONE deliberate departure from the workbook's order and it is a
 * departure that pulled a column towards the reader; a new column takes the
 * position the sheet gives it. Nothing is `pinned` on this table, so the last
 * position carries no other meaning.
 *
 * 🔴 **THE COLUMN SET IS NO LONGER THE WORKBOOK'S EXACTLY, and this sentence
 * used to promise that it was.** The owner removed the POC column
 * (`accountOwner`) on 2026-08-31; the workbook has it and this table does not.
 * That is a deliberate departure from the promise this page was built on, so it
 * is recorded here rather than quietly dropped — the people who kept the sheet
 * will notice a column missing, and "we removed it" is a better answer than a
 * comment still claiming parity.
 *
 * What survives: every OTHER column is the workbook's, and the one reordering
 * (Billing status, above) is still the only one.
 */
const COLUMN_KEYS = [
  'client',
  // Owner, 2026-10-18: the company's RECORDED type, right after the company.
  'companyType',
  'billingStatus',
  'regions',
  // ⚠️ **NOT A WORKBOOK COLUMN — 2026-09-13, owner's instruction, and its
  // position is chosen rather than inherited.** The sheet has no Cities column at
  // all, so every position is a departure; this one is the departure that costs
  // nothing. Inserting here keeps the RELATIVE order of every column the workbook
  // does have (POC through Ads plan all shift right together) and leaves Ads plan
  // last, which is the sheet's own place for it. Appending after Ads plan would
  // preserve the relative order too and would take that last position away from
  // it. Geography also reads as one block: Region of Focus, then the cities
  // inside it.
  'cities',
  'poc',
  // Owner, 2026-10-04: the LAE representative for the row, immediately after POC.
  'specialist',
  'intake',
  'product',
  // Owner, 2026-10-27: the line's campaign inclusions as text, read-only, right
  // after the product they ride on. The id is stored in layouts; never rename.
  'inclusions',
  'promised',
  'completed',
  'pending',
  'bookingSpec',
  'travel',
  'documents',
  // Owner, 2026-10-04: the old single Dates column became two, each with its own
  // editor and its own server-side From/To filter, matching the sheet.
  'startDate',
  'endDate',
  // The workbook's column P, restored 2026-09-02 — it was nested inside the
  // Billing Status cell while this page believed the workbook had no such
  // column. Reading the xlsx disproved that.
  'billingNote',
  // Owner, 2026-10-18: the source line's internal note, right after Billing
  // note — the same position the backend sheet mirror gives it.
  'lineNote',
  'adsPlan',
] as const;

type TrackerColumnKey = (typeof COLUMN_KEYS)[number];

/*
 * ⚠️ **`COLUMN_COUNT` IS GONE, AND ITS ONE READER WAS THE BUG.**
 * *SUPERSEDED: `const COLUMN_COUNT = COLUMN_KEYS.length;`* It was read in exactly
 * one place — the project group header's `colSpan` — and it counted the DECLARED
 * columns. The moment a reader could hide one, a constant derived from the static
 * array over-spans: the rule runs past the last rendered column and the browser
 * pads a phantom cell to make the row add up. Nothing throws, nothing logs, and
 * the only symptom is a section rule that is too long, which reads as styling.
 * The span is now `columns.visibleKeys.length`, taken from the same controller
 * the header and body map over, so the three cannot disagree.
 *
 * The derivation the old constant existed for is NOT retired — it is stronger.
 * See `COLUMN_CHROME` below: every one of the three parallel render surfaces
 * (header, body, skeleton) now maps the SAME key list, and a key added to
 * `COLUMN_KEYS` fails `tsc` in two places until it is given chrome and a cell.
 */

/**
 * Which columns may never be hidden.
 *
 * 🔴 **ONLY `client`, and the eight editor-hosting columns are DELIBERATELY NOT
 * LOCKED.** `client` is the cell that says which company's line a row is — hide
 * it and every remaining cell describes a record the reader cannot name, which
 * is not a narrower table, it is an unreadable one. It is also the only column
 * with no editor in it, so locking it costs a reader nothing they would want.
 *
 * ⚠️ **`poc`, `specialist`, `promised`, `completed`, `travel`, `bookingSpec`,
 * `startDate`, `endDate`, `billingNote` and `adsPlan` each host the ONLY in-place
 * editor for their field, and this page has no row detail drawer** — so hiding one
 * really does remove the only desktop path to writing it. They are still hideable,
 * for two reasons: the sub-`lg` card list renders every one of them regardless of
 * this layout, so no field is ever unreachable in the app; and a locked column a
 * reader cannot switch off is a worse answer than a visible one they chose to
 * put away and can bring back from the same menu. **Locking them would also make
 * the manager mostly disabled checkboxes**, which reads as a broken control.
 *
 * ⛔ **Do not add a lock to "protect" an editor.** If a field ever becomes
 * genuinely unreachable when its column is hidden — because the card list stops
 * rendering it, say — the fix is the card list, not a lock here.
 */
const LOCKED_COLUMN_KEYS: readonly TrackerColumnKey[] = ['client'];

/**
 * Why each locked column is locked, in the reader's own terms.
 *
 * ⚠️ **A LOCK HAS SEVERAL REASONS AND THE GENERIC ONE IS WRONG HERE.**
 * `ColumnManager` falls back to "This column cannot be hidden", which says
 * nothing, and its previous fixed sentence was about an ACTIONS lane — which
 * this table does not have. `client` is locked for the opposite reason: it is
 * the only cell that names the record, and every other cell describes a row
 * nobody could identify without it.
 *
 * This is a **why-this-is-disabled** sentence, one of the four kinds the
 * no-helper-text prohibition explicitly keeps: a bare `disabled` makes a locked
 * column and a broken switch identical.
 */
function columnLockReasonOf(key: string): string | undefined {
  return key === 'client' ? 'Always first and frozen, so every row shows its client' : undefined;
}

/**
 * ⛔ **NOTHING ON THIS TABLE IS `pinned`, AND THE EMPTY LIST IS THE ASSERTION.**
 *
 * `TH`/`TD`'s `pinned` is `sticky right-0`, so it can only ever hold the LAST
 * column — which is why Billing status was MOVED to second position rather than
 * pinned (see `COLUMN_KEYS`), and why the last position here carries no meaning
 * beyond the workbook's own order. There is no actions lane on this table
 * either; the row's editors live in the cells they belong to.
 *
 * ⚠️ **`pinnedKeys` IS NOT `lockedKeys`.** Locked says a column cannot be hidden
 * or moved; pinned says which edge a cell already occupies. `client` is locked
 * AND is the single best freeze-left candidate on this table, so conflating the
 * two would make the one column the feature exists for unfreezable.
 *
 * ⚠️ **Do not "fix" this by pinning something.** Passing a key here suppresses
 * its freeze controls in the manager and reserves it a right-edge slot in the
 * measured offsets; doing that for a column the table does not actually render
 * `pinned` leaves a reader with no freeze control and no freeze.
 */
const PINNED_COLUMN_KEYS: readonly TrackerColumnKey[] = [];

/**
 * Client is always the first column and always frozen to the left edge —
 * owner's instruction 2026-10-12, the same arrangement as the sales tracker's
 * Company column. Code-pinned, so a stored layout cannot move, hide or unfreeze
 * it. Other columns may still be frozen beside it.
 *
 * ⛔ **Never in `useFrozenOffsets`' `pinnedKeys`**, which means the RIGHT edge.
 */
const LAYOUT_OPTIONS = Object.freeze({ pinnedLeftKeys: Object.freeze(['client']) });

/**
 * For `ColumnManager` only, so Client draws no freeze buttons. The hook and
 * `useFrozenOffsets` keep {@link PINNED_COLUMN_KEYS}, which stays empty.
 */
const MANAGER_PINNED_KEYS: readonly TrackerColumnKey[] = Object.freeze(['client']);

/**
 * Everything the three render surfaces need to draw one column, in ONE record.
 *
 * 🔴 **THIS EXISTS BECAUSE THE THREE SURFACES DRIFTED ONCE AND NOTHING SAW IT.**
 * The skeleton drew FOURTEEN cells against THIRTEEN columns for as long as the
 * column list had existed: `tsc` cannot count JSX siblings, the row renders
 * happily, and it only shows for the moment before data lands. Now the header's
 * width request, the body cell's cap and the skeleton's shimmer are three fields
 * of one entry, and the record is keyed by `TrackerColumnKey` — so a column added
 * to `COLUMN_KEYS` is a compile error here until it is given all three.
 *
 * ⚠️ **`head` and `body` MUST MOVE TOGETHER.** This table has no `table-fixed`
 * and no `<colgroup>`, so a `w-*` on the header is a REQUEST the content can
 * outvote and the body's `max-w-*` is what stops a long value widening the
 * column past it. Re-cutting one alone changes which of the two is binding, and
 * the table simply renders differently with nothing to fail.
 *
 * ⚠️ **`width` is the column's px contribution to the table's minimum width, and
 * it is the `head` class in pixels.** It has to be a NUMBER rather than a class
 * because the sum is computed at runtime, and ⛔ **Tailwind v4 scans source text,
 * so `min-w-[${n}px]` emits no rule at all** — the class lands in the DOM and
 * does nothing, which looks exactly like a table that chose not to have a floor.
 * It goes through `style`. Keep it equal to `head`: `w-36` is 144, `w-72` is 288.
 *
 * ⚠️ **`client` reads `w-72`, not the `w-52` it carried, and that is a CORRECTION
 * rather than a widening.** Its body cell has claimed `min-w-[18rem]` (288px)
 * since 2026-09-03 — a floor the auto layout may not go under — so the header's
 * 208px request was already being outvoted on every render. The three now agree
 * on the width the column actually takes.
 */
interface TrackerColumnChrome {
  /** The header's width request. Paired with `body` and `width`. */
  head: string;
  /** The body cell's cap (or, on `client`, its floor). */
  body?: string;
  /** Right-aligns and tabular-aligns, on the header, the cell and the shimmer. */
  numeric?: boolean;
  /** The shimmer this column draws while the first page is in flight. */
  skeleton: string;
  /** `head` in px — this column's share of the table's minimum width. */
  width: number;
}

const COLUMN_CHROME: Record<TrackerColumnKey, TrackerColumnChrome> = {
  client: { head: 'w-72', body: 'min-w-[18rem]', skeleton: 'h-3 w-40', width: 288 },
  companyType: { head: 'w-32', body: 'max-w-[9rem]', skeleton: 'h-3 w-20', width: 128 },
  billingStatus: { head: 'w-36', body: 'max-w-[11rem]', skeleton: 'h-4 w-24', width: 144 },
  regions: { head: 'w-36', skeleton: 'h-4 w-24', width: 144 },
  // A joined list of names, so it takes the same shape as `documents` and
  // `product`: a cap on the body rather than a floor, and the cell truncates
  // inside it. `head` and `width` agree — `w-44` is 176.
  // 320px (owner, 2026-09-25): cities render as wrapping pills now, and at the
  // old 176px an eight-city row ran to five lines; at 320 it is about three.
  cities: { head: 'w-80', body: 'max-w-80', skeleton: 'h-3 w-28', width: 320 },
  poc: { head: 'w-40', body: 'max-w-[10rem]', skeleton: 'h-3 w-28', width: 160 },
  specialist: { head: 'w-40', body: 'max-w-[10rem]', skeleton: 'h-3 w-28', width: 160 },
  intake: { head: 'w-32', body: 'max-w-[9rem]', skeleton: 'h-3 w-24', width: 128 },
  product: { head: 'w-48', body: 'max-w-[13rem]', skeleton: 'h-3 w-20', width: 192 },
  // Wrapping text, so a cap rather than a floor; `w-64` is 256.
  inclusions: { head: 'w-64', body: 'max-w-64', skeleton: 'h-3 w-32', width: 256 },
  promised: { head: 'w-20', numeric: true, skeleton: 'ml-auto h-3 w-8', width: 80 },
  completed: { head: 'w-24', numeric: true, skeleton: 'ml-auto h-3 w-8', width: 96 },
  pending: { head: 'w-28', skeleton: 'h-4 w-20', width: 112 },
  bookingSpec: { head: 'w-52', body: 'max-w-[14rem]', skeleton: 'h-3 w-36', width: 208 },
  travel: { head: 'w-24', skeleton: 'h-4 w-20', width: 96 },
  documents: { head: 'w-44', body: 'max-w-[12rem]', skeleton: 'h-3 w-24', width: 176 },
  startDate: { head: 'w-36', body: 'max-w-[10rem]', skeleton: 'h-3 w-20', width: 144 },
  endDate: { head: 'w-36', body: 'max-w-[10rem]', skeleton: 'h-3 w-20', width: 144 },
  billingNote: { head: 'w-48', body: 'max-w-[14rem]', skeleton: 'h-3 w-32', width: 192 },
  lineNote: { head: 'w-48', body: 'max-w-[14rem]', skeleton: 'h-3 w-32', width: 192 },
  adsPlan: { head: 'w-40', body: 'max-w-[14rem]', skeleton: 'h-3 w-24', width: 160 },
};

/**
 * The table's minimum width — the sum of the VISIBLE columns' declared widths.
 *
 * ⚠️ *SUPERSEDED: a hand-written `min-w-[1972px]` on the `Table`, in two places.*
 * A constant floor cannot shrink, so hiding ten columns would have left five
 * columns stretched across 1972px of empty table — which is not a narrower view,
 * it is the same scrollbar with gaps in it.
 *
 * ⚠️ **1972 WAS NOT BINDING AT FULL VISIBILITY AND THIS IS NOT A WIDENING.** The
 * fifteen header requests already summed to more than that (they sum to 2336
 * with `client` corrected to the 288px its body cell has always claimed), and a
 * `min-width` below the sum of a table's own column requests never applies. The
 * file's own note that "`min-w` is a FLOOR, not a sum" stays true: it is still a
 * floor, and the floor now knows which columns are on screen.
 */
function tableMinWidth(keys: readonly string[]): number {
  return keys.reduce((sum, key) => sum + (COLUMN_CHROME[key as TrackerColumnKey]?.width ?? 0), 0);
}

/*
 * ── The URL filters ─────────────────────────────────────────────────────────
 *
 * The list schema is `.strict()`, so an unknown key is a 422 naming it and an
 * EMPTY value is a 422 rather than "no filter". Nine filters, in two families:
 * six positive-integer ids and three closed enums. They are validated apart
 * because a pasted `?billingStatus=paidish` and a pasted `?companyId=abc` fail
 * for different reasons and only one of them looks like a typo.
 *
 * ⚠️ `accountOwnerId` was a SEVENTH id filter until 2026-08-31 and went with the
 * POC column. `/client-deliverables` still accepts it — this app stopped asking,
 * because a filter narrowing rows by a person no column on this table names any
 * more would narrow the list with nothing on screen saying so.
 */
const ID_FILTER_KEYS = [
  'companyId',
  'productId',
  'projectId',
  'intakeId',
  'cycleId',
  'regionId',
] as const;

const SAVED_ENUM_FILTER_KEYS = ['source', 'billingStatus', 'outstanding'] as const;

/*
 * `removed` (2026-10-14) is the REMOVED VIEW — `?removed=true` lists only the
 * rows somebody took off the tracker, so they can be restored. Absent is the
 * live tracker. It is an ordinary filter key (counted, cleared, resets the page)
 * but deliberately NOT remembered against the account: see SAVED_FILTER_KEYS.
 */
/*
 * `possibleDuplicate` (2026-10-16): `?possibleDuplicate=true` narrows to the
 * standalone rows the server suspects of repeating a document line. Not
 * remembered either, for `removed`'s reason — it is a view for cleaning up, and
 * a saved one would quietly hide the rest of the tracker on every arrival.
 */
const ENUM_FILTER_KEYS = [...SAVED_ENUM_FILTER_KEYS, 'removed', 'possibleDuplicate'] as const;

const FILTER_KEYS = [...ID_FILTER_KEYS, ...ENUM_FILTER_KEYS] as const;

type IdFilterKey = (typeof ID_FILTER_KEYS)[number];
type FilterKey = (typeof FILTER_KEYS)[number];

/** Ids and enum members are both held as strings: that is what Combobox and the URL speak. */
type Filters = Record<FilterKey, string>;

/**
 * What this page remembers against the account.
 *
 * Every filter key, because `clearFilters()` clears every one of them — that is
 * the test a remembered filter has to pass. A saved filter needs a way OFF the
 * screen, not a way on.
 *
 * ⚠️ **There is no `independentKeys` list, and `view` is deliberately NOT in
 * one.** *SUPERSEDED: "this page has no view toggle (there is no board — a
 * deliverable does not move through stages) and its page size is not a URL key,
 * so there is nothing here whose meaning is independent of the filters."* The
 * page-size half still holds; the view half stopped being true when the Google
 * Sheets mirror gained a tab, and this sentence outlived it.
 *
 * ⚠️ **Saving `view` would quietly defeat the owner's 2026-09-02 instruction
 * that the sheet is the default.** `independentKeys` merges PER KEY with the URL
 * winning, so a stored `table` — written the one time somebody looked at the
 * table — would apply on every later arrival at a bare `/client-deliverables`,
 * and that person would never meet the default again. A default and a remembered
 * choice are different things and only the first was asked for. Adding it is a
 * decision for the owner, not a key added to a list.
 */
/*
 * ⚠️ Every filter key EXCEPT `removed`. A remembered Removed view would open the
 * tracker on the rows nobody wants on it, on every later arrival, under the
 * tracker's own heading — the view is for restoring one row, not a place to live.
 */
const SAVED_FILTER_KEYS = [...ID_FILTER_KEYS, ...SAVED_ENUM_FILTER_KEYS] as const;
type SavedTrackerKey = (typeof SAVED_FILTER_KEYS)[number];

/*
 * The five sorts THIS TABLE'S COLUMN HEADERS OFFER. `sort` is a strict enum
 * server-side and an unrecognised member is a 422, not a fallback, so the column
 * filters' bare string is narrowed rather than cast.
 *
 * ⚠️ *SUPERSEDED 2026-09-02: "The five columns the API can order by."* **The API
 * takes SEVEN** — `billingStatus` and `documentDate` landed the same day and are
 * named on {@link ClientDeliverableSort}. This list is deliberately NARROWER than
 * the type, because it answers a different question: which sorts a header on this
 * table can express. Neither new member has a column here to hang off — billing
 * status is a badge and the document date is not a column at all — so adding them
 * to this list would let `?sort=` survive a round trip while no header ever showed
 * the ordering, which reads as a table that has quietly re-sorted itself.
 *
 * ⚠️ **So a URL naming one of the two falls back to `project` here, on purpose.**
 * The place those two ARE offered is the Google Sheets mirror's "Sort by" control,
 * which orders the SHEET and not this table. Widening this list is a table feature
 * and needs headers, not a member.
 *
 * ⚠️ *SUPERSEDED 2026-10-09, the "deliberately NARROWER" paragraph above, in
 * part.* Owner's instruction: every column with a funnel sorts on the server, so
 * every such column now carries a `sortKey` and this list names each of them —
 * `billingStatus` included, which has a column now. `documentDate` is still the
 * one member with no header here and still falls back to `project`.
 * `regions`, `cities`, `travel`, `documents` and `billingStatus` are ordered in
 * memory server-side under a 5,000-row cap; over it the server answers a 422
 * naming `sort`, rendered through {@link loadErrorMessage}.
 */
const SORT_KEYS: readonly ClientDeliverableSort[] = [
  'project',
  'company',
  'startDate',
  'endDate',
  'updatedAt',
  'billingStatus',
  'regions',
  'cities',
  'poc',
  'specialist',
  'intake',
  'product',
  'promised',
  'completed',
  'pending',
  'bookingSpec',
  'travel',
  'documents',
  'billingNote',
  'adsPlan',
] satisfies readonly ClientDeliverableSort[];

/**
 * The sentence a failed list read shows. A 422 carries only "Validation failed"
 * in `message`; the server's own sentence is on the detail — `sort`,
 * `billingStatus` or `travel` for the in-memory cap refusal — so read the
 * field errors first, then the form-level ones, then the bare message.
 */
function loadErrorMessage(caught: unknown): string {
  /*
   * Deploy order: a server predating `?possibleDuplicate=` answers a 422 naming
   * the key. The filter stays SET rather than being dropped and retried — a
   * retry would show every row under a filtered view. Matched on the detail's
   * `key`, read by presence (see `ValidationDetail`), never on the wording.
   */
  if (
    caught instanceof ApiError &&
    caught.status === 422 &&
    (caught.details ?? []).some(
      (detail) => 'key' in detail && detail.key === 'possibleDuplicate',
    )
  ) {
    return 'This SEED EMERGE build filters for possible duplicates, but the API it is talking to does not support that yet. Clear the Duplicates filter to see the list.';
  }
  if (caught instanceof ApiError) {
    const field = Object.values(caught.fieldErrors)[0];
    return field ?? caught.formMessage ?? caught.message;
  }
  return caught instanceof Error ? caught.message : 'Failed to load client deliverables';
}

/**
 * ⚠️ Falls back to `project`, NOT to `createdAt` like the entity lists. It
 * matches the server's own default, so the first request agrees with the first
 * render.
 *
 * ⚠️ *SUPERSEDED 2026-09-02: "and it is the condition under which this page
 * draws its project group headers".* **It is not a condition of anything any
 * more** — the server leads EVERY sort with the project band key, so the bands
 * survive whichever column the reader picks and the headers are drawn
 * unconditionally. The fallback still has to match the server's default; it just
 * no longer decides whether the page is grouped.
 */
function asSortKey(key: string | undefined): ClientDeliverableSort {
  return SORT_KEYS.includes(key as ClientDeliverableSort)
    ? (key as ClientDeliverableSort)
    : 'project';
}

/**
 * The sentence a non-admin sees instead of a working "Sort by" control.
 *
 * ── WRITTEN OUT RATHER THAN ROUTED THROUGH `denialReason()` ───────────────
 *
 * Same call `SettingsPage` makes for the mailer and the quote terms, and for its
 * reason: `denialReason` takes a `ModuleSlug`, every slug in that union names a
 * real permission module the server publishes, and **there is no module for the
 * Google Sheets mirror.** Inventing one would make this screen ask the
 * permission resolver a question nobody has put to the server.
 *
 * ⚠️ **THE GATE IS `ability.isAdmin` — the ability object, never `user.role` and
 * never `/permissions/me`**, which is advisory and wrong in both directions. It
 * matches `PUT /client-deliverables/sheet/sort` and `POST
 * /client-deliverables/sheet/sync`, both `requireRole(...ADMIN_ROLES)`.
 *
 * ⚠️ **IT GATES THE WRITE ONLY.** `GET /client-deliverables/sheet/sort` is open
 * to every authenticated role, deliberately: a tab carries no record of the
 * ordering that produced it, so a reader who cannot see this control has no way
 * at all to learn how the sheet in front of them is sorted. **Do not "fix" the
 * asymmetry by hiding the control** — showing a value somebody may not change is
 * the whole point.
 *
 * ⚠️ *SUPERSEDED 2026-10-01: "**The `viewer` gets its own sentence** … a
 * read-only account is … structurally unable to write anything".* A viewer's
 * writes are now decided by its permission set, so it shares the administrator
 * sentence — which is the accurate one for this admin-tier control.
 */
function sheetSortDenial(ability: Ability): string | null {
  /*
   * 🔴 2026-10-01: the set decides for every role, and the sheet's sort and
   * sync routes are `requireGrant('deliverables', 'update')` (measured in
   * `client-deliverables-sheet.routes.ts`). *SUPERSEDED, the doc above:
   * `ability.isAdmin`, "Changing how the sheet is sorted is restricted to
   * administrators."*
   */
  return denialReason(ability, 'update', 'deliverables');
}

/*
 * The five populations, ordered so each document's INCLUSION rows sit directly
 * under the line rows they came from — which is the order a reader thinks in,
 * and the order that makes the pairing visible in a single glance at the list.
 *
 * 🔴 *SUPERSEDED 2026-10-28 (owner: every filter takes several values): "takes
 * ONE member … do not build a multi-select over this key".* `source` is a CSV
 * list now (OR). The labels paragraph below still governs.
 *
 * ⚠️ **THE `source` FILTER TAKES ONE MEMBER AND THE TWO PAIRS DO NOT SUBSUME
 * EACH OTHER.** There is no value meaning "everything on a quote" and none
 * meaning "all inclusion rows": narrowing to `quote_line` HIDES that line's
 * inclusion rows, and narrowing to `quote_inclusion` hides the lines. The
 * server's schema is `.strict()` and parses one enum value, so
 * `?source=quote_line,quote_inclusion` is a 422 naming `source` rather than an
 * OR — do not build a multi-select over this key.
 *
 * ⚠️ **Which is why the LABELS have to carry the distinction and no sentence
 * beside the control may.** A shorter pair ("Quote line" / "Inclusion") would
 * leave a reader who picked one wondering why the page got shorter, and this
 * page's own de-duplication incident is the precedent: a rule that removed rows
 * looked exactly like the fix working. The label is the whole explanation the
 * no-helper-text rule allows, so it says what the population IS.
 */
const SOURCE_VALUES: readonly ClientDeliverableSource[] = [
  'quote_line',
  'quote_inclusion',
  'invoice_line',
  'invoice_inclusion',
  'standalone',
  'deal_line',
];

const SOURCE_LABELS: Record<ClientDeliverableSource, string> = {
  quote_line: 'From a quote line',
  quote_inclusion: 'From a campaign inclusion on a quote line',
  invoice_line: 'From an invoice line',
  invoice_inclusion: 'From a campaign inclusion on an invoice line',
  standalone: 'Standalone',
  deal_line: 'From a deal line',
};

/*
 * ⚠️ Least to most advanced, matching the server's own `BILLING_STATUSES`. The
 * order is not cosmetic there — it is how the furthest point reached across
 * several invoices is picked — so a member added in the wrong place here would
 * read as a different sequence from the one the server computes.
 */
const BILLING_VALUES: readonly BillingStatus[] = [
  'none',
  'not_billable',
  // Third, never appended — see the union in `types.ts`. A quote that has gone
  // out is further along than nothing and short of an invoice being owed.
  'quote_sent',
  'awaiting_invoice',
  'invoiced',
  'cancelled',
  'paid',
];

/**
 * ⚠️ The labels for the two pairs that are easy to read as one thing:
 *
 * - `cancelled` is settled but NOT paid — no more money is coming, which is a
 *   different fact from the money having arrived.
 * - `none` is "no document yet"; `not_billable` is "there was never going to be
 *   one". Neither is outstanding.
 *
 * ⚠️ Nothing here says "sent to the client", and its absence is deliberate:
 * `f_invoice_statuses` carries no `is_sent` flag, so a sent invoice and a draft
 * one are indistinguishable. `Invoiced` says only what is provable. Claiming a
 * send that did not happen fails in the reassuring direction, and nobody reports
 * a document they were expecting anyway.
 */
const BILLING_LABELS: Record<BillingStatus, string> = {
  none: 'No document',
  not_billable: 'Not billable',
  // ⚠️ "Quote sent" is provable — `f_quote_stages.is_sent`, migration 106 — which
  // is the ONLY reason it may be said. The paragraph above still governs
  // invoices, which carry no such flag and must never claim a send.
  quote_sent: 'Quote sent',
  awaiting_invoice: 'Awaiting invoice',
  invoiced: 'Invoiced',
  cancelled: 'Cancelled',
  paid: 'Paid',
};

/**
 * Tone is a SECOND signal only — every badge carries its label, and the label is
 * what says which state this is. Never read the colour alone, and never add a
 * status here that renders as a bare dot.
 */
const BILLING_TONES: Record<BillingStatus, 'neutral' | 'info' | 'warning' | 'success' | 'danger'> = {
  none: 'neutral',
  not_billable: 'neutral',
  // `info`, not `warning`. A quote with the customer is the normal state of a
  // live offer, not something outstanding on us — and it is about to be the
  // most common row on this page, where a warning tone on the majority teaches
  // people to stop reading the colour at all.
  quote_sent: 'info',
  awaiting_invoice: 'warning',
  invoiced: 'info',
  cancelled: 'danger',
  paid: 'success',
};

const OUTSTANDING_LABELS: Record<'true' | 'false', string> = {
  true: 'Still to deliver',
  false: 'Nothing outstanding',
};

/**
 * Reads the ten filters out of the URL.
 *
 * Anything that is not a positive integer, or not a member of its enum, is
 * DROPPED rather than forwarded: every one of these is a 422 on a `.strict()`
 * schema rather than "no filter", so a pasted `?regionId=abc` would take the
 * whole list down with it and read as a broken page rather than a bad link.
 */
/*
 * 2026-10-28 (owner): the six ids, `source` and `billingStatus` are CSV lists —
 * several values mean OR, the same param name, a single value still parses (old
 * bookmarks and saved filters unchanged). Each member is validated on its own and
 * an invalid one is dropped, never the whole list. `outstanding`, `removed` and
 * `possibleDuplicate` are booleans and stay single.
 */
function splitCsv(raw: string): string[] {
  return raw === '' ? [] : raw.split(',').filter((value) => value !== '');
}

function readFilters(params: URLSearchParams): Filters {
  const id = (key: IdFilterKey) => {
    const kept = splitCsv(params.get(key) ?? '').filter((value) => /^[1-9]\d*$/.test(value));
    return Array.from(new Set(kept)).join(',');
  };
  const memberList = (key: FilterKey, allowed: readonly string[]) => {
    const kept = splitCsv(params.get(key) ?? '').filter((value) => allowed.includes(value));
    return Array.from(new Set(kept)).join(',');
  };
  const member = (key: FilterKey, allowed: readonly string[]) => {
    const raw = params.get(key) ?? '';
    return allowed.includes(raw) ? raw : '';
  };
  return {
    companyId: id('companyId'),
    productId: id('productId'),
    projectId: id('projectId'),
    intakeId: id('intakeId'),
    cycleId: id('cycleId'),
    regionId: id('regionId'),
    source: memberList('source', SOURCE_VALUES),
    billingStatus: memberList('billingStatus', BILLING_VALUES),
    outstanding: member('outstanding', ['true', 'false']),
    removed: member('removed', ['true']),
    possibleDuplicate: member('possibleDuplicate', ['true']),
  };
}

/**
 * Written out key by key so a request can never carry a stray param, and so an
 * empty value is omitted entirely rather than emitted as a bare `key=`.
 *
 * The enums are re-narrowed here rather than trusted from `Filters`, which types
 * every value as a plain string: `readFilters` has already checked membership,
 * and this is the assertion that keeps the two in step at compile time.
 */
function filterParams(filters: Filters): ClientDeliverableListParams {
  return {
    // CSV, already validated member by member in `readFilters`.
    ...(filters.companyId ? { companyId: filters.companyId } : {}),
    ...(filters.productId ? { productId: filters.productId } : {}),
    ...(filters.projectId ? { projectId: filters.projectId } : {}),
    ...(filters.intakeId ? { intakeId: filters.intakeId } : {}),
    ...(filters.cycleId ? { cycleId: filters.cycleId } : {}),
    ...(filters.regionId ? { regionId: filters.regionId } : {}),
    ...(filters.source ? { source: filters.source } : {}),
    ...(filters.billingStatus ? { billingStatus: filters.billingStatus } : {}),
    ...(filters.outstanding ? { outstanding: filters.outstanding as 'true' | 'false' } : {}),
    // Absent is the live tracker; `false` is never sent — it means the same.
    ...(filters.removed === 'true' ? { removed: true } : {}),
    // The same: absent is every row, and `false` is never sent.
    ...(filters.possibleDuplicate === 'true' ? { possibleDuplicate: true } : {}),
  };
}

/* -------------------------------------------------------------------------
 * Icons — Lucide paths, drawn by hand rather than pulling in a dependency.
 * --------------------------------------------------------------------- */

function SearchIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-4 w-4"
    >
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}

function FilterIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-4 w-4"
    >
      <path d="M4 6h16" />
      <path d="M7 12h10" />
      <path d="M10 18h4" />
    </svg>
  );
}

function TrackerIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-5 w-5"
    >
      <rect x="8" y="2" width="8" height="4" rx="1" />
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      <path d="m9 14 2 2 4-4" />
    </svg>
  );
}

function ChevronIcon({ direction }: { direction: 'left' | 'right' }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-3.5 w-3.5"
    >
      <path d={direction === 'left' ? 'm15 18-6-6 6-6' : 'm9 18 6-6-6-6'} />
    </svg>
  );
}

/* -------------------------------------------------------------------------
 * Filter option sources
 * --------------------------------------------------------------------- */

type RemoteSource = 'companies' | 'products' | 'intakes';

/**
 * Options for the company / product / intake filters.
 *
 * Companies and products have a server-side `search`, so the Combobox debounces
 * the typed term straight into `setTerm`. Intakes have **no** search param, so
 * that list is fetched once and filtered locally — wiring `onSearch` there would
 * send a param the API strips in silence, and the picker would look like it
 * searched while quietly returning the same page.
 */
/*
 * 🔴 *SUPERSEDED 2026-10-28: server-side `search` through `setTerm`.* The
 * filters are `MultiCombobox`es now, which search locally only, so companies and
 * products are read WHOLE (paged) and the local search is exhaustive.
 */
function useRemoteOptions(source: RemoteSource) {
  const [options, setOptions] = useState<ComboboxOption[]>([]);
  const [failed, setFailed] = useState(false);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    const request: Promise<{ options: ComboboxOption[]; total: number }> =
      source === 'companies'
        ? fetchAllPages(
            (page, perPage, pageSignal) =>
              api.listCompanies({ page, perPage, sort: 'name', order: 'asc' }, pageSignal),
            signal,
          ).then((result) => ({
            options: result.rows.map((company) => ({
              value: String(company.id),
              label: company.name,
            })),
            total: result.total,
          }))
        : source === 'products'
          ? api.listAllProducts(signal).then((products) => ({
              // Retired products are listed too: a line can legitimately sell
              // one, and the filter has to be able to find those rows.
              options: products.map((product) => ({
                value: String(product.id),
                label: product.productName,
                ...(product.productCode ? { hint: product.productCode } : {}),
              })),
              total: products.length,
            }))
          : api.listIntakes({ page: 1, perPage: OPTION_PER_PAGE }, signal).then((result) => ({
              options: result.data.map((intake) => ({
                value: String(intake.id),
                label: intake.intakeName,
              })),
              total: result.meta.total,
            }));

    request
      .then((result) => {
        setOptions(result.options);
        setTotal(result.total);
        setFailed(false);
      })
      .catch(() => {
        if (signal.aborted) return;
        setOptions([]);
        setFailed(true);
      })
      .finally(() => {
        if (!signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [source]);

  return { options, loading, failed, total };
}

/**
 * Keeps a selected filter renderable when its id is not in the loaded options.
 *
 * Three ways that happens: the id arrived on the URL before the options did, the
 * list is one page of a longer one, or the value is a master row since
 * deactivated — a retired row still filters correctly server-side, but the
 * option hooks list active rows only.
 *
 * The loaded rows are the best label source available: every row matching a
 * `companyId` filter carries that company's name. With even that missing we show
 * the bare id rather than an empty trigger, which would read as "no filter"
 * while the list stayed filtered.
 */
function withSelected(
  options: ComboboxOption[],
  value: string,
  labels: Map<string, string>,
  noun: string,
): ComboboxOption[] {
  if (!value || options.some((option) => option.value === value)) return options;
  const known = labels.get(value);
  return [
    known
      ? { value, label: known }
      : { value, label: `${noun} #${value}`, hint: 'Not in the list below' },
    ...options,
  ];
}

/** `withSelected` for a CSV filter: every held value stays renderable. */
function withSelectedMany(
  options: ComboboxOption[],
  csv: string,
  labels: Map<string, string>,
  noun: string,
): ComboboxOption[] {
  return splitCsv(csv).reduce(
    (current, value) => withSelected(current, value, labels, noun),
    options,
  );
}

/**
 * One MULTI-value filter control (owner 2026-10-28): a `MultiCombobox` over a
 * CSV held in one URL param. Empty = all. No `hint` prop, as `FilterField`.
 */
function MultiFilterField({
  label,
  value,
  options,
  onChange,
  allLabel,
  loading,
  failed,
  searchPlaceholder,
  footnote,
  noun,
}: {
  label: string;
  /** The CSV as held in the URL. */
  value: string;
  options: ComboboxOption[];
  /** The next CSV; `''` clears the param. */
  onChange: (value: string) => void;
  allLabel: string;
  loading: boolean;
  failed: boolean;
  searchPlaceholder?: string;
  /** A fact about the list, inside the dropdown — never advice. */
  footnote?: string;
  noun?: { singular: string; plural: string };
}) {
  return (
    <Field
      label={label}
      {...(failed ? { error: `Couldn’t load ${label.toLowerCase()} options.` } : {})}
    >
      {({ id, 'aria-describedby': describedBy }) => (
        <MultiCombobox
          id={id}
          values={splitCsv(value)}
          onChange={(next) => onChange(next.join(','))}
          options={options}
          placeholder={allLabel}
          loading={loading}
          searchPlaceholder={searchPlaceholder}
          maxVisibleChips={2}
          {...(noun ? { summaryNoun: noun } : {})}
          {...(footnote ? { footnote } : {})}
          aria-describedby={describedBy}
        />
      )}
    </Field>
  );
}

/**
 * One filter control.
 *
 * No `hint` prop, deliberately: helper text is prohibited across this app, and a
 * prop nobody may pass is one somebody eventually does. `error` stays — a failed
 * option fetch is a refusal the reader has to see, not advice.
 */
function FilterField({
  label,
  value,
  options,
  onChange,
  allLabel,
  loading,
  failed,
  onSearch,
  searchPlaceholder,
  header,
  footnote,
}: {
  label: string;
  value: string;
  options: ComboboxOption[];
  onChange: (value: string) => void;
  allLabel: string;
  loading: boolean;
  failed: boolean;
  onSearch?: (term: string) => void;
  searchPlaceholder?: string;
  /** Rendered inside the dropdown above the list — the project cycle pills. */
  header?: ReactNode;
  /** A fact about the list, inside the dropdown — never advice. */
  footnote?: string;
}) {
  return (
    <Field
      label={label}
      {...(failed ? { error: `Couldn’t load ${label.toLowerCase()} options.` } : {})}
    >
      {({ id, 'aria-describedby': describedBy }) => (
        <Combobox
          id={id}
          value={value}
          onChange={onChange}
          options={options}
          emptyOptionLabel={allLabel}
          placeholder={allLabel}
          loading={loading}
          onSearch={onSearch}
          searchPlaceholder={searchPlaceholder}
          aria-describedby={describedBy}
          {...(header ? { header } : {})}
          {...(footnote ? { footnote } : {})}
        />
      )}
    </Field>
  );
}

/* -------------------------------------------------------------------------
 * Cells
 * --------------------------------------------------------------------- */

/** Nothing recorded, rendered as a mark rather than as blank space. */
function NotRecorded() {
  return <span className="text-xs text-fg-subtle">—</span>;
}

/**
 * What a row needs to be editable, threaded from the page rather than fetched
 * per row.
 *
 * ⚠️ It carried three STAFF option fields until 2026-08-31 and now carries
 * none: the POC picker was the only per-row control needing a shared list, and
 * it went with the column. The bundle survives on one field because the shape
 * is the right one for the next control that needs page-level state — but if
 * nothing else ever does, this is a candidate for collapsing back to a bare
 * `onSaved` callback.
 */
interface RowEditing {
  /** Replaces the row in page state with the record the write returned. */
  onSaved: (next: ClientDeliverable) => void;
  /**
   * Staff for the POC picker, narrowed to the `delivery_lead` group.
   *
   * ⚠️ Fetched ONCE by the page and handed down, never per row. The tracker
   * reads whole at 50 rows a page, and a hook inside the row component would be
   * 50 identical requests against a bucket shared across the office.
   * ⚠️ *SUPERSEDED: "a 120/60s bucket."* It is **12,000/60s** (corrected
   * 2026-09-17) — 50 copies of one answer is still the wrong shape.
   */
  staffOptions: ComboboxOption[];
  /**
   * Opens the delete-or-remove confirmation — `undefined` when the reader may
   * not update deliverables, so no control renders at all.
   *
   * 🔴 *SUPERSEDED 2026-10-14: "Standalone rows only … `DELETE
   * /client-deliverables/:id` is `ADMIN_ROLES`."* That comment was already
   * stale (deletes followed `deliverables.delete` from 2026-10-01), and the
   * owner has since widened the control to EVERY row, gated on
   * `ability.canUpdate('deliverables')`:
   *
   * - a STANDALONE row is DELETED for good (`DELETE /client-deliverables/:id`);
   * - every other row is REMOVED from the tracker by its `key` (`POST
   *   /client-deliverables/remove`) — hidden from the list and the Sheet,
   *   nothing on the quote or invoice changes, restorable from the Removed view.
   *
   * ⛔ **Never send a line-derived row to the DELETE endpoint.** It deletes only
   * the typed OVERLAY: the row comes straight back on the next read with its
   * progress wiped, which reads as "delete did nothing" while it destroyed the
   * completed count. `canDeleteRow` and `rowRemovalOf` decide which path.
   */
  onDelete?: (row: ClientDeliverable) => void;
  /** Puts a removed row back on the tracker; `undefined` when the reader may not. */
  onRestore?: (row: ClientDeliverable) => void;
  /** The `key` of the row whose restore is in flight, so its control shows busy. */
  restoringKey: string | null;
  /**
   * Re-reads the list. Fired by the writer after a write on a SPLIT row, because
   * the pieces share their typed fields and a write on one changes its siblings —
   * replacing the one row in place would leave the others stating the old value.
   */
  onSplitRowWritten: () => void;
  /**
   * Re-reads the list after a line-note write. The note lives on the SOURCE
   * line, so every row drawn from it (its pieces, its inclusions) changes too.
   */
  onLineNoteWritten: () => void;
  /**
   * Re-reads the list after the first write on a derived `deal_line` row. That
   * write makes a deal placeholder row (`POST /from-deal-lines`) whose `key` is
   * `s:<id>`, not the derived row's, so `onSaved` cannot match it by key.
   */
  onDealPlaceholderMade: () => void;
  /** The LAE representatives for the Specialist picker; `null` = not read / failed. */
  specialists: LaeSpecialist[] | null;
  specialistsError: string | null;
  /** Every loaded row sharing this row's split target, the row included. */
  siblingsOf: (row: ClientDeliverable) => ClientDeliverable[];
  /**
   * Opens the split dialog — `undefined` when the reader may not update
   * deliverables, so no control renders. Line and standalone rows only.
   */
  onSplit?: (row: ClientDeliverable) => void;
  /**
   * Opens the link dialog — `undefined` when the reader may not update
   * deliverables. Standalone rows with an overlay only; see `canLinkRow`.
   */
  onLink?: (row: ClientDeliverable) => void;
}

/**
 * Whether a row offers "Link to a deal, quote or invoice" (owner 2026-10-16):
 * a hand-entered row with an id, still on the tracker. A line or inclusion row
 * already IS its document's row, so there is nothing to link it to.
 */
function canLinkRow(row: ClientDeliverable, editing: RowEditing): boolean {
  return (
    editing.onLink !== undefined &&
    populationOf(row) === 'standalone' &&
    row.id !== null &&
    row.removed !== true
  );
}

/** The Link control, beside Split and Delete in the Client cell and the stacked card. */
function LinkRowButton({ row, editing }: { row: ClientDeliverable; editing: RowEditing }) {
  if (!canLinkRow(row, editing)) return null;
  const label = 'Link to a deal, quote or invoice';
  return (
    <Tooltip label={label}>
      <Button
        variant="ghost"
        size="sm"
        iconOnly
        aria-label={label}
        leadingIcon={<LinkIcon />}
        className="text-fg-subtle hover:text-fg"
        onClick={() => editing.onLink?.(row)}
      />
    </Tooltip>
  );
}

/** Two chain links — Lucide `link`. Decorative. */
function LinkIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4"
      aria-hidden="true"
    >
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  );
}

/**
 * The link state of a STANDALONE row, as badges. Each field is optional and
 * `undefined` is an older API, which renders nothing — never a "not linked".
 *
 * - `linkedDealLine`: the row will be billed on that deal; links to it.
 * - `possibleDuplicate`: the server suspects a document line already covers it.
 * - `linkConflict`: the link disagrees with its line; opens the link dialog
 *   when the reader may link, otherwise it is a plain badge.
 */
function LinkStateBadges({ row, editing }: { row: ClientDeliverable; editing: RowEditing }) {
  if (populationOf(row) !== 'standalone') return null;
  const linked = row.linkedDealLine ?? null;
  // The Deal badge (`DealMarker`) already names and links this deal.
  const deal = linked !== null && dealMarkerOf(row)?.id === linked.dealId ? null : linked;
  const canOpen = canLinkRow(row, editing);
  return (
    <>
      {deal !== null && (
        <Link
          to={`/deals/${deal.dealId}`}
          className="inline-flex rounded-sm underline-offset-2 hover:underline"
        >
          <Badge tone="info" variant="outline" wrap>
            Billed on deal {deal.dealName}
          </Badge>
        </Link>
      )}
      {row.possibleDuplicate === true && (
        <Badge tone="warning" variant="outline">
          Possible duplicate
        </Badge>
      )}
      {row.linkConflict === true &&
        (canOpen ? (
          <button
            type="button"
            className="inline-flex cursor-pointer rounded-sm"
            onClick={() => editing.onLink?.(row)}
          >
            <Badge tone="danger" variant="outline">
              Link needs review
            </Badge>
          </button>
        ) : (
          <Badge tone="danger" variant="outline">
            Link needs review
          </Badge>
        ))}
    </>
  );
}

/**
 * Whether a row offers Split: a line row or a standalone row with an overlay,
 * never an inclusion row (it has no row of its own to divide).
 */
function canSplitRow(row: ClientDeliverable, editing: RowEditing): boolean {
  // A removed row is off the tracker; restore it before dividing it.
  return editing.onSplit !== undefined && row.removed !== true && splitTargetKey(row) !== null;
}

/** The Split control, beside Delete in the Client cell and the stacked card. */
function SplitRowButton({ row, editing }: { row: ClientDeliverable; editing: RowEditing }) {
  if (!canSplitRow(row, editing)) return null;
  const label =
    row.piece === null
      ? `Split this deliverable for ${row.company?.name ?? 'this client'}`
      : `Change the split for ${row.company?.name ?? 'this client'}`;
  return (
    <Button
      variant="ghost"
      size="sm"
      iconOnly
      aria-label={label}
      title={label}
      leadingIcon={<SplitIcon />}
      className="text-fg-subtle hover:text-fg"
      onClick={() => editing.onSplit?.(row)}
    />
  );
}

/** Two branches leaving one stem — the split mark. Decorative. */
function SplitIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4"
      aria-hidden="true"
    >
      <path d="M12 21v-7" />
      <path d="M12 14 6 8V3" />
      <path d="m12 14 6-6V3" />
    </svg>
  );
}

/**
 * What taking this row off the tracker MEANS — the one decision, read by the
 * button, the dialog and the request alike so the three cannot disagree.
 *
 * - `delete`: a standalone row, which is all there is — gone for good.
 * - `remove`: a quote line, invoice line, inclusion or piece — hidden by `key`,
 *   restorable, and nothing on the document changes.
 * - `null`: nothing to offer — a row already removed (it offers Restore), a
 *   standalone row with no id, or a population this build does not recognise
 *   (fail closed, as `rowIsEditable` does).
 */
function rowRemovalOf(row: ClientDeliverable): 'delete' | 'remove' | null {
  if (row.removed === true) return null;
  const population = populationOf(row);
  if (population === 'standalone') return row.id !== null ? 'delete' : null;
  if (population === 'attached' || population === 'inclusion') return 'remove';
  return null;
}

/** The one test for whether a row offers Delete or Remove. See `RowEditing.onDelete`. */
function canDeleteRow(row: ClientDeliverable, editing: RowEditing): boolean {
  return editing.onDelete !== undefined && rowRemovalOf(row) !== null;
}

/** The Delete / Remove control, shared by the table's Client cell and the stacked card. */
function DeleteRowButton({ row, editing }: { row: ClientDeliverable; editing: RowEditing }) {
  if (!canDeleteRow(row, editing)) return null;
  const client = row.company?.name ?? 'this client';
  const removing = rowRemovalOf(row) === 'remove';
  const label = removing
    ? `Remove this deliverable for ${client} from the tracker`
    : `Delete this deliverable for ${client}`;
  return (
    <Button
      variant="ghost"
      size="sm"
      iconOnly
      aria-label={label}
      title={label}
      leadingIcon={removing ? <RemoveFromTrackerIcon /> : <TrashIcon />}
      className="text-fg-subtle hover:text-danger"
      onClick={() => editing.onDelete?.(row)}
    />
  );
}

/** A circle with a bar — "take off the list", distinct from the bin of a real delete. */
function RemoveFromTrackerIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M8 12h8" />
    </svg>
  );
}

/**
 * Restore, on a row in the Removed view. A text button rather than an icon,
 * because it is the one thing that view is for.
 */
function RestoreRowButton({ row, editing }: { row: ClientDeliverable; editing: RowEditing }) {
  if (row.removed !== true || editing.onRestore === undefined) return null;
  return (
    <Button
      variant="secondary"
      size="sm"
      loading={editing.restoringKey === row.key}
      disabled={editing.restoringKey !== null && editing.restoringKey !== row.key}
      aria-label={`Restore this deliverable for ${row.company?.name ?? 'this client'} to the tracker`}
      onClick={() => editing.onRestore?.(row)}
    >
      Restore
    </Button>
  );
}

/** Says a row is off the tracker. Neutral: it is a state, not a fault. */
function RemovedBadge({ row }: { row: ClientDeliverable }) {
  if (row.removed !== true) return null;
  return <Badge tone="neutral">Removed</Badge>;
}

/**
 * The sentence a failed delete, remove or restore shows. A 403 carries no
 * `details`, so it is a banner sentence, never a field error.
 */
function trackerActionError(caught: unknown, fallback: string): string {
  if (caught instanceof ApiError) {
    if (caught.status === 403) {
      return 'Your permission set does not include this. An administrator can change that.';
    }
    if (caught.status === 404) {
      return 'This row is no longer where it was. Reload the tracker to see the current list.';
    }
    return caught.formMessage ?? caught.message;
  }
  return caught instanceof Error ? caught.message : fallback;
}

/**
 * SEED's regions of focus. Always an array, `[]` when none — so
 * `regions.length === 0` is the whole test and there is no `?? []` anywhere.
 *
 * An empty set is UNCLASSIFIED, a real and ordinary state; it is not a failed
 * lookup and must not read as one.
 *
 * Every region is a pill that wraps, with no cap — owner's decision 2026-09-25.
 * `ValuePills` rather than `Badge`, because `Badge` is `whitespace-nowrap` and a
 * long region name would overflow this narrow column instead of wrapping.
 */
function RegionsCell({ row }: { row: ClientDeliverable }) {
  if (row.regions.length === 0) return <NotRecorded />;
  return (
    <ValuePills
      items={row.regions.map((region) => ({
        key: region.id,
        label: region.name.trim() || `Region #${region.id}`,
      }))}
    />
  );
}

/**
 * The cities on this row's LINE, comma-separated — owner's instruction.
 *
 * ⚠️ **THE SERVER SENDS AN ARRAY AND THE COMMA IS OURS.** One function builds the
 * string so the table cell, its `title` and the column funnel cannot word the
 * same list three ways. The name is trimmed and falls back to the id, which is the
 * one place the ids earn their keep on screen — two cities may legitimately share
 * a name, so an id is the only thing that separates them.
 */
function cityNames(row: ClientDeliverable): string {
  return row.cities.map((city) => city.name.trim() || `City #${city.id}`).join(', ');
}

/**
 * 🔴 **`[]` RENDERS AS AN EMPTY CELL, NOT AS A DASH — and this is the one set
 * column on the table that does not use `NotRecorded`.**
 *
 * `RegionsCell` above draws a dash for its empty set because an unclassified
 * region is a gap somebody can go and close. Cities are not: a STANDALONE row has
 * no line to read them from, so it is `[]` permanently and by construction. A dash
 * there says "nothing recorded yet", which is a sentence a reader acts on, and
 * "None" is worse — it asserts the line runs in no cities when the truth is that
 * there is no line. Nothing is the only honest rendering.
 *
 * ⚠️ *SUPERSEDED 2026-09-25, owner's decision: "A long list DEGRADES rather than
 * overflowing … `truncate` inside the column's own `max-w`, with the full list on
 * the `title`", and the `wrap` prop that gave the sub-`lg` card a wrapping
 * variant.* **Every city is now a pill that WRAPS, with no cap and no "+N more"**,
 * in the table and the card alike — so the ROW grows and nothing is lost to an
 * ellipsis, which also retires the touch argument for a second variant. The
 * joined string stays on the `title` (and in the column funnel), so those cannot
 * word the list differently from the pills. What must NOT fork is the empty-set
 * decision, which is why both callers still come through here.
 */
function CitiesCell({ row }: { row: ClientDeliverable }) {
  if (row.cities.length === 0) return null;
  return (
    <ValuePills
      title={cityNames(row)}
      items={row.cities.map((city) => ({
        key: city.id,
        label: city.name.trim() || `City #${city.id}`,
      }))}
    />
  );
}

/*
 * ⚠️ **`TravelCell` STOOD HERE and is now `TravelField` in
 * `InlineTrackerFields` — 2026-09-09, owner's decision.** It rendered one badge
 * and nothing else, because travel was derived and read-only: *"the way to
 * change this cell is to edit the PRODUCT (on `/products`) or the document's
 * lines — never this row."*
 *
 * **That advice is still right for a DERIVED row and is now only half the
 * story.** A person may overrule the derivation on the row itself, so the cell
 * has to show which of the two states it is in — a bare badge made "Travelling
 * because the quote says so" and "Travelling because somebody said so" look
 * identical, which is exactly what `travelOverride` exists to tell apart.
 *
 * It moved rather than growing a wrapper here because it is ONE field, like
 * `DeliveryLeadField` beside it, and unlike `DatesCell` / `BookingSpecCell`,
 * which each bundle two.
 */

/**
 * `pending` is `promised - completed` and is **SIGNED**.
 *
 * Overdelivery is real — the workbook this replaces carries rows at `-1` — so
 * the figure is never clamped. `formatPending` spells the sign in words
 * ("2 over") because a bare minus reads as an error rather than as good news.
 */
function PendingCell({ pending }: { pending: number }) {
  const over = pending < 0;
  const settled = pending === 0;
  return (
    <Badge tone={over ? 'info' : settled ? 'success' : 'neutral'} dot>
      {formatPending(pending)}
    </Badge>
  );
}

/**
 * The one class both document numbers render with.
 *
 * ⚠️ **Shared rather than repeated, because the two are now DIFFERENT
 * COMPONENTS.** While both were plain `Link`s a drifted class was a cosmetic
 * bug; now that one opens a quote form and the other an invoice form, two
 * numbers stacked in one cell looking unalike would read as two different KINDS
 * of control — which is exactly what they must not, since both do the same
 * thing to different documents.
 */
const DOCUMENT_NUMBER_CLASS =
  'truncate text-xs text-fg underline-offset-2 decoration-hairline-strong transition-colors duration-150 ease-out hover:underline focus-visible:underline motion-reduce:transition-none';

/**
 * The quote this line came from, and every invoice it has been billed on.
 *
 * ⚠️ `invoices` is a LIST, and the wording says so. A staged quote is billed a
 * line at a time and `d_invoices.quote_id` carries no UNIQUE constraint, so one
 * line legitimately appears on several invoices — "invoices raised", never "the
 * invoice", and never `invoices[0]` rendered as though it were the answer.
 *
 * 🔴 **EVERY NUMBER OPENS ITS OWN DOCUMENT'S EDIT FORM — owner's instruction,
 * 2026-09-17.** A quote opens `QuoteFormModal`, an invoice opens
 * `InvoiceFormModal`, and **each invoice in the list is independently
 * clickable**: `invoices` is an array and a row naming three of them offers
 * three separate openers, because "the invoice" is not a thing this cell has.
 *
 * ⚠️ **They are still real links.** Ctrl/cmd-click, middle-click and "copy link
 * address" all still reach `/quotes/{id}` and `/invoices/{id}`, and a reader
 * without the matching write permission gets that navigation on a plain click
 * too — the components decide, not this cell. See `InvoiceNumberLink.tsx`.
 *
 * ⚠️ **The FALLBACK LABELS stay here**, and they are the reason both components
 * take the text rather than deriving it. `quoteNumber` is server-allocated and
 * is `null` until the server assigns one; `invoiceNumber` is likewise nullable.
 * What to print instead is a decision about this surface — this one prints
 * `Quote #{id}` — and it is taken where the shape is visible.
 */
function DocumentsCell({ row }: { row: ClientDeliverable }) {
  const { quote, invoices, label } = row.billing;

  if (!quote && invoices.length === 0) {
    // A standalone row's label is the reason there is no document at all —
    // "complimentary", "paid from 1-1 Budget". It is a fact about this row, not
    // a placeholder for one that is coming.
    if (label && label.trim() !== '') {
      return <Badge tone="neutral" variant="outline">{label}</Badge>;
    }
    return <NotRecorded />;
  }

  return (
    <div className="grid gap-1">
      {quote && (
        <QuoteNumberLink
          quoteId={quote.id}
          quoteNumber={quote.quoteNumber ?? `Quote #${quote.id}`}
          className={DOCUMENT_NUMBER_CLASS}
        />
      )}
      {invoices.map((invoice) => (
        <span key={invoice.id} className="flex flex-wrap items-baseline gap-1">
          <InvoiceNumberLink
            invoiceId={invoice.id}
            invoiceNumber={invoice.invoiceNumber ?? `Invoice #${invoice.id}`}
            className={DOCUMENT_NUMBER_CLASS}
          />
          {invoice.dueDate && (
            <span className="text-2xs text-fg-muted tabular">
              <span className="sr-only">due </span>
              {formatDateOnly(invoice.dueDate)}
            </span>
          )}
        </span>
      ))}
    </div>
  );
}

/**
 * Billing status, read from `billing.status` and NEVER from
 * `billing.invoiceStatusName`.
 *
 * ⚠️ That name is an admin-editable master label. Branching on it is a client
 * manufacturing a contract the server never offered, and this codebase has been
 * bitten three times doing exactly that — `"Unpaid"` contains `"paid"`. The name
 * is rendered UNDERNEATH the badge, as a label for a person to read, because the
 * server cannot tell a draft invoice from a sent one and this is the only way a
 * reader sees which it is.
 *
 * ⚠️ **EVERYTHING IN THIS CELL IS NOW DERIVED, AND IT CARRIES NO CONTROL.**
 * *SUPERSEDED: "Three of the four things in this cell are DERIVED and stay
 * read-only … Only `billingNote` is typed, so only it carries a control."*
 *
 * `billingNote` moved out to its OWN COLUMN on 2026-09-02, because the workbook
 * has one — `P1` on the live sheet reads `Billing note`. It was nested here
 * while this page believed the workbook had no such column, which reading the
 * xlsx disproved.
 *
 * So the status, the invoice-status label and a standalone row's reason are all
 * read through the document at request time, and nothing here is editable.
 */
// Exported for the company page's Services section (2026-10-18) — one badge, one place.
export function BillingStatusCell({ row }: { row: ClientDeliverable }) {
  const { status, invoiceStatusName, label } = row.billing;
  return (
    <div className="grid gap-1">
      <span>
        <Badge tone={BILLING_TONES[status]} dot>
          {BILLING_LABELS[status]}
        </Badge>
      </span>
      {invoiceStatusName && invoiceStatusName.trim() !== '' && (
        <span className="truncate text-2xs text-fg-muted">{invoiceStatusName}</span>
      )}
      {status === 'not_billable' && label && label.trim() !== '' && (
        <span className="truncate text-2xs text-fg-muted">{label}</span>
      )}
    </div>
  );
}

/**
 * Which of the three populations a row belongs to — the ONE place this page
 * reads `source`, so a member added to the union lands here as a type error
 * rather than as a row that renders like the wrong kind of thing.
 *
 * ⚠️ **Read `source`, never the `key` PREFIX.** `row.key` is `q:` / `i:` /
 * `qx:` / `ix:` / `s:` and the prefixes would answer this correctly today — but
 * that string's job is React keying and page-to-page stability, and parsing
 * meaning out of an identity is how a client manufactures a contract the server
 * never offered. The two `x` forms are THREE-part, so a reader splitting on `:`
 * and trusting `parts[1]` is right about three members and wrong about two.
 *
 * ⛔ **AND NEVER `row.id === null`, WHICH IS NOW TWO FACTS.** On an attached or
 * standalone row `null` means *nobody has typed anything yet* — writable. On an
 * inclusion row it means *there is nowhere to type* — read-only, permanently,
 * because `d_client_deliverables` overlays a LINE and an inclusion inside a line
 * has no overlay row to hang one from. `source` is the only thing that tells the
 * two apart, and getting it wrong is silent: see `rowIsEditable`.
 *
 * ⚠️ **An unrecognised member resolves to `'unknown'`, never to a guess and
 * never to `null`.** Unknown means "newer than this branch", the same rule
 * `line-project.ts` and `xero.ts` follow — and what it must NOT do is fall
 * through to `'attached'`, which would mount write controls over a population
 * this build has never heard of.
 */
type RowPopulation = 'attached' | 'inclusion' | 'standalone' | 'deal' | 'unknown';

function populationOf(row: ClientDeliverable): RowPopulation {
  switch (row.source) {
    case 'quote_line':
    case 'invoice_line':
      return 'attached';
    case 'quote_inclusion':
    case 'invoice_inclusion':
      return 'inclusion';
    case 'standalone':
      return 'standalone';
    /*
     * 2026-10-27 (owner): a deal line not yet quoted or invoiced, derived by the
     * server (`id: null`, `line: null`). Its first write makes a deal placeholder
     * row through `POST /from-deal-lines` — see `useDeliverableRowWriter`; the
     * shared writer's `PUT` upsert has no line to name here.
     */
    case 'deal_line':
      return 'deal';
    default: {
      // Exhaustiveness: a member added to `ClientDeliverableSource` lands here
      // as a type error rather than as an unmarked row carrying live editors.
      const unreached: never = row.source;
      void unreached;
      return 'unknown';
    }
  }
}

/*
 * ⚠️ **`isStandaloneRow` STOOD HERE and its reasoning moved up into
 * `populationOf`**, which now answers the same question as one arm of a larger
 * one. It was deleted rather than kept as a wrapper because the question it
 * asked — *is this row hand-entered?* — is no longer the only thing this page
 * needs to know about a row's population, and a predicate that answers one of
 * four cases invites a second predicate for the next one until the four are
 * decided in four places. Three call sites read `populationOf(row)` once and
 * branch on the result.
 */

/**
 * 🔴 **WHETHER THE TYPED-FIELD EDITORS MAY BE MOUNTED AT ALL — and it FAILS
 * CLOSED, deliberately.**
 *
 * An inclusion row has no overlay row and can never have one, so there is
 * nothing to `PATCH`. ⛔ **But it DOES have a `line`**, and that is what makes
 * this dangerous rather than merely wrong: `useTrackerRowWriter` reads
 * `row.id !== null || row.line !== null` for its own gate and `row.id === null`
 * for its VERB, so an editor mounted here would take the `PUT` upsert path
 * naming `quoteProductId` / `invoiceProductId` — a real line, which exists — and
 * the write would **succeed**. The figure lands on the PARENT LINE's
 * deliverable, overwriting somebody's typed progress, with a `200` and nothing
 * anywhere failing. That is why the controls are ABSENT here rather than
 * disabled: a disabled control says *you* may not do this, which would be a
 * refusal about the reader and is not what is true.
 *
 * ⚠️ **`'unknown'` is read-only too, and that asymmetry is the point.** A
 * missing editor on a population this build does not recognise is visible and
 * gets reported; a wrongly-offered one writes to the wrong row in silence. Fail
 * towards the visible wrong answer.
 */
function rowIsEditable(population: RowPopulation): boolean {
  return population === 'attached' || population === 'standalone' || population === 'deal';
}

/**
 * The deal behind a DEAL PLACEHOLDER row (owner 2026-10-27), or `null`.
 *
 * A placeholder is a derived `deal_line` row or a standalone row made from a deal
 * (`isDealPlaceholder`). It paints `DEAL_ROW_TINT` and carries the Deal badge.
 * `undefined` on either key is an older API and reads as not a placeholder.
 */
function dealMarkerOf(row: ClientDeliverable): { id: number; name: string } | null {
  if (row.isDealPlaceholder !== true) return null;
  const deal = row.deal ?? null;
  return deal === null ? null : { id: deal.id, name: deal.name };
}

/** Whether the row paints the yellow deal-placeholder tint. */
function isDealPlaceholderRow(row: ClientDeliverable): boolean {
  return row.isDealPlaceholder === true;
}

/**
 * The deal-placeholder row tint (owner 2026-10-27: "yellow"). `warning-soft` is
 * the token yellow in both themes; it is not a billing state here, and the Deal
 * badge is `outline` so it keeps its border on the tint. Never a literal hex,
 * never a `dark:` variant.
 */
const DEAL_ROW_TINT = 'bg-warning-soft';

/** The left edge of a deal-placeholder row; see `STANDALONE_EDGE` for the rules. */
const DEAL_EDGE = 'shadow-[inset_3px_0_0_0_var(--warning-border)]';

/**
 * The writer for one row: the shared `useTrackerRowWriter`, except on a derived
 * `deal_line` row, which has no overlay id and no quote/invoice line for the
 * shared writer's `PATCH` or `PUT` to name.
 *
 * There, the first write makes the deal placeholder row with
 * `POST /client-deliverables/from-deal-lines` (the `create` grant), then
 * `PATCH`es the typed field onto it, then re-reads the list: the new row's key is
 * `s:<id>`, so it cannot replace the derived row by key. Two requests, no
 * transaction — a made row whose patch failed is reported as `partial`.
 *
 * ⚠️ `dealProductId` names the deal line. It is read from the field, never
 * parsed out of `key`.
 */
function useDeliverableRowWriter(row: ClientDeliverable, editing: RowEditing): TrackerRowWriter {
  const shared = useTrackerRowWriter(row, editing.onSaved, editing.onSplitRowWritten);
  const { ability } = useAuth();
  if (populationOf(row) !== 'deal') return shared;

  const dealProductId = row.dealProductId ?? null;
  const disabledReason = !ability.canCreate('deliverables')
    ? (denialReason(ability, 'create', 'deliverables') ??
      'Your account does not have permission to change this.')
    : !ability.canUpdate('deliverables')
      ? (denialReason(ability, 'update', 'deliverables') ??
        'Your account does not have permission to change this.')
      : dealProductId === null
        ? 'This row names no deal line, so there is nothing to record progress against.'
        : undefined;

  const failureOf = (caught: unknown, requestKey: string): string => {
    if (caught instanceof ApiError) {
      if (caught.status === 403) {
        return 'Your account no longer has permission to change this row.';
      }
      const { fields, unroutable } = caught.routeFieldErrors([requestKey]);
      const text = [...Object.values(fields), ...unroutable].join(' ');
      return text !== '' ? text : caught.message;
    }
    return caught instanceof Error && caught.message !== ''
      ? caught.message
      : 'Could not reach the server. Check your connection and try again.';
  };

  const save: TrackerRowWriter['save'] = async (requestKey, patch) => {
    if (dealProductId === null) {
      return {
        status: 'error',
        message: 'This row names no deal line, so there is nothing to record progress against.',
      };
    }
    let placeholderId: number;
    try {
      const response = await api.createDeliverablesFromDealLines([dealProductId]);
      const made = response.data.created[0];
      if (made === undefined || made.id === null) {
        editing.onDealPlaceholderMade();
        const skipped = response.data.skipped[0];
        return {
          status: 'error',
          message: `${skipped?.message ?? 'This deal line could not be added to the tracker.'} Nothing was saved.`,
        };
      }
      placeholderId = made.id;
    } catch (caught) {
      return { status: 'error', message: `${failureOf(caught, requestKey)} Nothing was saved.` };
    }
    try {
      await api.updateClientDeliverable(placeholderId, patch);
    } catch (caught) {
      editing.onDealPlaceholderMade();
      return {
        status: 'partial',
        message: `The row was added to the tracker, but this change was not saved. ${failureOf(caught, requestKey)}`,
      };
    }
    editing.onDealPlaceholderMade();
    return { status: 'saved' };
  };

  return { canEdit: disabledReason === undefined, disabledReason, save };
}

/**
 * The highlight a manually-added row carries, on the owner's instruction that
 * "only the ones manually added should show the whole row of that in yellow".
 *
 * ✅ **The owner decided a NEUTRAL SURFACE STEP, not a hue** — a standalone row
 * is not a billing state, so it must not borrow a status colour to say so. It
 * stays behind this ONE constant, read by both renderers, so a later change is a
 * single line: do not inline it at a call site.
 *
 * ⛔ **`surface-raised` WAS THE OTHER CANDIDATE AND IS DISQUALIFIED BY
 * MEASUREMENT, not by taste.** In the light theme `--surface-raised` is
 * `oklch(100% 0 0)` and so is `--surface` — the SAME VALUE — and this table sits
 * on a `Panel`, which is `bg-surface`. Contrast against the untinted row is
 * therefore **1.000: a highlight that renders nothing at all in light mode**,
 * while `tsc`, `oxlint` and a dark-theme screenshot all look perfectly fine.
 * `surface-active` measures 1.214 in light and 1.253 in dark — the only step on
 * the ramp that moves AWAY from `surface` in BOTH themes, because the ramp is
 * not symmetric: in light a tint must go darker than a white surface, in dark it
 * must go lighter than a near-black one.
 *
 * ⛔ **NOT A LITERAL HEX.** `#FFFF00` is one colour in a two-theme app.
 * `RevenueTrackerPage` refused the same workbook yellow days ago and translated
 * it up the surface ramp instead; `bg-surface-active` stands here as that
 * precedent's answer until the owner replaces it.
 *
 * ⛔ **NOT `bg-warning-soft`, AND YELLOW IS ALREADY TAKEN TWICE OVER.** It is
 * exactly what `Badge tone="warning"` paints, which is what `awaiting_invoice`
 * renders in the Billing status cell two columns over — the one column the owner
 * wants to go on carrying colour — so the badge would vanish into its own
 * background and the row would claim a complimentary deliverable is in trouble.
 * In the Google Sheet mirror pale yellow is likewise already spoken for: it is
 * the `invoiced` row tint. And it is not even yellow in both themes —
 * `--warning-soft` is `oklch(96% 0.05 85)` light but `oklch(29% 0.07 75)` dark,
 * an amber-brown nobody would call yellow. `--accent-gold` is no way out either:
 * a mid-tone FOREGROUND with no soft step, reserved by its own comment in
 * `index.css` for the `K`/`M` on a compacted figure "and nothing else" — for the
 * reason this bullet just restated.
 *
 * ⛔ **NOT `bg-surface-sunken`.** The project group-header `<TH>` and `THead`
 * both hold it, so a sunken row would read as a second group header.
 *
 * ⚠️ **MEASURED, in both themes, against every token actually rendered in these
 * cells — an estimate is not a measurement, and dark is where this goes wrong.**
 * WCAG contrast on `surface-active`, light / dark:
 *
 *     fg            14.60 / 13.38      fg-secondary  8.59 / 9.23   (TD default)
 *     fg-subtle      4.54 /  5.07      warning       4.62 / 7.19   (status line)
 *     accent-brand   5.25 / 🔴 4.29    ← the company link, BELOW AA in dark
 *
 * Everything clears 4.5:1 except the Client column's `text-accent-brand` link in
 * the dark theme: 5.38 on an untinted row, **4.29 on a tinted one**, so the tint
 * itself is what pushes it under. It is `text-sm` (13px), which is not large
 * text, so 4.5:1 is the threshold that applies. See `STANDALONE_LINK` for the
 * one-step fix; the tint is not weakened to buy it, because a tint weak enough
 * to save that link is a tint nobody can see.
 *
 * ⚠️ **Two things it must live beside, both about hover, both silent:**
 *
 *  - `InlineField`'s own trigger hover is `hover:bg-surface-hover` on a
 *    `<button>` INSIDE the cell, so hovering one of the eight editable fields
 *    paints a patch on the tint. That affordance is what says the value can be
 *    changed and is deliberately NOT removed here — eight fields that stopped
 *    lighting up would read as not editable. Measured separation is small (1.068
 *    light, 1.096 dark), which is the right answer: it reads as a hover, not as
 *    damage.
 *  - The tint must not swallow the status lines `InlineField` renders in the
 *    row — `text-danger`, `text-success` and `text-warning`, the last carrying
 *    both the `warning` AND the `partial` outcome. This is the sharpest argument
 *    against the amber that was rejected above: amber text on an amber fill is a
 *    save that only PARTLY landed going unreported. On this neutral step
 *    `warning` measures 4.62 / 7.19 and stays legible.
 *
 * ⚠️ **THE TINT IS NEVER THE SIGNAL** — see `StandaloneMarker`.
 */
const STANDALONE_ROW_TINT = 'bg-surface-active';

/**
 * The left edge of a highlighted row, drawn on its FIRST CELL and not on the
 * `<tr>`.
 *
 * `Table` is `border-collapse: collapse`, where a border belongs to the table
 * rather than to the element — the same reason `Table.tsx` draws a pinned cell's
 * hairlines as inset shadows rather than borders. A `<tr>` background does paint
 * through (that is how `TR interactive`'s hover already works on this table), so
 * the wash goes on the row and only the edge rule goes on the cell.
 *
 * ✅ **Nothing on this table is `pinned`** — verified against the whole file, and
 * the note on `COLUMN_KEYS` says so independently. So there is no frozen cell
 * carrying its own opaque background for the row wash to fail to reach. If a
 * column is ever pinned here, `PINNED_TD` sets `bg-surface` on the cell itself
 * and this highlight will stop short of it, silently, and only once somebody
 * scrolls sideways.
 *
 * ⚠️ **`box-shadow` is ONE property and two `shadow-[…]` utilities do NOT
 * merge** — one wins, and which one is whatever order Tailwind emitted them in.
 * That is safe here only because this is the sole shadow on both elements it
 * lands on: `TD`'s base classes carry none (a shadow arrives only with `pinned`,
 * which nothing on this table uses) and the sub-`lg` `<li>` carries none either.
 * **If either ever needs a second edge, this becomes ONE whole class literal**
 * with every inset in it, the way `RevenueTrackerPage`'s frozen totals cell is
 * written — never two utilities racing.
 */
const STANDALONE_EDGE = 'shadow-[inset_3px_0_0_0_var(--hairline-strong)]';

/*
 * SUPERSEDED (2026-10 UI rework): a `STANDALONE_LINK` constant used to step the
 * Client link along the brand ramp on a tinted row, because `accent-brand` on
 * `surface-active` measured 4.29:1 in the dark theme. Links are neutral now
 * (`TextLink`, `text-fg`), which measures 14.60 / 13.38 on the tint, so the
 * AA problem it solved no longer exists and the constant is gone.
 */

/**
 * The non-colour half of the standalone highlight — and the half that carries
 * the meaning.
 *
 * ⚠️ **Colour is never the only signal.** A tint is unreadable to a colour-blind
 * reader, invisible to a screen reader, and gone from a greyscale screenshot, so
 * the row says what it is in words as well.
 *
 * It sits in the Client cell, under the company name, because that is the cell
 * that identifies the row — and because the tracker's 15 columns and the width
 * they already sum to mean a marker gets an existing cell, never a new column.
 * ⚠️ *SUPERSEDED, the figure: `min-w-[1972px]`.* The floor is computed from the
 * VISIBLE columns now (`tableMinWidth`), so there is no one number to quote —
 * and the argument is unchanged, because the Client column is locked and is
 * therefore the one cell guaranteed to be on screen for the marker to sit in.
 *
 * ⚠️ It is a VALUE, not helper text: it names which population this row belongs
 * to, rather than explaining a control or a colour. A sentence anywhere on this
 * page explaining what the highlight means WOULD be helper text and is
 * forbidden — the badge has to do that work by itself.
 *
 * ⚠️ **The ABSENCE of a document reference is not this marker and cannot stand in
 * for it.** `DocumentsCell` falls through to `NotRecorded` on a standalone row
 * carrying no `label`, and an empty cell reads as missing data — the exact wrong
 * conclusion, since these rows are correct and complete.
 *
 * The wording is "Added manually" and deliberately not "No document":
 * `BILLING_LABELS.none` is already the string "No document" and renders two
 * columns away, so that phrase would put one form of words against two different
 * facts in a single row.
 */
function StandaloneMarker() {
  return <Badge tone="neutral" variant="outline">Added manually</Badge>;
}

/**
 * What an inclusion row says it is.
 *
 * A row here is normally a LINE — a product sold, with a quantity. These two
 * populations are one step inside that: a campaign inclusion an administrator
 * has marked as tracked, which is a separate promise living inside the line. One
 * line carrying three of them produces three of these rows **beside** its own,
 * so without a marker the table shows four rows that look like four line items
 * and share a product, an intake and a document reference.
 *
 * ⚠️ **It is a VALUE, not helper text** — the same standing as
 * `StandaloneMarker`, which the owner's highlight already relies on. It names
 * which population this row belongs to; it does not explain a control, and
 * there is deliberately no sentence anywhere on this page explaining why these
 * rows carry no editors.
 *
 * ⛔ **AND IT SAYS WHAT THE ROW IS, NEVER WHAT CANNOT BE DONE TO IT.** "Cannot
 * be edited" and "not editable yet" were both available and are both refused:
 * the first is a claim about a control on a page whose other rows have one, and
 * the second asserts an absence, which rots the moment somebody builds the
 * thing — and the builder never comes back to re-read a badge. *Campaign
 * inclusion* stays true whatever is built later.
 *
 * ⚠️ **NO TINT, and that is the owner's instruction rather than an omission.**
 * `STANDALONE_ROW_TINT` exists because the owner asked that *"only the ones
 * manually added should show the whole row"* — so painting a second population
 * with it would make a row claim to be hand-entered, and painting a different
 * colour would put a second meaning on a ramp where the one tint already means
 * one thing. The word does the work, which is what the standalone row's own
 * comment says the word is for.
 */
function InclusionMarker() {
  return <Badge tone="neutral" variant="outline">Campaign inclusion</Badge>;
}

/** The "Deal" badge on a deal placeholder row, a real link to the deal; the tooltip names it. */
function DealMarker({ deal }: { deal: { id: number; name: string } }) {
  return (
    <Tooltip label={deal.name}>
      <Link
        to={`/deals/${deal.id}`}
        className="inline-flex rounded-sm underline-offset-2 hover:underline"
      >
        <Badge tone="warning" variant="outline">
          Deal
        </Badge>
      </Link>
    </Tooltip>
  );
}

/**
 * The marker for whichever population this row is in, or nothing.
 *
 * `attached` is the ordinary row and carries none — a badge on the majority
 * teaches people to stop reading badges. `unknown` carries none either, and
 * that is deliberate rather than an oversight: a build that does not recognise
 * a population cannot name it, and guessing a label would state something about
 * the row that this code does not know. It still loses its editors, because
 * `rowIsEditable` answers a different question and answers it conservatively.
 */
function RowMarker({ row, population }: { row: ClientDeliverable; population: RowPopulation }) {
  /*
   * The wrapping `<span>` lives HERE rather than at the two call sites, so a
   * population with no marker renders nothing at all — no empty element holding
   * open a grid row and its gap. It is what stops the Client cell growing a
   * blank line under every ordinary row.
   */
  // A deal placeholder (derived deal line, or a standalone row made from a deal)
  // says "Deal" instead of "Added manually" (owner 2026-10-27).
  const deal = dealMarkerOf(row);
  if (deal !== null) {
    return (
      <span>
        <DealMarker deal={deal} />
      </span>
    );
  }
  if (population === 'deal') {
    return (
      <span>
        <Badge tone="warning" variant="outline">
          Deal
        </Badge>
      </span>
    );
  }
  if (population === 'standalone') {
    return (
      <span>
        <StandaloneMarker />
      </span>
    );
  }
  if (population === 'inclusion') {
    return (
      <span>
        <InclusionMarker />
      </span>
    );
  }
  return null;
}

/*
 * ⚠️ **`DatesCell` STOOD HERE — it stacked both date editors in one "Dates"
 * column.** Owner, 2026-10-04: Start date and End date are now two columns,
 * each hosting ONE editor (`StartDateField` / `EndDateField`) and each with its
 * own server-side From/To filter. The rule it carried is unchanged and lives with
 * the editors: TWO fields, TWO writes — the server merges a lone date key against
 * the stored row before its ordering CHECK, so never fuse them into one control
 * that patches both. Plain `YYYY-MM-DD` end to end; never construct a `Date`.
 */

/**
 * "Piece n of m" on a split row, plus a warning when the pieces on screen no
 * longer add up to the whole row's promised quantity (the line quantity can be
 * edited after the split).
 *
 * ⚠️ **The sum is of the pieces ON SCREEN.** A filter can hide a sibling, so the
 * warning only fires when every piece of the split is present — a partial set
 * cannot be judged and must not be accused.
 */
function PieceMarker({ row, siblings }: { row: ClientDeliverable; siblings: ClientDeliverable[] }) {
  const piece = row.piece;
  if (piece === null) return null;
  const pieces = siblings.filter((sibling) => sibling.piece !== null);
  const complete = pieces.length === piece.of;
  const sum = pieces.reduce((total, sibling) => total + sibling.promised, 0);
  const mismatch = complete && sum !== piece.rowPromised;
  return (
    <span className="flex flex-wrap items-center gap-1">
      <Badge tone="neutral" variant="outline">{`Piece ${piece.position} of ${piece.of}`}</Badge>
      {mismatch && (
        <Badge tone="warning">{`Pieces total ${sum}, row promises ${piece.rowPromised}`}</Badge>
      )}
    </span>
  );
}

/**
 * The split target a row belongs to — the identity siblings share.
 *
 * A line row is its line (kind + id); a standalone row is its overlay id. An
 * inclusion row, or a standalone row with no overlay, has none and cannot be
 * split.
 */
function splitTargetKey(row: ClientDeliverable): string | null {
  const population = populationOf(row);
  if (population === 'attached') {
    return row.line === null ? null : `${row.line.kind}:${row.line.id}`;
  }
  if (population === 'standalone') {
    return row.id === null ? null : `s:${row.id}`;
  }
  return null;
}

/* -------------------------------------------------------------------------
 * The read-only half of the eight typed columns
 *
 * Mounted INSTEAD OF the editors on a row `rowIsEditable` refuses — today an
 * inclusion row, and any population a later build does not recognise. Read that
 * function for why the controls have to be absent rather than disabled; what
 * follows is only what takes their place.
 *
 * ⚠️ **They render the SAME MARKUP the editors' `display` renders**, class for
 * class, because these two states sit in one column and a value that changed
 * size or weight when it lost its control would read as a different kind of
 * value rather than as the same one. The only thing missing is the trigger.
 *
 * ⚠️ **Each falls back to `NotRecorded`, the same dash every unset value on this
 * page gets** — never a sentence, and never one saying why. Six of these columns
 * live on `d_client_deliverables`, which an inclusion row has none of, so they
 * will be empty down the whole population; a column of dashes is the DATA here
 * exactly as it is for Ads plan, and copy explaining it would be helper text
 * about a state the reader can already see.
 * --------------------------------------------------------------------- */

/**
 * ⚠️ **A THIRD COPY of two words, and it is recorded rather than hidden.**
 * `ENGAGEMENT_ROLE_LABELS` in `InlineTrackerFields` and
 * `ENGAGEMENT_ROLE_OPTIONS` in `DeliverableFormModal` already spell these, and
 * neither is exported. The slug is `full_time`, so the alternative was printing
 * a wire token onto a customer-facing screen — which is the failure the editor's
 * own `display` exists to prevent. Typed as a `Record` over the union so a third
 * member breaks the build here too; **all three move together.**
 */
const READ_ONLY_ENGAGEMENT_LABELS: Record<EngagementRole, string> = {
  full_time: 'Full-time',
  part_time: 'Part-time',
};

/** POC — the same `deliveryLead ?? clientSuccessManager` the column resolves. */
function ReadOnlyPoc({ row }: { row: ClientDeliverable }) {
  /*
   * Read through the same `??` the editable cell and the column funnel use, and
   * NOT shortened to the CSM on the grounds that an inclusion row can hold no
   * delivery lead. That is true of the overlay column today and it is a fact
   * about the server, not about this cell — resolving both keeps the three
   * readings of POC identical whatever the server does next.
   */
  const shown = row.deliveryLead ?? row.clientSuccessManager;
  if (shown === null) return <NotRecorded />;
  return (
    <span
      className="block truncate"
      title={
        row.deliveryLead === null
          ? `${shown.name} — the client success manager on this company.`
          : `${shown.name} — the delivery lead on this row.`
      }
    >
      {shown.name}
    </span>
  );
}

/** The inclusion's OWN quantity — never the line's, and never summed with it. */
function ReadOnlyPromised({ row }: { row: ClientDeliverable }) {
  return <span className="tabular">{row.promised}</span>;
}

/**
 * 🔴 **THE DASH IS THE HONEST RENDERING AND `0` IS NOT.**
 *
 * `completed` is always `0` on an inclusion row, and that zero is STRUCTURAL:
 * the column lives on the overlay table, so it is not a count somebody took and
 * found to be nothing — it is a count nobody can take from here. Printing the
 * figure would say "nothing has been delivered", which is a measurement this row
 * has never carried, down a column where every other row's `0` really does mean
 * that. The dash says the same thing every other unrecorded value on this page
 * says, which is all that is true.
 *
 * ⚠️ **The column funnel is kept in step at `TRACKER_COLUMNS`** — its `get`
 * returns `null` for this population, so the funnel offers `(Empty)` rather than
 * a `0` option that would select rows showing a dash.
 */
function ReadOnlyCompleted() {
  return <NotRecorded />;
}

/** Booking specifications, plus the engagement role — the cell's two values. */
function ReadOnlyBookingSpec({ row }: { row: ClientDeliverable }) {
  const spec = row.bookingSpec && row.bookingSpec.trim() !== '' ? row.bookingSpec : null;
  const role = row.engagementRole;
  if (spec === null && role === null) return <NotRecorded />;
  return (
    <div className="grid gap-1">
      {/*
       * Both are rendered when both are set. Neither stands in for the other —
       * a booking specification and a full-time/part-time engagement are two
       * facts, and a `??` between them would drop whichever lost.
       */}
      {spec !== null && (
        <span className="text-xs text-fg-secondary [overflow-wrap:anywhere]">{spec}</span>
      )}
      {role !== null && (
        <span>
          <Badge tone="neutral" variant="outline">{READ_ONLY_ENGAGEMENT_LABELS[role]}</Badge>
        </span>
      )}
    </div>
  );
}

/**
 * Travel — the EFFECTIVE answer, which the server derives from the catalogue and
 * the parent line's document.
 *
 * ⚠️ **The editor's second line is deliberately not reproduced.** It reads "Set
 * on this row" / "From the document" and exists to tell an override apart from a
 * derivation — a distinction that cannot arise here, because an override is a
 * stored value and there is nowhere to store one. A line saying "From the
 * document" would be true and would still be an explanation of a state the
 * reader has no control over, which is the sentence the no-helper-text rule
 * refuses. The badge is the value.
 */
function ReadOnlyTravel({ row }: { row: ClientDeliverable }) {
  return (
    <Badge tone={row.travel ? 'info' : 'neutral'} dot>
      {travelLabel(row.travel)}
    </Badge>
  );
}

/** One date, formatted without ever constructing a `Date`. */
function ReadOnlyDate({ value }: { value: string | null }) {
  if (value === null) return <NotRecorded />;
  return (
    <span className="whitespace-nowrap text-xs text-fg-secondary tabular">
      {formatDateOnly(value)}
    </span>
  );
}

/** The company's recorded type; a dash when it has none (or the API predates it). */
function CompanyTypeCell({ row }: { row: ClientDeliverable }) {
  const name = row.company?.type?.name;
  if (name === undefined || name.trim() === '') return <NotRecorded />;
  return <span className="block truncate text-xs text-fg-secondary">{name}</span>;
}

/*
 * `ReadOnlyDates` stood here and stacked both dates; with the two date columns
 * split (2026-10-04) each column renders `ReadOnlyDate` on its own value.
 */

/** Free text, at the weight its own column renders it. */
function ReadOnlyText({ value, className }: { value: string | null; className: string }) {
  if (value === null || value.trim() === '') return <NotRecorded />;
  return <span className={className}>{value}</span>;
}

/**
 * Booking specifications, plus the engagement role.
 *
 * The role rides in this cell rather than taking a column of its own: it is a
 * specification of the booking, and the workbook's column set — which this table
 * reproduces exactly — has no separate heading for it. Rendering it nowhere
 * would make a contract field unreachable in the only screen that shows it.
 */
function BookingSpecCell({ row, writer }: { row: ClientDeliverable; writer: TrackerRowWriter }) {
  return (
    <div className="grid gap-1">
      <BookingSpecField row={row} writer={writer} />
      <EngagementRoleField row={row} writer={writer} />
    </div>
  );
}

/**
 * The workbook's "Ads plan" — TYPED free text on the row, not derived from
 * anything, because nothing on a quote or invoice line records an ads plan.
 *
 * ⚠️ **EXPECT IT TO BE EMPTY, AND SAY NOTHING ABOUT THAT.** Measured 2026-08-31
 * from the workbook's own sheet XML: the Ads plan column carries ZERO values on
 * all three sheets that have it, across three years — 374 data rows on the live
 * one, one non-empty cell in the whole column and that cell is the header. A
 * column of dashes here is the DATA. It gets the same `NotRecorded` dash every
 * other unset value on this page gets, and no sentence of its own: an
 * explanation would be helper text, and a hopeful one would promise values that
 * nothing is bringing.
 *
 * `[overflow-wrap:anywhere]` and no `truncate`, matching `BookingSpecCell`: the
 * one ads plan ever actually written down anywhere in the workbook is four
 * lines of prose in "Booking specifications", so this is a list rather than a
 * label and clipping it would hide the counts that are the point of it.
 */
function AdsPlanCell({ row, writer }: { row: ClientDeliverable; writer: TrackerRowWriter }) {
  return <AdsPlanField row={row} writer={writer} />;
}

/* -------------------------------------------------------------------------
 * Grouping
 * --------------------------------------------------------------------- */

interface RowGroup {
  /** `p:<id>` or the literal `none` — never the label, which is not unique. */
  key: string;
  label: string;
  rows: ClientDeliverable[];
  /**
   * The band's company-group runs (Institutions, then Partners — owner
   * 2026-10-18), consecutive and in the order received. `null` when no row in
   * the band carries `companyGroup` at all: an older API, rendered with no
   * sub-headers exactly as before.
   */
  companyGroups: { key: string; label: string; rows: ClientDeliverable[] }[] | null;
}

/**
 * Groups CONSECUTIVE runs of rows by project, never by collecting them into a
 * map.
 *
 * The distinction matters: the API returns rows already ordered by project and a
 * map-based grouping would silently REORDER anything that did not arrive that
 * way, so the page would show an arrangement the server never sent and no filter
 * or sort could explain. Runs show exactly what came back — if a project appears
 * twice, that is the response, and it is visible rather than papered over.
 *
 * ⚠️ THAT IS MORE LOAD-BEARING SINCE 2026-09-02, NOT LESS. Every sort now leads
 * with the band key server-side, so the ordering this walk depends on is no
 * longer a property of one sort that the page could check for — it is a promise
 * the server makes on all five, and the ONLY way this page can show that the
 * promise was broken is by drawing the shattered bands. A map here would repair
 * the symptom and hide the defect, which is exactly what happened before the
 * band key led every sort: the rows interleaved and there was nothing on screen
 * to say so.
 *
 * A row with no project gets its own run. `project: null` does not prove the row
 * has no project — the server's lookup swallows its own failures — so the label
 * is deliberately neutral rather than a confident "No project".
 */
function groupByProject(rows: ClientDeliverable[]): RowGroup[] {
  const groups: RowGroup[] = [];
  for (const row of rows) {
    const key = row.project ? `p:${row.project.id}` : 'none';
    const label = row.project ? row.project.name.trim() || `Project #${row.project.id}` : 'No project recorded';
    const last = groups.at(-1);
    if (last && last.key === key) last.rows.push(row);
    else groups.push({ key, label, rows: [row], companyGroups: null });
  }
  /*
   * Inside each band, the company-group runs — split by the same consecutive
   * walk, never re-sorted: the server orders band → group → sort, and a
   * shattered group run is a server defect this page should show, not hide.
   */
  const grouped = rows.some((row) => row.companyGroup !== undefined);
  if (grouped) {
    for (const group of groups) {
      group.companyGroups = splitByCompanyGroup(group.rows, (row) => row.companyGroup);
    }
  }
  return groups;
}

/*
 * ✅ GROUP HEADERS ARE DRAWN UNDER EVERY SORT — owner's instruction, 2026-09-02:
 * *"while sorting by any column, the project name should not get sorted — the
 * deliverables inside each project should get sorted."*
 *
 * ⚠️ *SUPERSEDED: "Group headers are drawn ONLY under the default `project`
 * sort, and this is the same rule the audit log applies to its day headings.
 * Under any other order a project's rows interleave, so a header would announce
 * a section that ends two rows later and starts again further down."* The
 * REASONING was right about the clause as it stood and is now void: the server
 * leads every sort with the project band key, so a project's rows can no longer
 * interleave and there is nothing left for the gate to protect against. A column
 * sort orders the rows INSIDE each band.
 *
 * ⚠️ THE AUDIT-LOG ANALOGY DOES NOT CARRY ACROSS AND MUST NOT BE RE-DERIVED.
 * `ApiLogsPage` still gates its day headings on `sort === 'createdAt'`, and
 * correctly — its server sorts globally, so under "Slowest first" the days
 * genuinely do interleave. The two pages look alike and their servers do not
 * behave alike; do not restore a gate here on the strength of that resemblance.
 */

/* -------------------------------------------------------------------------
 * Skeletons
 * --------------------------------------------------------------------- */

/**
 * ✅ **THE SHIMMER ROW IS DRIVEN BY THE SAME KEY LIST THE TABLE IS.**
 *
 * ⚠️ *SUPERSEDED: fifteen hand-written `<TD>`s, and before that FOURTEEN against
 * THIRTEEN columns — a spurious cell that sat here for as long as the column list
 * had existed and was removed on 2026-09-02.* That comment ended "keep the three
 * in step: header, body and this", which is an instruction a person has to
 * remember; the three are now one `map` over `columns.visibleKeys` and one
 * `Record` keyed by the column key, so a missing shimmer is a compile error
 * rather than a row that renders one cell too wide for a fraction of a second.
 *
 * It is found by counting, never by looking — which is exactly why it is no
 * longer counted by hand.
 */
function TableSkeleton({
  columns,
  frozenOffsets,
}: {
  columns: ColumnLayoutController;
  frozenOffsets: Record<string, number>;
}) {
  return (
    <TBody>
      {Array.from({ length: SKELETON_ROWS }, (_, i) => (
        <TR key={i}>
          {columns.visibleKeys.map((key) => {
            const chrome = COLUMN_CHROME[key as TrackerColumnKey];
            const frozen = columns.frozenSideOf(key);
            return (
              <TD
                key={key}
                numeric={chrome.numeric}
                // Frozen here too, so a frozen column does not scroll out from
                // under its own stuck header for the moment the first page is in
                // flight — which reads as the freeze not working.
                {...(frozen ? { frozen, frozenOffset: frozenOffsets[key] ?? 0 } : {})}
              >
                <Skeleton className={chrome.skeleton} />
              </TD>
            );
          })}
        </TR>
      ))}
    </TBody>
  );
}

function ListSkeleton() {
  return (
    <ul className="m-0 list-none p-0">
      {Array.from({ length: SKELETON_ROWS }, (_, i) => (
        <li key={i} className="border-b border-border px-4 py-3 last:border-b-0">
          <Skeleton className="h-3.5 w-44" />
          <div className="mt-2 flex items-center gap-2">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-24" />
          </div>
          <div className="mt-2.5 grid gap-1.5">
            <Skeleton className="h-2.5 w-40" />
            <Skeleton className="h-2.5 w-32" />
          </div>
        </li>
      ))}
    </ul>
  );
}

/* -------------------------------------------------------------------------
 * Column filters
 * --------------------------------------------------------------------- */

/**
 * The Progress column's filterable value. `pending` is signed, so overdelivered
 * rows get their own bucket rather than being folded into "Complete".
 */
// Exported for the company page's Services section (2026-10-18).
export function deliveryState(row: ClientDeliverable): string {
  if (row.pending < 0) return 'Overdelivered';
  if (row.pending === 0) {
    return row.promised === 0 && row.completed === 0 ? 'Nothing promised' : 'Complete';
  }
  return row.completed === 0 ? 'Not started' : 'In progress';
}

/**
 * The travel value as the column funnel's option label.
 *
 * ⚠️ It used to return `string | null`, because travel was hand-entered and
 * tri-state, and the unrecorded state had to reach the funnel as `(Empty)`
 * rather than being folded into "No travel" — those are different facts and
 * merging them would have handed somebody filtering to "not travelling" a set of
 * rows nobody had answered for. Making travel derived closed that: a derivation
 * always has an answer, so two options became the honest number.
 *
 * 🔴 **THE OVERRIDE DID NOT REOPEN IT, AND THE REASON IS WORTH READING BEFORE
 * ANYBODY "RESTORES" A THIRD OPTION — 2026-09-09.** There are three states on
 * the ROW again (`row.travelOverride` is `boolean | null`), but this funnel
 * filters on `row.travel`, which is the EFFECTIVE answer and is still a
 * `boolean`. "Show me the rows that travel" is a question about the answer, not
 * about who supplied it — and a third bucket here would hide travelling rows
 * from a reader who ticked "Travelling", because a derived one would have fallen
 * into it. Which state a row is in is visible in the cell itself.
 *
 * The words come from `travelLabel`, which the badge and the picker's two
 * options also read, so no two of the three can disagree about what a row says.
 */
function travelState(travel: boolean): string {
  return travelLabel(travel);
}

/*
 * Every column is `scope: 'page'`, deliberately: the ten server filters are
 * already driven by the URL-backed bank above the table, and declaring any of
 * them again on a header would give one query param two uncoordinated controls.
 *
 * ✅ **RE-AUDITED 2026-09-09 against `listQuerySchema` in the backend repo, and
 * every column stays page-scoped.** The finding worth keeping is that the two
 * halves fail the test for opposite reasons, so neither is a candidate waiting
 * to be picked up: Company Name, Billing Status, Region of Focus, Intake and
 * Product each map onto a param the BANK ALREADY WRITES (`companyId`,
 * `billingStatus`, `regionId`, `intakeId`, `productId`), and the rest — Promised,
 * Completed, Pending, Booking specifications, Travel, Invoice/Quote Number,
 * Dates, Billing note, Ads plan — have **no query key at all**, so a `param` for
 * one would be a name this app invented. Not a 422 either: the schema is
 * `.strict()`, so an invented key takes the whole list down rather than being
 * ignored. POC is the single exception to both and is refused on its own
 * grounds — see its column below. `ClientDeliverableListParams` is the list to
 * check against, never this paragraph.
 *
 * ⚠️ **And a server column here would have to answer to the BANDING.** The
 * server leads every sort with the project arrangement and this page groups
 * consecutive runs unconditionally, so any filter must narrow rows WITHIN that
 * order rather than reorder it. Every param above does; that is a property of
 * the endpoint, not something a header could guarantee for a key it made up.
 * It would also lose what the bank does that a header cannot — survive a
 * refresh, be linked to, and be the ONLY filter UI below `lg`, where the header
 * row is not rendered at all.
 *
 * `sortKey` appears only on the columns the API can order by. An unrecognised
 * `sort` is a 422, so a header that highlighted while nothing moved would be
 * worse than no control.
 */
const TRACKER_COLUMNS: ColumnSpec<ClientDeliverable>[] = [
  /*
   * ⚠️ THE LABELS BELOW ARE THE WORKBOOK'S, VERBATIM, INCLUDING ITS CASING.
   * Three were shortened or recased and were corrected on 2026-09-01:
   * "Client" -> "Client Name", "Billing status" -> "Billing Status",
   * "Invoice/Quote" -> "Invoice/Quote Number". They read as house style rather
   * than as a divergence, which is why they survived — but this page's promise
   * to the people who kept the sheet is that a column is called what they call
   * it. `SHEET_HEADERS` in the backend mirror is the same list.
   *
   * 🔴 **ONE LABEL NOW DIVERGES ON PURPOSE, AND THE TWO SIDES ARE MEANT TO —
   * 2026-09-03, owner's instruction: the FIRST column reads "Company Name" in
   * the TABLE VIEW.** *SUPERSEDED, the clause that used to close this comment:
   * "the two must not drift."* The instruction was scoped to the table, so the
   * MIRROR IS UNTOUCHED and its header row still says "Client Name".
   *
   * ⚠️ **That is a divergence to leave alone rather than an inconsistency to
   * tidy up, and "harmonising" it in either direction is the damaging move.**
   * Renaming the sheet's header to match would change `SHEET_HEADERS`, and the
   * backend recognises its own tab BY ITS HEADER ROW: an unrecognised set means
   * the mirror declines to write and the owner's tab silently goes stale, which
   * reads as "this cycle has no deliverables". Renaming this label back would
   * undo an instruction. The word EMERGE uses for the record is *company*; the
   * word the workbook uses is *client*; each surface now says the one its
   * readers use.
   */
  {
    key: 'client',
    label: 'Company Name',
    get: (row) => row.company?.name ?? null,
    sortKey: 'company' satisfies ClientDeliverableSort,
  },
  /*
   * The company's RECORDED type (owner, 2026-10-18) — never the display group a
   * typeless company is listed under, so a company with no type shows a dash.
   * No `sortKey`: the group already orders every band, and the API has no sort
   * member for it.
   */
  {
    key: 'companyType',
    label: 'Type',
    get: (row) => row.company?.type?.name ?? null,
  },
  {
    key: 'billingStatus',
    label: 'Billing Status',
    // The STATUS, never `invoiceStatusName`. `format` turns the slug into the
    // same words the badge shows, so the funnel and the cell cannot disagree.
    get: (row) => row.billing.status,
    format: (value) => BILLING_LABELS[value as BillingStatus] ?? value,
    sortKey: 'billingStatus' satisfies ClientDeliverableSort,
  },
  {
    key: 'regions',
    label: 'Region of Focus',
    /*
     * A SET flattened to the joined cell, and the funnel filters on THAT — so
     * one option per combination ("India", "India, Africa"), not one per region.
     *
     * ⚠️ Deliberately NOT `matches`, and the reason is worth keeping because the
     * obvious version is wrong and fails silently. A page-scoped column's option
     * VALUES are the distinct results of `get`; `matches` only replaces the
     * comparison. A `matches` testing individual region names would therefore be
     * handed "India, Africa" and compare it against "India" and "Africa",
     * matching neither — so ticking any multi-region option would hide the very
     * rows it names, with nothing failing.
     *
     * Filtering to one region across the whole tracker is what the `regionId`
     * filter in the bank above does, server-side and honestly.
     */
    get: (row) => row.regions.map((region) => region.name).join(', '),
    sortKey: 'regions' satisfies ClientDeliverableSort,
  },
  {
    key: 'cities',
    label: 'Cities',
    /*
     * ⚠️ **PAGE-SCOPED, AND THERE IS NO SERVER ALTERNATIVE TO WEIGH IT AGAINST.**
     * Checked against `ClientDeliverableListParams` before this was added:
     * **`cityId` is not a member**, and the list schema is `.strict()` — so a
     * `scope: 'server'` here would not be ignored, it would 422 and take the whole
     * table out. There is no key to invent a coordinated control for either, which
     * is the test every other column on this table fails in one direction or the
     * other.
     *
     * ⚠️ **The same flattened-set reading as Region of Focus, and deliberately NOT
     * `matches`** — a page-scoped column's option VALUES are the distinct results
     * of `get`, and `matches` only replaces the comparison. A `matches` testing
     * individual city names would therefore be handed "Bangalore, Mumbai, Delhi"
     * and compare it against "Mumbai", matching neither, so ticking any
     * multi-city option would hide the very rows it names with nothing failing.
     *
     * ⚠️ **Accepted cost, stated rather than discovered: one option per
     * COMBINATION.** A line running in five cities produces one long option, and
     * two lines covering the same five in a different order produce two. That is
     * the honest reading of what is on the page — the funnel offers the values the
     * cells show — and it is the same bargain Region of Focus already makes; there
     * is simply more of it, because a line names more cities than regions. The
     * string comes from `cityNames`, so an option and its cell cannot disagree.
     *
     * ⚠️ **`null` where the cell is empty, so the funnel offers `(Empty)`** rather
     * than an `''` option selecting rows that show nothing — the rule Billing
     * Status, Travel and Completed already follow. `(Empty)` is how a reader finds
     * the standalone rows, which are the permanently city-less population.
     */
    get: (row) => (row.cities.length > 0 ? cityNames(row) : null),
    sortKey: 'cities' satisfies ClientDeliverableSort,
  },
  /**
   * The workbook's "POC", restored 2026-09-01 at the workbook's own position.
   *
   * ⚠️ **It is DERIVED from two fields and shows ONE.** Owner's rule: the
   * client success manager, and the delivery lead instead wherever one is set
   * for those deliverables. Only the delivery lead is typed here; the CSM is
   * read through the company.
   *
   * ⚠️ *SUPERSEDED: this column rendered `accountOwner`, and was removed on
   * 2026-08-31.* Do not point it back at that field — it is a different person
   * (this row's own account owner) and nothing writes it from this app.
   *
   * The `??` is deliberate and is NOT the silent-drop shape this codebase
   * warns about: the two are alternatives for one slot rather than two facts
   * that can both be true and both need showing. Which one is in play is
   * visible in the row's own editor, where the delivery lead is the control.
   *
   * 🔴 **THIS IS THE ONE COLUMN ON THIS PAGE WITH A FREE QUERY KEY BESIDE IT,
   * AND IT STILL MUST NOT BECOME `scope: 'server'`. `accountOwnerId` DOES NOT
   * FILTER ON WHAT THIS COLUMN SHOWS.** It is the only member of
   * `ClientDeliverableListParams` no control here writes, so it passes the
   * "one writer" test that rules out every other column — and it is the wrong
   * key anyway. Measured in the backend repository, not inferred from the name:
   * `buildFilter` emits `t.account_owner_id = :accountOwnerId`, the column
   * migration 131 SUPERSEDED, while this cell reads `delivery_lead_id` falling
   * back to the COMPANY's client success manager. Two different people, and one
   * of them lives on another table — no single id param can express the `??`
   * above, and nothing in this app has written `account_owner_id` since
   * 2026-08-31.
   *
   * ⚠️ **It would fail silently and in the convincing direction.** The key is
   * accepted, so there is no 422 to find: the request succeeds and returns the
   * rows of a person nobody is looking at — in practice none — under a funnel
   * labelled POC. An empty tracker reads as "that person has no deliverables",
   * which is a sentence somebody acts on.
   *
   * What would close this is a filter matching the RESOLVED POC the way
   * `/deals?accountLeadId=` matches the resolved account lead. Raised with the
   * backend; **do not synthesise one from `accountOwnerId` meanwhile.**
   */
  {
    key: 'poc',
    label: 'POC',
    get: (row) => row.deliveryLead?.name ?? row.clientSuccessManager?.name ?? null,
    sortKey: 'poc' satisfies ClientDeliverableSort,
  },
  /*
   * The LAE representative (owner, 2026-10-04). PAGE-SCOPED: no list param
   * filters on a specialist, and the list schema is `.strict()`, so a
   * `scope: 'server'` here would 422 the whole table. `null` where none is set,
   * so the funnel offers `(Empty)`.
   */
  {
    key: 'specialist',
    label: 'Specialist',
    get: (row) => row.specialist?.name ?? null,
    sortKey: 'specialist' satisfies ClientDeliverableSort,
  },
  // Note the key: `intakeName`, not `name`.
  {
    key: 'intake',
    label: 'Intake',
    get: (row) => row.intake?.intakeName ?? null,
    sortKey: 'intake' satisfies ClientDeliverableSort,
  },
  // And here: `productName`.
  {
    key: 'product',
    label: 'Product',
    get: (row) => row.product?.productName ?? null,
    sortKey: 'product' satisfies ClientDeliverableSort,
  },
  /*
   * The line's campaign inclusions (owner, 2026-10-27). Read-only, no `sortKey`
   * (the API has no sort member for it). Empty or an older API's absent key is
   * `null`, so the funnel offers `(Empty)` where the cell shows a dash.
   */
  {
    key: 'inclusions',
    label: 'Inclusions',
    get: (row) => (row.inclusions && row.inclusions.length > 0 ? inclusionsLabel(row.inclusions) : null),
  },
  /*
   * ⚠️ **The inclusion rows' promises are in this funnel and are NOT comparable
   * with the line rows' beside them.** A line promising 10 campuses and an
   * inclusion inside it promising 5 of something else both produce an option
   * here. That is the honest reading — the funnel offers the values that are on
   * the page — and it is the reason nothing anywhere totals this column.
   */
  {
    key: 'promised',
    label: 'Promised',
    get: (row) => row.promised,
    numeric: true,
    sortKey: 'promised' satisfies ClientDeliverableSort,
  },
  {
    key: 'completed',
    label: 'Completed',
    /*
     * ⚠️ **`null` on a row whose cell shows a dash, so the funnel and the cell
     * cannot disagree** — the same rule the Billing Status and Travel columns
     * follow. An inclusion row's `completed` is a STRUCTURAL zero (no overlay
     * row, so no count can ever be recorded), and `ReadOnlyCompleted` renders
     * `NotRecorded` for it. Returning the raw `0` here would put those rows
     * under a `0` option that selects rows showing no figure at all, and would
     * mix them in with the line rows where `0` is a real measurement somebody
     * took. `(Empty)` is what the funnel offers instead, which is what every
     * other unrecorded value on this page offers.
     */
    get: (row) => (rowIsEditable(populationOf(row)) ? row.completed : null),
    numeric: true,
    sortKey: 'completed' satisfies ClientDeliverableSort,
  },
  {
    key: 'pending',
    label: 'Pending',
    get: deliveryState,
    sortKey: 'pending' satisfies ClientDeliverableSort,
  },
  {
    key: 'bookingSpec',
    label: 'Booking specifications',
    get: (row) => row.bookingSpec,
    sortKey: 'bookingSpec' satisfies ClientDeliverableSort,
  },
  {
    key: 'travel',
    label: 'Travel',
    get: (row) => travelState(row.travel),
    sortKey: 'travel' satisfies ClientDeliverableSort,
  },
  {
    key: 'documents',
    label: 'Invoice/Quote Number',
    // Counted, not listed: a cell naming three invoice numbers produces one
    // funnel option per combination, which is an option list nobody can use.
    get: (row) =>
      row.billing.invoices.length > 0
        ? `${row.billing.invoices.length} invoiced`
        : row.billing.quote
          ? 'Quoted'
          : null,
    sortKey: 'documents' satisfies ClientDeliverableSort,
  },
  /*
   * The two date columns (owner, 2026-10-04 — *SUPERSEDED: one "Dates" column*).
   * Each carries a DATE range whose From/To bounds go to the server as the
   * inclusive `startDateAfter`/`startDateBefore` and `endDateAfter`/
   * `endDateBefore` — see where `table.serverParams` is merged into the request.
   * ⚠️ An undated row matches NEITHER bound, so the pair does not partition the
   * list; Is empty / Is not empty stay the way to reach it. The value stays the
   * plain 'YYYY-MM-DD' string: it sorts lexicographically and `formatDateOnly`
   * renders it without a timezone.
   */
  {
    key: 'startDate',
    label: 'Start date',
    get: (row) => row.startDate,
    format: formatDateOnly,
    sortKey: 'startDate' satisfies ClientDeliverableSort,
    range: { date: true, minParam: 'startDateAfter', maxParam: 'startDateBefore' },
  },
  {
    key: 'endDate',
    label: 'End date',
    get: (row) => row.endDate,
    format: formatDateOnly,
    sortKey: 'endDate' satisfies ClientDeliverableSort,
    range: { date: true, minParam: 'endDateAfter', maxParam: 'endDateBefore' },
  },
  /*
   * Last, as the workbook has it. *SUPERSEDED 2026-10-09: "No `sortKey`:
   * `ClientDeliverableSort` has no member for it."* It has one now (`adsPlan`).
   *
   * The funnel is worth having even over an empty column — its one option will
   * be `(Empty)`, which is a truthful reading of the data rather than a broken
   * control, and it becomes useful the day somebody types the first plan.
   */
  /**
   * The workbook's column P, between its Dates column (O) and Ads plan (Q).
   * The table now shows those dates as two columns, Start date and End date.
   *
   * ⚠️ It was NESTED inside the Billing Status cell until 2026-09-02, on the
   * belief that the workbook had no such column — a belief the backend mirror
   * asserted in one comment and contradicted in another, thirty lines apart.
   * Reading `P1` on the live sheet settled it. The FIELD never changed: it has
   * always been typed and always been editable; only where it renders moved.
   */
  {
    key: 'billingNote',
    label: 'Billing note',
    get: (row) => row.billingNote,
    sortKey: 'billingNote' satisfies ClientDeliverableSort,
  },
  /*
   * The source line's internal note (owner, 2026-10-18). No `sortKey`: the API
   * has no sort member for it. `?? null` maps an older API's absent key to the
   * funnel's `(Empty)` only; the cell itself renders nothing for `undefined`.
   */
  {
    key: 'lineNote',
    label: 'Notes',
    get: (row) => row.lineNote ?? null,
  },
  {
    key: 'adsPlan',
    label: 'Ads plan',
    get: (row) => row.adsPlan,
    sortKey: 'adsPlan' satisfies ClientDeliverableSort,
  },
];

/**
 * The same keys in the same order the header row renders them, derived rather
 * than retyped so the mobile filter sheet cannot drift out of step with the
 * table when a column is added, removed or moved.
 */
const TRACKER_COLUMN_KEYS: readonly string[] = TRACKER_COLUMNS.map((column) => column.key);

/**
 * The server params the two date columns' ranges emit — the ONLY members of
 * `table.serverParams` that reach the list request. Must name exactly the
 * `minParam`/`maxParam` pairs declared on `startDate` and `endDate` above.
 */
const DATE_BOUND_PARAMS = [
  'startDateAfter',
  'startDateBefore',
  'endDateAfter',
  'endDateBefore',
] as const;

/**
 * The column manager's labels, read off `TRACKER_COLUMNS` rather than retyped.
 *
 * ⚠️ **The manager must say what the HEADER says, verbatim** — including the one
 * label that deliberately diverges from the workbook ("Company Name", see the
 * note above). A reader hides a column by finding the words they can see on the
 * table; a second, tidier list of names is a list they cannot match against it,
 * and it would drift the first time a header is renamed.
 */
const COLUMN_LABELS: Record<string, string> = Object.fromEntries(
  TRACKER_COLUMNS.map((column) => [column.key, column.label]),
);

function columnLabelOf(key: string): string {
  return COLUMN_LABELS[key] ?? key;
}

/* -------------------------------------------------------------------------
 * Table / Sheet
 * --------------------------------------------------------------------- */

/**
 * 🔴 **THE SHEET IS THE DEFAULT — owner's instruction, 2026-09-02.**
 *
 * *SUPERSEDED: "⚠️ **THE TABLE IS THE DEFAULT AND MUST STAY THE DEFAULT.** It is
 * the view that filters, that carries the billing-status ordering this page
 * exists to answer, and — the half that is easy to forget — **the only one that
 * works for a reader with no Google access at all.** The mirror is a convenience
 * over the same data; opening onto it would put a document somebody may not be
 * able to see in front of them instead of the page they navigated to."*
 *
 * ⚠️ **The cost that paragraph named is REAL and is now ACCEPTED, not
 * disproved.** A reader with no Google access lands on a frame that paints
 * nothing, and this app cannot detect that — a cross-origin iframe reports no
 * error (see `TrackerSheetPanel`). So the recovery has to be visible without
 * anything having to notice the failure, and it is: the **Table** button in the
 * header renders whenever the sheet does, on every view and at every width, and
 * an explicit choice of it is written into the URL and stays. That toggle is
 * load-bearing now in a way it was not when the table opened by default — do not
 * hide it behind a menu, a breakpoint, or the current view.
 *
 * What is unchanged: the table is still the view that filters, sorts and edits,
 * and it is still where a box with no mirror configured lands.
 */
type TrackerView = 'table' | 'sheet';

const VIEW_OPTIONS: readonly { value: TrackerView; label: string }[] = [
  { value: 'table', label: 'Table' },
  { value: 'sheet', label: 'Sheet' },
];

/**
 * Two buttons in a group, matching the Table/Board control on `/companies`,
 * `/deals`, `/quotes` and `/invoices`.
 *
 * `role="group"` with `aria-pressed`, NOT `role="tab"` — the house reasoning is
 * spelled out on `QuotesPage`: these switch what the panel below IS, rather than
 * labelling panels that share one tabstop. There is no tab or segmented-control
 * primitive in `src/components/ui`; this pattern built out of `Button` is what
 * this app has, so it is copied verbatim rather than invented beside it.
 *
 * ⚠️ **Both options render at every width, always.** This codebase has already
 * had a working feature reported as *deleted* when it had merely become
 * unreachable behind a view toggle — a missing affordance is indistinguishable
 * from a removed one. Nothing here collapses into a menu on a narrow screen.
 */
function ViewToggle({
  view,
  onChange,
}: {
  view: TrackerView;
  onChange: (next: TrackerView) => void;
}) {
  return (
    <ViewTogglePrimitive
      label="Client deliverables view"
      value={view}
      options={[...VIEW_OPTIONS]}
      onChange={onChange}
    />
  );
}

/**
 * The sheet's place, held while the mirror's address is still being read.
 *
 * ⚠️ **This exists because the DEFAULT view is the one that has to wait for a
 * network answer.** Without it the page renders the table — the fallback — and
 * replaces it with the frame a moment later, which is a swap the reader sees and
 * did not ask for. Standing in the sheet's shape instead means the first paint
 * is already the view they are going to get.
 *
 * The header is `TrackerSheetPanel`'s header, verbatim, so nothing moves when
 * the real panel takes over; the block below it is the frame's own height.
 *
 * ⚠️ **"Verbatim" IS AN INVARIANT NOTHING CHECKS, and it has already been broken
 * once.** The two strings are typed out in two files; when the panel's dropped
 * "Read-only." on 2026-09-02 this copy still said it, so for the length of one
 * edit the placeholder promised a read-only sheet and the frame that replaced it
 * did not. Nothing failed — the header simply changed under the reader between
 * two paints. **Change both in the same edit, or hoist the pair.**
 *
 * ⚠️ It is NOT rendered when the mirror turns out to be unconfigured — that is a
 * settled answer, not a pending one, and it lands on the table. A placeholder
 * that resolved to nothing would be a shimmer promising a view that is never
 * coming.
 */
function SheetPlaceholderPanel() {
  return (
    <Panel>
      <PanelHeader
        title="Google Sheets mirror"
        description="EMERGE rewrites this sheet automatically — edit the record in EMERGE, not here."
      />
      <div aria-busy className="border-b border-border p-4">
        <Skeleton className="h-[70vh] min-h-[360px] w-full" />
      </div>
    </Panel>
  );
}

/* -------------------------------------------------------------------------
 * Page
 * --------------------------------------------------------------------- */

/**
 * 🔴 **TWO TABS, AND EXACTLY ONE IS MOUNTED — owner's decision 2026-09-13.**
 *
 * `?deliverables=capacity` renders the Project Capacity cross-tab (labelled
 * *"Event Capacity"* until the owner's rename of 2026-09-13 — **the URL value
 * did NOT move with the label**); anything else,
 * the key's ABSENCE included, renders the tracker. This wrapper is the whole of
 * the switch, and it is a BRANCH rather than two children with one hidden, for
 * the reasons `OperationsDashboardPage` already records about its own pills:
 *
 * - **The request budget.** The tracker below fires its list read plus several
 *   option reads on arrival, against a limit shared by the whole office.
 *   Mounting it behind a hidden `div` spends all of that to render a screen
 *   nobody is looking at. ⚠️ *SUPERSEDED: "a 120-per-60s limit."* It is
 *   **12,000/60s** (corrected 2026-09-17); the waste is the point, not the
 *   headroom.
 * - ⚠️ **And the silent half: `useSavedFilters`.** The tracker seeds a WHOLE
 *   query string from its first render's snapshot. With the capacity panel also
 *   writing to that string, two writers race off one stale snapshot — last write
 *   wins, the other seeding vanishes, and **nothing errors**.
 *
 * ⚠️ **THE EXPORTED NAME IS UNCHANGED**, so `App.tsx` and
 * `OperationsDashboardPage` keep working without an edit. The tracker screen is
 * the same function it always was, renamed: the diff is this wrapper and a
 * signature line, not a re-indent of ~1,700 lines. **Do not "tidy" it into one
 * component** — this repo has mixed line endings and a bulk reflow is exactly the
 * operation most likely to convert them silently.
 */
export function ClientDeliverablesPage() {
  const [searchParams] = useSearchParams();
  return deliverablesTabOf(searchParams) === 'capacity' ? (
    /*
     * ⚠️ **`load` IS WHAT MAKES THE PANEL'S NOT-READING NOTICE UNREACHABLE**, and
     * that is structural rather than a flag: the prop's absence is the notice, so
     * there is nothing here anybody has to remember to unset. Never pass a
     * wrapper that resolves the cycle — `api.eventCapacity` takes the required
     * `cycleId` and the panel resolves it with `currentCycle()`.
     */
    <EventCapacityPanel load={api.eventCapacity} />
  ) : (
    <ClientDeliverablesTrackerScreen />
  );
}

function ClientDeliverablesTrackerScreen() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Saved filters, against the EMERGE account rather than this browser.
  const applySavedFilters = useSavedFilterApply<SavedTrackerKey>();
  useSavedFilters({
    scope: 'client-deliverables',
    keys: SAVED_FILTER_KEYS,
    store: preferencesStore,
    apply: applySavedFilters,
  });

  const [result, setResult] = useState<Paginated<ClientDeliverable> | null>(null);
  const [search, setSearch] = useRememberedState('search', '');
  const [page, setPage] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showMoreFilters, setShowMoreFilters] = useRememberedState(
    'deliverables-more-filters',
    false,
  );

  /*
   * The ten filters live in the URL so a filtered view survives a refresh and
   * can be linked to (`/client-deliverables?projectId=22` from a project).
   * Every write is `replace: true` — changing a filter is not a navigation, and
   * a history entry per keystroke would make Back mean "undo one filter".
   *
   * `useSearchParams()` takes NO argument: its first parameter is a default
   * init, not options, so `useSearchParams({ replace: true })` would write a
   * literal `replace=true` into the query string and then into these filters.
   */
  const searchParamsKey = searchParams.toString();
  const filters = useMemo(
    () => readFilters(new URLSearchParams(searchParamsKey)),
    [searchParamsKey],
  );
  const activeFilterCount = FILTER_KEYS.filter((key) => filters[key] !== '').length;

  // Keyed on `result` so the identity is stable between renders: `?? []` is a
  // fresh array every time, which would make every memo below it do nothing.
  const rows = useMemo(() => result?.data ?? [], [result]);
  const total = result?.meta.total ?? 0;
  // `meta.totalPages` is 0 (not 1) for an empty set, which would disable both
  // pager buttons and render "Page 1 of 0".
  const totalPages = Math.max(result?.meta.totalPages ?? 1, 1);
  const firstRow = total === 0 ? 0 : (page - 1) * PER_PAGE + 1;
  const lastRow = total === 0 ? 0 : firstRow + rows.length - 1;
  const initialLoad = loading && !result;

  /*
   * Column filters. Sorting is the half that reaches the server — `table.sort`
   * feeds the request below — while the value checkboxes narrow the loaded page
   * only, which is what each popover states.
   *
   * ⚠️ *SUPERSEDED 2026-10-04: "`table.serverParams` is deliberately not spread
   * into the request: no column here is `scope: 'server'`, so it is always
   * empty".* The Start date and End date columns carry a `range`, and a range's
   * bounds are server-side whatever `scope` says — so `serverParams` now holds
   * them, and a range not merged into the fetch is a control that silently does
   * nothing. ⛔ **Only the four date bounds are taken, by name, never a blanket
   * spread**: no column is `scope: 'server'`, and the reason that rule was written
   * still stands — a spread would give the filter bank and the headers two
   * writers for one query param the day somebody adds one.
   */
  const table = useTableFilters(rows, TRACKER_COLUMNS);
  const dateBounds = useMemo(() => {
    const bounds: Pick<
      ClientDeliverableListParams,
      'startDateAfter' | 'startDateBefore' | 'endDateAfter' | 'endDateBefore'
    > = {};
    for (const key of DATE_BOUND_PARAMS) {
      const value = table.serverParams[key];
      if (value) bounds[key] = value;
    }
    return bounds;
  }, [table.serverParams]);
  const dateBoundsKey = JSON.stringify(dateBounds);
  const sort = asSortKey(table.sort?.key);
  /*
   * ASC by default, not DESC. The server's own default is ascending, and under
   * the `project` grouping ascending is the order the workbook is read in — a
   * descending default would flip the sections on first paint against a request
   * that had asked for neither.
   */
  const order: ListOrder = table.sort?.direction ?? 'asc';
  const visibleRows = table.visibleRows;
  const hiddenByColumns = rows.length - visibleRows.length;
  // Unconditional: the server bands every sort, so there is no ungrouped view.
  const groups = useMemo(() => groupByProject(visibleRows), [visibleRows]);

  /*
   * The header row's rendered height — where a project band heading sticks,
   * just under the frozen column headers (owner's instruction 2026-10-12, as on
   * the sales tracker). Measured rather than written down, because a heading
   * can wrap and the funnels change the row's height. Re-run when the table
   * mounts: the skeleton, the empty state and the rows are separate trees.
   */
  const [headHeight, setHeadHeight] = useState(0);
  const tableMounted = !initialLoad && visibleRows.length > 0;
  useLayoutEffect(() => {
    const row = headRowRef.current;
    if (!row) return;
    const measure = () => setHeadHeight(row.getBoundingClientRect().height);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(row);
    return () => observer.disconnect();
  }, [tableMounted]);

  /*
   * ── Column layout ─────────────────────────────────────────────────────────
   *
   * Order, hidden set and the two frozen edges, stored against the ACCOUNT — the
   * same `client-deliverables` scope the saved filters above use, in the
   * `preferences.columns` branch rather than `preferences.tables`. See
   * `use-column-layout.ts` for why it is neither `localStorage` nor the URL: this
   * is the reader's own chrome and has no joint meaning with a filter, so putting
   * it in a link would impose the sender's hidden columns on the recipient.
   *
   * ⚠️ **`columns.hiddenCount` is columns the READER put away; `hiddenByColumns`
   * a few lines up is ROWS a column FILTER hid.** Two counts, two causes, two
   * fixes — the names are deliberately not alike, and neither may be worded as
   * the other in the footer or the empty state.
   *
   * ⚠️ **A frozen column's offset is MEASURED from the rendered header row**, not
   * computed from `COLUMN_CHROME.width`. This table is auto layout, so a `w-*` is
   * a request the content can outvote; a width taken from the class would be
   * right for most columns and quietly wrong for the ones the content widened,
   * and the symptom is two frozen columns overlapping by the difference.
   *
   * ⚠️ **`PINNED_COLUMN_KEYS` is EMPTY and is passed anyway** — see the constant.
   * Nothing on this table is `pinned`, so there is no right-edge slot to reserve
   * and no freeze control to suppress; passing it states that rather than leaving
   * the next reader to infer it from an argument that is not there.
   */
  const columns = useColumnLayout(
    'client-deliverables',
    COLUMN_KEYS,
    LOCKED_COLUMN_KEYS,
    PINNED_COLUMN_KEYS,
    LAYOUT_OPTIONS,
  );
  const headRowRef = useRef<HTMLTableRowElement | null>(null);
  const frozenOffsets = useFrozenOffsets(
    headRowRef,
    columns.visibleKeys,
    columns.frozenSideOf,
    PINNED_COLUMN_KEYS,
  );
  const tableWidth = tableMinWidth(columns.visibleKeys);

  /*
   * A new sort re-orders the whole table, so page 4 of the old order is
   * meaningless in the new one — the same reset the search box and the filters
   * do.
   *
   * Adjusted during render rather than in an effect: `sort` lives inside
   * `useTableFilters`, so there is no onSort event to hang it off, and an effect
   * would commit one render (and fire one request) against the stale page first.
   */
  // The date bounds narrow the server's result, so they reset the page too —
  // page 3 of a shorter result is usually empty and reads as "nothing matches".
  const sortToken = `${sort}:${order}:${dateBoundsKey}`;
  const [lastSortToken, setLastSortToken] = useState(sortToken);
  if (lastSortToken !== sortToken) {
    setLastSortToken(sortToken);
    setPage(1);
  }

  const load = useCallback(
    (signal?: AbortSignal) => {
      setLoading(true);
      return api
        .listClientDeliverables(
          {
            page,
            perPage: PER_PAGE,
            sort,
            order,
            ...(search ? { search } : {}),
            ...filterParams(filters),
            ...dateBounds,
          },
          signal,
        )
        .then((data) => {
          setResult(data);
          setError(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          setError(loadErrorMessage(caught));
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [page, search, sort, order, filters, dateBounds],
  );

  // Debounced at 250ms so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(() => void load(controller.signal), 250);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [load]);

  /*
   * Takes the record a write RETURNED and puts it in place of the one on
   * screen.
   *
   * ⚠️ **The response is the row, and the local copy is never patched from the
   * value that was sent.** Both write paths read back through the same union
   * the list uses, so what comes back carries the id an upsert has just
   * allocated — which is what makes the NEXT edit on that row a `PATCH` rather
   * than a second upsert — plus the recomputed `pending` and every derived
   * field the write never saw. Merging the sent key into the old object would
   * leave `id: null` for ever and show a stale `pending`.
   *
   * Matched on `key`, never on `id`: half the rows share `id: null`, and `key`
   * is line-derived on an attached row so it survives the transition intact.
   *
   * A row that is no longer in state is left alone rather than appended — the
   * page has re-read since the save started, and appending would put the row
   * outside its project group and outside the server's ordering.
   */
  const replaceRow = useCallback((next: ClientDeliverable) => {
    setResult((current) => {
      if (!current) return current;
      const index = current.data.findIndex((row) => row.key === next.key);
      if (index === -1) return current;
      const data = current.data.slice();
      data[index] = next;
      return { ...current, data };
    });
  }, []);

  const companies = useRemoteOptions('companies');
  const products = useRemoteOptions('products');
  const intakes = useRemoteOptions('intakes');
  const regions = useReferenceOptions('regions');
  const projects = useAllActiveProjects();
  const cycles = useCycleOptions();
  /*
   * Current / Previous on the Project filter — owner's request 2026-10-04, the
   * same pills and the same rule the line-item project pickers use
   * (`project-cycle-scope.ts`: current means current AND upcoming). It narrows
   * what the dropdown OFFERS and is not itself a filter, so it is neither saved
   * nor put in the URL — but it is remembered on the account (filter memory,
   * owner 2026-10-16; *SUPERSEDED: "`current` on every mount"*). A project
   * already chosen stays visible under both pills through `withSelected`.
   */
  const cycleScopes = useCycleScopeIds();
  const [projectScope, setProjectScope] = useRememberedState<ProjectCycleScope>(
    'deliverables-project-cycle-scope',
    DEFAULT_PROJECT_CYCLE_SCOPE,
    (raw) => (raw === 'current' || raw === 'previous' ? raw : undefined),
  );

  /*
   * id → name harvested from the rows currently on screen. Cheap, and it is the
   * only label source for a value the option lists cannot offer: a master row
   * deactivated since the line was written still filters correctly, but the
   * option hooks list active rows only.
   */
  const rowLabels = useMemo(() => {
    const maps: Record<IdFilterKey, Map<string, string>> = {
      companyId: new Map(),
      productId: new Map(),
      projectId: new Map(),
      intakeId: new Map(),
      cycleId: new Map(),
      regionId: new Map(),
    };
    for (const row of result?.data ?? []) {
      if (row.company) maps.companyId.set(String(row.company.id), row.company.name);
      // Watch the key names: productName and intakeName, not `name`.
      if (row.product) maps.productId.set(String(row.product.id), row.product.productName);
      if (row.project) maps.projectId.set(String(row.project.id), row.project.name);
      if (row.intake) maps.intakeId.set(String(row.intake.id), row.intake.intakeName);
      if (row.cycle) maps.cycleId.set(String(row.cycle.id), row.cycle.name);
      for (const region of row.regions) maps.regionId.set(String(region.id), region.name);
    }
    return maps;
  }, [result]);

  /*
   * ⚠️ **`useUserOptions()` was called here for the POC filter and every row's
   * POC picker, and the call went with them on 2026-08-31** — nothing on this
   * page reads the staff directory any more, so the page makes one fewer
   * request per load against a per-IP bucket shared across the office.
   * ⚠️ *SUPERSEDED: "a 120/60s per-IP bucket."* It is **12,000/60s** (corrected
   * 2026-09-17).
   *
   * Restoring an owner control means restoring the hook HERE, once for the
   * page: a `useUserOptions()` per row would be up to 50 requests.
   */
  /*
   * The POC picker's options: staff, narrowed to the `delivery_lead` group.
   *
   * ⚠️ `useUserOptions` is called ONCE for the whole page and handed down
   * through `editing`. It was removed from this page on 2026-08-31 with the POC
   * column and comes back with it — see that hook's own note about a per-row
   * call being 50 identical requests against a shared rate bucket.
   *
   * ⚠️ **`byGroup` degrades to the UN-NARROWED directory when the group master
   * cannot be read**, which is the right direction: over-offering costs a
   * refusal the server will not actually make (it does not enforce this group),
   * where under-offering silently hides the person somebody is looking for.
   */
  const staff = useUserOptions(true, true);
  const staffOptions = useMemo<ComboboxOption[]>(
    () =>
      staff.byGroup.delivery_lead.users.map((user) => ({
        value: String(user.id),
        label: user.name.trim() || user.email,
      })),
    [staff.byGroup.delivery_lead.users],
  );

  /*
   * ADDING A ROW — the one population somebody creates.
   *
   * ⚠️ **This does not add "a row to the tracker"; it adds a row with NO QUOTE
   * AND NO INVOICE.** Every other row is derived from a line, so creating one
   * here for work that is already sold puts the same work on the tracker twice
   * — a 201, nothing failing, two figures where the business has one. The
   * control and the modal are worded around that, because the wording is the
   * only thing standing between a reader and the mistake.
   *
   * ⚠️ Gated on `ability.canCreate('deliverables')`, never on the role and never
   * on `/permissions/me`. When it is withheld the REASON is rendered rather than
   * nothing: an absent button and a removed feature look identical, which is the
   * report this app has already had once about the companies "+" menu.
   */
  const { ability } = useAuth();

  /*
   * DELETING A STANDALONE ROW. Admin only, because the API is (`ADMIN_ROLES`
   * on `DELETE /client-deliverables/:id`) — offering it below that tier would
   * only collect a 403. One dialog for the page, never one per row.
   *
   * 🔴 *SUPERSEDED 2026-10-01: "Admin only".* Deletes follow the set:
   * `deliverables.delete`.
   */
  /*
   * 🔴 2026-10-14, owner's decision: delete, remove and restore are all gated
   * on `ability.canUpdate('deliverables')` — anyone who can edit deliverables —
   * and reach EVERY row. *SUPERSEDED: `ability.canDelete('deliverables')`, on
   * standalone rows only.* `rowRemovalOf` decides delete versus remove.
   */
  const canRemoveDeliverables = ability.canUpdate('deliverables');
  const [deleting, setDeleting] = useState<ClientDeliverable | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const deletingRemoval = deleting ? rowRemovalOf(deleting) : null;
  const requestDelete = useCallback((row: ClientDeliverable) => {
    setDeleteError(null);
    setDeleting(row);
  }, []);
  const confirmDelete = () => {
    if (deleting === null || deletingRemoval === null || deleteBusy) return;
    // Standalone keeps the id-based DELETE it always used; every other row is
    // removed by its opaque `key`, which also covers rows with `id: null`.
    const request =
      deletingRemoval === 'delete' && deleting.id !== null
        ? api.deleteClientDeliverable(deleting.id)
        : api.removeClientDeliverable(deleting.key);
    setDeleteBusy(true);
    setDeleteError(null);
    request
      .then(() => {
        setDeleting(null);
        // Re-read rather than splice: the server pages and bands these rows.
        void load();
      })
      .catch((error: unknown) => {
        setDeleteError(
          trackerActionError(
            error,
            deletingRemoval === 'delete'
              ? 'The deliverable was not deleted.'
              : 'The deliverable was not removed from the tracker.',
          ),
        );
      })
      .finally(() => setDeleteBusy(false));
  };

  /*
   * RESTORING A REMOVED ROW. No confirmation — it is the undo of a reversible
   * step. One at a time; a failure is a page banner, never a field error.
   */
  const [restoringKey, setRestoringKey] = useState<string | null>(null);
  const [restoreError, setRestoreError] = useState<string | null>(null);
  const requestRestore = useCallback(
    (row: ClientDeliverable) => {
      if (restoringKey !== null) return;
      setRestoringKey(row.key);
      setRestoreError(null);
      api
        .restoreClientDeliverable(row.key)
        .then(() => void load())
        .catch((error: unknown) => {
          setRestoreError(trackerActionError(error, 'The deliverable was not restored.'));
        })
        .finally(() => setRestoringKey(null));
    },
    [restoringKey, load],
  );
  /*
   * The LAE specialists, read ONCE for the page and handed down like
   * `staffOptions` — a per-row hook would be one request per row.
   * `specialists: null` means not read or failed, never an empty list.
   */
  const laeSpecialists = useLaeSpecialists(true);

  /*
   * Every loaded row grouped by its split target — the LOADED page, not the
   * column-filtered view, so a funnel hiding one piece cannot make the split
   * dialog or the "pieces total" check see a partial set as the whole.
   */
  const siblingsByTarget = useMemo(() => {
    const map = new Map<string, ClientDeliverable[]>();
    for (const row of rows) {
      const target = splitTargetKey(row);
      if (target === null) continue;
      const list = map.get(target);
      if (list) list.push(row);
      else map.set(target, [row]);
    }
    return map;
  }, [rows]);
  const siblingsOf = useCallback(
    (row: ClientDeliverable): ClientDeliverable[] => {
      const target = splitTargetKey(row);
      return (target === null ? undefined : siblingsByTarget.get(target)) ?? [row];
    },
    [siblingsByTarget],
  );

  /*
   * SPLITTING A ROW. One dialog for the page. Gated on
   * `ability.canUpdate('deliverables')` — the pieces API is the update grant.
   */
  const canSplit = ability.canUpdate('deliverables');
  const [splitting, setSplitting] = useState<ClientDeliverable | null>(null);
  const requestSplit = useCallback((row: ClientDeliverable) => setSplitting(row), []);
  const refetch = useCallback(() => void load(), [load]);

  /*
   * LINKING A STANDALONE ROW to a deal, quote or invoice line (owner
   * 2026-10-16). One dialog for the page, the update grant, like Split.
   */
  const canLink = ability.canUpdate('deliverables');
  const [linking, setLinking] = useState<ClientDeliverable | null>(null);
  const requestLink = useCallback((row: ClientDeliverable) => setLinking(row), []);

  const editing = useMemo<RowEditing>(
    () => ({
      onSaved: replaceRow,
      // Pieces share fields, so a write on one changes its siblings: re-read.
      onSplitRowWritten: refetch,
      // The note is the source line's: rows sharing that line change with it.
      onLineNoteWritten: refetch,
      onDealPlaceholderMade: refetch,
      staffOptions,
      specialists: laeSpecialists.specialists,
      specialistsError: laeSpecialists.error,
      siblingsOf,
      // 2026-10-14: the update grant. *SUPERSEDED: `canDelete`, and before that `isAdmin`.*
      ...(canRemoveDeliverables ? { onDelete: requestDelete, onRestore: requestRestore } : {}),
      restoringKey,
      ...(canSplit ? { onSplit: requestSplit } : {}),
      ...(canLink ? { onLink: requestLink } : {}),
    }),
    [
      replaceRow,
      refetch,
      staffOptions,
      laeSpecialists.specialists,
      laeSpecialists.error,
      siblingsOf,
      canRemoveDeliverables,
      requestDelete,
      requestRestore,
      restoringKey,
      canSplit,
      requestSplit,
      canLink,
      requestLink,
    ],
  );

  /*
   * The read gate on the sidebar SURFACE, not on the deliverable records.
   *
   * ⛔ **`trackers-client-deliverables`, never `deliverables`.** The second gates
   * the RECORDS and is the one `canCreate` below asks about; this gates one nav
   * row and this page, client-side only. Two slugs that look interchangeable and
   * answer different questions — see `NAV_SURFACE_MODULE` in
   * `@/lib/permissions`.
   *
   * ⚠️ DEGRADES OPEN: only an explicit `none` scope denies.
   *
   * ⚠️ **Hiding a page is not refusing a request.** `GET /client-deliverables`
   * answers the same to everybody, so nothing here may be worded as though the
   * rows were protected. This is a view control.
   *
   * ⚠️ Computed here rather than as an early return, because the return has to
   * sit below every hook. The create gate below is untouched.
   */
  const canReadSurface = ability.canRead(NAV_SURFACE_MODULE.trackerClientDeliverables);

  const canCreate = ability.canCreate('deliverables');
  const createDenial = denialReason(ability, 'create', 'deliverables');
  const [creating, setCreating] = useState(false);
  // Masters render in the order the API sent them (sortOrder ASC, name ASC).
  // That ordering is curated — re-sorting alphabetically would discard it.
  const regionOptions = useMemo<ComboboxOption[]>(
    () => regions.records.map((record) => ({ value: String(record.id), label: record.name })),
    [regions.records],
  );
  const projectPicker = useMemo(
    () =>
      projectOptionsByScope(
        projects.projects,
        cycleScopes,
        (project): ComboboxOption => ({ value: String(project.id), label: project.name }),
        null,
      ),
    [projects.projects, cycleScopes],
  );
  const projectOptions = projectPicker.options[projectScope];
  const projectScopeNote = projectScopeFootnote(projectScope, projectPicker.counts);
  const cycleOptions = useMemo<ComboboxOption[]>(
    () => cycles.cycles.map((cycle) => ({ value: String(cycle.id), label: cycle.cycleName })),
    [cycles.cycles],
  );
  const sourceOptions = useMemo<ComboboxOption[]>(
    () => SOURCE_VALUES.map((value) => ({ value, label: SOURCE_LABELS[value] })),
    [],
  );
  const billingOptions = useMemo<ComboboxOption[]>(
    () => BILLING_VALUES.map((value) => ({ value, label: BILLING_LABELS[value] })),
    [],
  );
  const outstandingOptions = useMemo<ComboboxOption[]>(
    () =>
      (['true', 'false'] as const).map((value) => ({
        value,
        label: OUTSTANDING_LABELS[value],
      })),
    [],
  );
  // One option: the empty row ("On the tracker") is the live view.
  const removedOptions = useMemo<ComboboxOption[]>(
    () => [{ value: 'true', label: 'Removed' }],
    [],
  );
  // One option again: the empty row is every row.
  const duplicateOptions = useMemo<ComboboxOption[]>(
    () => [{ value: 'true', label: 'Possible duplicates' }],
    [],
  );

  const setFilter = useCallback(
    (key: FilterKey, value: string) => {
      const next = new URLSearchParams(searchParamsKey);
      if (value) next.set(key, value);
      else next.delete(key);
      setSearchParams(next, { replace: true });
      setPage(1);
    },
    [searchParamsKey, setSearchParams],
  );

  /*
   * The mirror's two addresses, from the SHARED reader — the same one the header
   * button and the sidebar row use, so this page still makes one request for the
   * pair rather than a second one for the frame.
   */
  const trackerSheet = useTrackerSheetLink();
  /*
   * ⚠️ THE TAB IS GATED ON THE PREVIEW URL, NOT ON `link`, AND NOT ON
   * `configured`. Three states collapse to "there is nothing to embed": no
   * mirror configured, the answer not known yet, and — the one this exists for —
   * **a server that predates `spreadsheetPreviewUrl` and does not send it.**
   * This app deploys separately from the API and can be newer than it, so that
   * last case is ordinary rather than broken, and it must degrade to the table
   * rather than to a tab that opens a frame pointed at nothing.
   */
  const sheetUrl = trackerSheet.loading ? null : trackerSheet.previewLink;
  /**
   * 🔴 **THE FRAME IS THE EDIT URL NOW — owner's instruction, 2026-09-02:** the
   * embed is to allow editing and commenting for whoever Google says may.
   *
   * ⚠️ **`?? sheetUrl` IS NOT TIDINESS.** `spreadsheetUrl` is non-optional on the
   * response and `spreadsheetPreviewUrl` is the one an older API omits, so this
   * pair reads backwards from `sheetUrl` above on purpose: the edit address is
   * the one that is always there, and the preview is the fallback for a server
   * whose shape we cannot predict. Never derive one from the other by string
   * surgery — the shape of a Google address is the API's fact, and a bad swap
   * paints an empty frame that looks exactly like a permissions problem.
   *
   * ⚠️ **THE TAB IS STILL GATED ON `sheetUrl`, deliberately.** An older server
   * sending no preview URL sends no gid-bearing frame address either; falling
   * back to the table there is the behaviour the comment above describes, and it
   * is unchanged.
   */
  const sheetEmbedUrl = trackerSheet.loading ? null : (trackerSheet.link ?? sheetUrl);
  const canShowSheet = sheetUrl !== null;

  /*
   * ⚠️ **READ ONLY WHEN THERE IS A MIRROR TO ORDER.** Gated on `canShowSheet` so
   * a deployment with no spreadsheet configured spends no request on how to sort
   * one — and so the sidebar, which mounts everywhere, never touches this at all.
   * It is deliberately NOT the cached reader beside it; see the hook's header for
   * why a mutable setting must not ride on a read documented as unchanging.
   */
  const sheetSort = useTrackerSheetSort(canShowSheet);

  /*
   * `?view=table` is the opt-in and the SHEET is what no param means — the
   * inverse of the `?view=board` convention on the four record pages, because
   * the owner made the sheet this page's default on 2026-09-02.
   *
   * ⚠️ **A URL THAT NAMES A VIEW STILL WINS, IN BOTH DIRECTIONS.** `?view=table`
   * renders the table and `?view=sheet` renders the sheet; only an ABSENT (or
   * unrecognised) key falls to the default. Every link anybody has already
   * shared therefore still resolves to the view it names.
   *
   * ⚠️ **NOTHING IS WRITTEN INTO THE URL ON ARRIVAL, and that is what keeps the
   * default clearable.** `/projects` has to seed its cycle into the query string
   * because "no cycle named" is also what a cleared filter looks like — the two
   * are indistinguishable, so a hidden default there re-applies itself for ever
   * and the control reads as broken. This has no such collision: **`table` is
   * expressible as a value**, so choosing it writes `?view=table` and that
   * sticks, through a reload, the Back button and a shared link. A resolved view
   * written on arrival would buy nothing here and would put a second writer on
   * the query string.
   *
   * ⚠️ **`canShowSheet` is applied on the READ, so a link to `?view=sheet` on a
   * box with no mirror lands on the table rather than on a blank frame beside a
   * toggle that is not rendered** — and so does the new default, which is the
   * whole reason an unconfigured deployment is unaffected by this change. The
   * param is left in the URL untouched: rewriting somebody's link on arrival is
   * that second writer again, and the answer changes as soon as the mirror is
   * configured, at which point the same link resolves the way it was meant to.
   */
  // The Removed view lives in the table only: the Sheet never carries removed
  // rows, so a `?removed=true` link landing on the sheet would show the opposite.
  // The same for Possible duplicates: the Sheet is not narrowed by it.
  const wantsTable =
    searchParams.get('view') === 'table' ||
    filters.removed === 'true' ||
    filters.possibleDuplicate === 'true';
  const view: TrackerView = !wantsTable && canShowSheet ? 'sheet' : 'table';

  /*
   * ⚠️ **The mirror's address is not known on the first paint of a fresh tab**
   * (`useTrackerSheetLink` reads it once over the network), so `canShowSheet` is
   * false for that window and the resolution above would answer `table` —
   * rendering the whole table and swapping it for the frame a moment later.
   *
   * That flash did not matter while the table WAS the default and the answer
   * only ever added a tab. It matters now: it is the default view arriving as a
   * visible replacement of another one, which reads as the page changing its
   * mind. So the undecided case is its own state rather than being folded into
   * the fallback — and it is only entered when the reader has NOT asked for the
   * table, because that request needs no network answer and must never wait on
   * one.
   */
  const resolvingSheet = !wantsTable && trackerSheet.loading;

  const setView = useCallback(
    (next: TrackerView) => {
      const params = new URLSearchParams(searchParamsKey);
      // The sheet is the default, spelled by the key's ABSENCE rather than by
      // `?view=sheet` — one canonical URL per view. The table is the value,
      // which is also what makes an explicit choice of it survive a reload.
      if (next === 'table') params.set('view', 'table');
      else params.delete('view');
      setSearchParams(params, { replace: true });
    },
    [searchParamsKey, setSearchParams],
  );

  function clearFilters() {
    const next = new URLSearchParams(searchParamsKey);
    for (const key of FILTER_KEYS) next.delete(key);
    setSearchParams(next, { replace: true });
    setPage(1);
  }

  function clearEverything() {
    setSearch('');
    table.clearAll();
    clearFilters();
  }

  const filtered = activeFilterCount > 0;
  const countLabel = initialLoad
    ? 'Loading…'
    : `${total} ${total === 1 ? 'row' : 'rows'}${search ? ` matching “${search}”` : ''}${
        filtered ? ` (${activeFilterCount} ${activeFilterCount === 1 ? 'filter' : 'filters'})` : ''
      }`;

  const emptyState = (
    <EmptyStateSlot
      search={search}
      filtered={filtered}
      // The server returned rows and the column filters hid every one of them —
      // a different problem from "nothing matched", with a different fix.
      columnsHidAll={rows.length > 0 && visibleRows.length === 0}
      onClearColumns={table.clearAll}
      onClear={clearEverything}
      hasError={Boolean(error)}
    />
  );

  /*
   * ⚠️ Below every hook, so the early return cannot change the hook order. The
   * refusal is rendered here rather than by the route, which carries no guard.
   */
  if (!canReadSurface) {
    return (
      <>
        <PageHeader eyebrow="Trackers" title="Client Deliverables" />
        <Panel className="min-w-0">
          <EmptyState
            title="This page isn’t available to your account"
            description="Your permission set does not include this. An administrator can change that."
          />
        </Panel>
      </>
    );
  }

  return (
    <>
      {/*
       * The Sheets link goes in the header's `actions` slot rather than beside
       * the filters, because it acts on the TRACKER and not on the current
       * query: it opens the whole mirror, which no filter on this page narrows.
       * Sitting it among controls that do narrow the table would make it read
       * as "export what I am looking at", which is the one thing it is not.
       *
       * It renders nothing when there is no mirror configured, so on a box
       * without one the header is exactly what it was.
       */}
      <PageHeader
        eyebrow="Trackers"
        title="Client Deliverables"
        actions={
          <>
            {/*
             * ⚠️ **THE TAB TOGGLE IS UNCONDITIONAL, unlike the view toggle
             * beneath it.** Project Capacity reads the same permission module as
             * this screen and is always there to reach, so there is no condition
             * to gate it on — and a tab that appeared only sometimes would read
             * as a feature that comes and goes. It is FIRST because it switches
             * what the page IS, where everything after it acts on what is
             * already on screen.
             */}
            <DeliverablesTabToggle />
            {/*
             * The toggle renders only when there is a second view to reach. On
             * a box with no mirror the header is exactly what it was — a lone
             * "Table" chip with nothing to switch to would be an affordance
             * whose only outcome is nothing happening.
             *
             * ⚠️ **Since 2026-09-02 this is also the way OUT of the default.**
             * The sheet opens first now, and a frame that paints nothing for a
             * reader with no Google access is undetectable from this side — so
             * the "Table" button is the recovery, and it has to be present on
             * the sheet view, at every width, without anything having noticed
             * the failure. It is rendered exactly when the sheet can be, which
             * is the same condition; do not narrow it further.
             */}
            {canShowSheet && <ViewToggle view={view} onChange={setView} />}
            {/*
             * ⚠️ Rendered on BOTH views. The sheet is a mirror of the same
             * query, so a row created here appears in it on the next sync —
             * hiding the control behind the Table tab would make the default
             * view the one place the tracker cannot be added to.
             */}
            {canCreate ? (
              <Button variant="primary" onClick={() => setCreating(true)}>
                Add deliverable with no document
              </Button>
            ) : (
              createDenial && (
                <p className="self-center text-xs text-fg-muted">{createDenial}</p>
              )
            )}
            {/*
             * ⚠️ KEPT ON BOTH VIEWS, DELIBERATELY. The frame is read-only, so
             * the way to actually work in the sheet is this button — removing
             * it on the Sheet tab would make the embed a dead end, and leaving
             * it on the Table tab keeps the document one click away without
             * anybody having to discover the tab first.
             */}
            <OpenTrackerSheetButton />
          </>
        }
      />

      {/*
       * ⚠️ **The list is RE-READ, never patched with the created row.** The
       * server bands rows by project and pages them, so where a new row belongs
       * is its answer and not ours: inserting it locally would put it outside
       * its project group and outside the server's ordering, and on any page but
       * its own it would appear somewhere it is not. `load()` asks the question
       * the page already asks.
       */}
      <DeliverableFormModal
        open={creating}
        onClose={() => setCreating(false)}
        staffOptions={staffOptions}
        specialists={laeSpecialists.specialists}
        onCreated={() => {
          void load();
        }}
      />

      <SplitDeliverableModal
        open={splitting !== null}
        row={splitting}
        siblings={splitting ? siblingsOf(splitting) : []}
        onClose={() => setSplitting(null)}
        onSaved={() => {
          setSplitting(null);
          // Re-read rather than splice: a split changes how many rows the target has.
          void load();
        }}
      />

      <LinkDeliverableModal
        open={linking !== null}
        row={linking}
        onClose={() => setLinking(null)}
        onLinked={() => {
          setLinking(null);
          // Re-read: a merge removes this row and the document row's key moves.
          void load();
        }}
      />

      <ConfirmDialog
        open={deleting !== null}
        onClose={() => {
          if (!deleteBusy) setDeleting(null);
        }}
        onConfirm={confirmDelete}
        loading={deleteBusy}
        title={
          deletingRemoval === 'remove'
            ? 'Remove this deliverable from the tracker?'
            : 'Delete this deliverable?'
        }
        confirmLabel={deletingRemoval === 'remove' ? 'Remove from tracker' : 'Delete deliverable'}
        {...(deletingRemoval === 'remove' ? { tone: 'primary' as const } : {})}
        description={
          deleting &&
          (deletingRemoval === 'remove' ? (
            <>
              <strong>{deleting.company?.name ?? 'This client'}</strong>
              {deleting.product ? ` — ${deleting.product.productName}` : ''}. It leaves the tracker
              and its Sheet. Nothing on the quote or invoice changes, and it can be restored from
              the Removed view.
            </>
          ) : (
            <>
              <strong>{deleting.company?.name ?? 'This client'}</strong>
              {deleting.product ? ` — ${deleting.product.productName}` : ''}. This deliverable has no
              quote or invoice behind it, so deleting it removes it from the tracker for good, with
              its delivered count, dates and notes
              {deleting.piece !== null ? ', and every piece of the split goes with it' : ''}. This
              cannot be undone.
            </>
          ))
        }
        error={deleteError}
      />

      {/*
       * ⚠️ **ONE HOST PER DOCUMENT KIND, MOUNTED ONCE FOR THE WHOLE PAGE.**
       * Every quote and invoice number in the `Invoice/Quote Number` column
       * reaches these through context — from the table above `lg` and from the
       * stacked card list below it, which are two renderers of the same rows and
       * must not disagree about what a number does. Mounting a host per row, or
       * once per layout, would give one page several open documents and several
       * sets of notices.
       *
       * ⚠️ **`load` on BOTH, and it is not housekeeping.** This tracker is a
       * QUERY OVER QUOTE AND INVOICE LINE ITEMS: a saved document can add rows,
       * remove them, re-band them under a different project, move a billing
       * status, or — through the quote/invoice de-duplication rule — take a row
       * off the list entirely. A tracker left showing the rows from before the
       * save is wrong with nothing erroring.
       *
       * ⚠️ **The quote host is the OUTER one so its notices sit above the
       * invoice host's**, which is the order the cell itself renders them in:
       * the quote line first, then the invoices raised against it.
       *
       * ⚠️ **They wrap the sheet view too, deliberately.** No link renders in
       * the mirror, so nothing reaches the context there — but a Xero report
       * raised by a save must survive a reader switching view, because it is the
       * only record anywhere that Xero did not take the change.
       */}
      <QuoteQuickEdit onSaved={() => void load()}>
      <InvoiceQuickEdit onSaved={() => void load()}>

      {/*
       * ⚠️ The table `Panel` below is deliberately LEFT AT ITS ORIGINAL
       * INDENTATION rather than re-indented into this branch. Re-indenting it
       * would rewrite ~380 untouched lines and bury a four-line change in a diff
       * nobody can review — and this repo has mixed line endings, so a bulk
       * reflow is exactly the operation most likely to convert them silently.
       * It is not a mistake; do not "tidy" it.
       *
       * `sheetUrl !== null` is re-tested here even though `view` can only be
       * `'sheet'` when it is non-null: it is what narrows the type for
       * `previewUrl`, so the guard is structural rather than defensive.
       *
       * ⚠️ The placeholder is tested FIRST, and only it can be reached while the
       * mirror's address is unknown — so the table below is what a settled "no
       * mirror" answer falls to, never what an unsettled one flashes through.
       */}
      {resolvingSheet ? (
        <SheetPlaceholderPanel />
      ) : view === 'sheet' && sheetUrl !== null && sheetEmbedUrl !== null ? (
        <TrackerSheetPanel
          embedUrl={sheetEmbedUrl}
          openUrl={trackerSheet.link}
          /*
           * ⚠️ **THE CONTROL IS OFFERED TO EVERY ROLE AND WORKS FOR ADMINS
           * ONLY.** `PUT /client-deliverables/sheet/sort` is
           * `requireRole(...ADMIN_ROLES)`, matching the sync endpoint it depends
           * on — a non-admin who could set it would change everybody else's
           * document and then be unable to apply it.
           *
           * ⚠️ **DISABLED WITH A REASON RATHER THAN HIDDEN**, which is the
           * OPPOSITE of the rule the panel used to carry, and deliberately: that
           * rule was about a refusal identical for everybody, and this one is
           * about THIS READER. Hiding it would also hide the ordering itself,
           * and a tab carries no record of the arrangement that produced it.
           *
           * ⚠️ **`available` GATES IT, NOT `stored !== null`.** A server
           * predating the route 404s, which is ordinary rather than broken —
           * and while the read is in flight both are false, so the control
           * arrives once rather than flashing in and out.
           */
          {...(sheetSort.available
            ? {
                sort: {
                  value: trackerSheetSortValueOf(sheetSort.stored),
                  disabledReason: sheetSortDenial(ability),
                  onChange: sheetSort.save,
                },
              }
            : {})}
        />
      ) : (
      <Panel>
        <PanelHeader title="All client deliverables" description={countLabel} />

        <div className="border-b border-border px-4 py-3">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Field label="Search">
              {({ id, 'aria-describedby': describedBy }) => (
                <Input
                  id={id}
                  type="search"
                  placeholder="Search…"
                  leadingIcon={<SearchIcon />}
                  aria-describedby={describedBy}
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                />
              )}
            </Field>

            <MultiFilterField
              label="Client"
              value={filters.companyId}
              onChange={(value) => setFilter('companyId', value)}
              options={withSelectedMany(
                companies.options,
                filters.companyId,
                rowLabels.companyId,
                'Company',
              )}
              allLabel="All clients"
              loading={companies.loading}
              failed={companies.failed}
              searchPlaceholder="Search clients…"
              noun={{ singular: 'client', plural: 'clients' }}
            />

            <MultiFilterField
              label="Billing status"
              value={filters.billingStatus}
              onChange={(value) => setFilter('billingStatus', value)}
              options={billingOptions}
              allLabel="Any billing status"
              loading={false}
              failed={false}
              noun={{ singular: 'status', plural: 'statuses' }}
            />

            <div className="flex items-end gap-2">
              <Button
                variant="secondary"
                leadingIcon={<FilterIcon />}
                aria-expanded={showMoreFilters}
                aria-controls="tracker-more-filters"
                onClick={() => setShowMoreFilters((open) => !open)}
                className="w-full sm:w-auto"
              >
                {showMoreFilters ? 'Fewer filters' : 'More filters'}
              </Button>
              {/*
                * ⚠️ **`lg` ONLY, because below it there is no table to arrange.**
                * This page swaps its table for a stacked card list at `lg`, and
                * the cards render every field regardless of this layout — so a
                * manager on a phone would be a control whose only outcome is
                * nothing, which is the affordance this codebase refuses
                * everywhere else (Xero's `sync`, DocuSign's absent connect).
                *
                * ⚠️ **`hidden lg:contents` on a wrapper rather than on the
                * button itself.** `hidden` and the `inline-flex` `Button` bakes
                * in are the same CSS property, so which won would be decided by
                * the emitted stylesheet's order rather than by the order they
                * are written — the trap the layer ladder and `TH`'s `wrap` prop
                * both record. `contents` takes the wrapper out of the flex row
                * so the button is still a direct flex child at `lg`. Same
                * pattern as the "Clear column filters" button below.
                */}
              <span className="hidden lg:contents">
                <ColumnManager
                  layout={columns}
                  labelOf={columnLabelOf}
                  lockedKeys={LOCKED_COLUMN_KEYS}
                  lockReasonOf={columnLockReasonOf}
                  pinnedKeys={MANAGER_PINNED_KEYS}
                />
              </span>
            </div>
          </div>

          {showMoreFilters && (
            <div
              id="tracker-more-filters"
              className="mt-3 grid gap-3 border-t border-border pt-3 sm:grid-cols-2 lg:grid-cols-4"
            >
              {/*
                * The cycle pills sit ABOVE the picker (`MultiCombobox` has no
                * popover header) — the shape `/future-billables-tracker` uses.
                */}
              <div className="grid min-w-0 content-start gap-1.5">
                <SegmentedControl
                  label="Show projects from"
                  value={projectScope}
                  onChange={setProjectScope}
                  options={PROJECT_CYCLE_SCOPES.map((scope) => ({
                    value: scope,
                    label: PROJECT_CYCLE_SCOPE_LABELS[scope],
                  }))}
                />
                <MultiFilterField
                  label="Project"
                  value={filters.projectId}
                  onChange={(value) => setFilter('projectId', value)}
                  options={withSelectedMany(
                    projectOptions,
                    filters.projectId,
                    rowLabels.projectId,
                    'Project',
                  )}
                  allLabel="All projects"
                  loading={projects.loading}
                  failed={projects.failed}
                  searchPlaceholder="Search projects…"
                  noun={{ singular: 'project', plural: 'projects' }}
                  {...(projectScopeNote ? { footnote: projectScopeNote } : {})}
                />
              </div>

              <MultiFilterField
                label="Product"
                value={filters.productId}
                onChange={(value) => setFilter('productId', value)}
                options={withSelectedMany(
                  products.options,
                  filters.productId,
                  rowLabels.productId,
                  'Product',
                )}
                allLabel="All products"
                loading={products.loading}
                failed={products.failed}
                searchPlaceholder="Search products…"
                noun={{ singular: 'product', plural: 'products' }}
              />

              <MultiFilterField
                label="Intake"
                value={filters.intakeId}
                onChange={(value) => setFilter('intakeId', value)}
                options={withSelectedMany(intakes.options, filters.intakeId, rowLabels.intakeId, 'Intake')}
                allLabel="All intakes"
                loading={intakes.loading}
                failed={intakes.failed}
                searchPlaceholder="Search intakes…"
                noun={{ singular: 'intake', plural: 'intakes' }}
              />

              <MultiFilterField
                label="Fiscal year"
                value={filters.cycleId}
                onChange={(value) => setFilter('cycleId', value)}
                options={withSelectedMany(cycleOptions, filters.cycleId, rowLabels.cycleId, 'Cycle')}
                allLabel="All fiscal years"
                loading={cycles.loading}
                failed={cycles.failed}
                searchPlaceholder="Search fiscal years…"
                noun={{ singular: 'fiscal year', plural: 'fiscal years' }}
              />

              <MultiFilterField
                label="Region of Focus"
                value={filters.regionId}
                onChange={(value) => setFilter('regionId', value)}
                options={withSelectedMany(regionOptions, filters.regionId, rowLabels.regionId, 'Region')}
                allLabel="All regions"
                loading={regions.loading}
                failed={regions.failed}
                searchPlaceholder="Search regions…"
                noun={{ singular: 'region', plural: 'regions' }}
              />

              <MultiFilterField
                label="Source"
                value={filters.source}
                onChange={(value) => setFilter('source', value)}
                options={sourceOptions}
                allLabel="Any source"
                loading={false}
                failed={false}
                noun={{ singular: 'source', plural: 'sources' }}
              />

              <FilterField
                label="Delivery"
                value={filters.outstanding}
                onChange={(value) => setFilter('outstanding', value)}
                options={outstandingOptions}
                allLabel="Any"
                loading={false}
                failed={false}
              />

              <FilterField
                label="Tracker"
                value={filters.removed}
                onChange={(value) => setFilter('removed', value)}
                options={removedOptions}
                allLabel="On the tracker"
                loading={false}
                failed={false}
              />

              <FilterField
                label="Duplicates"
                value={filters.possibleDuplicate}
                onChange={(value) => setFilter('possibleDuplicate', value)}
                options={duplicateOptions}
                allLabel="All rows"
                loading={false}
                failed={false}
              />
            </div>
          )}

          {restoreError !== null && (
            <Notice
              tone="danger"
              role="alert"
              title={<>Couldn&rsquo;t restore that deliverable.</>}
              className="mt-3"
            >
              {restoreError}
            </Notice>
          )}

          {filtered && (
            <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-border pt-3">
              <p className="m-0 text-xs text-fg-muted">
                {activeFilterCount} {activeFilterCount === 1 ? 'filter' : 'filters'} applied.
              </p>
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                Clear filters
              </Button>
            </div>
          )}

          {/*
            * Explained here, not only at the headers, so a filter set at one
            * breakpoint doesn't silently keep hiding rows at another. The clear
            * button is wide-screen only — below `lg` the FilterSheet has its
            * own, and two buttons with the same label a screen-length apart
            * would read as two different scopes.
            */}
          {table.activeCount > 0 && (
            <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-border pt-3">
              <p className="m-0 text-xs text-fg-muted">
                {table.activeCount} column {table.activeCount === 1 ? 'filter' : 'filters'} applied
                to the {rows.length} {rows.length === 1 ? 'row' : 'rows'} on this page
                {hiddenByColumns > 0 ? `, hiding ${hiddenByColumns}` : ''}.
              </p>
              <span className="hidden lg:contents">
                <Button variant="ghost" size="sm" onClick={table.clearAll}>
                  Clear column filters
                </Button>
              </span>
            </div>
          )}
        </div>

        <p role="status" aria-live="polite" className="sr-only">
          {countLabel}
        </p>

        {error && (
          <Notice
            tone="danger"
            role="alert"
            title={<>Couldn&rsquo;t load client deliverables.</>}
            className="mx-4 my-3"
          >
            <span className="[overflow-wrap:anywhere]">{error}</span>
          </Notice>
        )}

        <div
          aria-busy={loading || undefined}
          className={cx(
            'transition-opacity duration-150 ease-out motion-reduce:transition-none',
            loading && result && 'opacity-60',
          )}
        >
          {/* Below lg a thirteen-column table cannot fit 320px, so rows become a stacked list. */}
          <div className="lg:hidden">
            {/*
              * `FilterSheet` is the header row's mobile equivalent — cards have
              * no header row for the column funnels to hang off below `lg`. It
              * shares the same `headerFor`/`clearAll`, so a filter set here is
              * what the table shows once the viewport widens (one state store).
              */}
            <div className="border-b border-border px-4 py-2">
              <FilterSheet
                keys={TRACKER_COLUMN_KEYS}
                headerFor={table.headerFor}
                activeCount={table.activeCount}
                clearAll={table.clearAll}
              />
            </div>
            {initialLoad ? (
              <ListSkeleton />
            ) : visibleRows.length === 0 ? (
              emptyState
            ) : (
              groups.map((group) => (
                <section key={group.key}>
                  <h2 className="m-0 flex items-baseline gap-2 border-b border-border bg-surface-sunken px-4 py-2">
                    <span className="min-w-0 truncate text-xs font-semibold text-fg">
                      {group.label}
                    </span>
                    <span className="shrink-0 text-2xs text-fg-muted tabular">
                      {group.rows.length} {group.rows.length === 1 ? 'row' : 'rows'}
                    </span>
                  </h2>
                  {group.companyGroups === null ? (
                    <RowCards rows={group.rows} editing={editing} />
                  ) : (
                    group.companyGroups.map((run) => (
                      <section key={run.key}>
                        <h3 className="m-0 flex items-baseline gap-1 border-b border-border px-4 py-1.5 text-2xs font-medium text-fg-muted">
                          <span className="min-w-0 truncate">{run.label}</span>
                          <span className="shrink-0 tabular">· {run.rows.length}</span>
                        </h3>
                        <RowCards rows={run.rows} editing={editing} />
                      </section>
                    ))
                  )}
                </section>
              ))
            )}
          </div>

          <div className="hidden lg:block">
            {initialLoad ? (
              /* `frozenHeader` on BOTH wrappers — skeleton and rows — so the
                 table does not jump into its scroll box when data lands. The
                 default reserve leaves room for the pager below the table. */
              <TableWrap frozenHeader>
                {/*
                  * `min-w` is a FLOOR, not a sum of what the content needs: this
                  * table has no `table-fixed`, so a `<th>` width is a request and
                  * the content decides. Do not carry the quote grid's colgroup
                  * arithmetic across — there is no colgroup here.
                  *
                  * 🔴 **IT IS A STYLE ON `Table`, NOT A CLASS AND NOT A WRAPPER.**
                  * The floor has to shrink as columns are hidden, so it is a
                  * runtime number — and ⛔ Tailwind v4 scans source text, so
                  * `min-w-[${n}px]` emits no rule at all and the table would
                  * simply have no floor, which looks like a table that chose not
                  * to have one. ⚠️ *SUPERSEDED: a spacer `<div>` carried it,
                  * because `Table` took no `style`.* It does now, so the number
                  * sits on the element it describes — a wrapper whose only job
                  * was to hold one style is a workaround for a missing prop, and
                  * `Table.tsx` says three pages had written the same one.
                  */}
                <Table style={{ minWidth: tableWidth }}>
                  <TrackerTableHead
                    headerFor={table.headerFor}
                    columns={columns}
                    frozenOffsets={frozenOffsets}
                    headRowRef={headRowRef}
                  />
                  <TableSkeleton columns={columns} frozenOffsets={frozenOffsets} />
                </Table>
              </TableWrap>
            ) : visibleRows.length === 0 ? (
              emptyState
            ) : (
              <TableWrap frozenHeader>
                <Table style={{ minWidth: tableWidth }}>
                  <TrackerTableHead
                    headerFor={table.headerFor}
                    columns={columns}
                    frozenOffsets={frozenOffsets}
                    headRowRef={headRowRef}
                  />
                  {groups.map((group) => (
                    <TBody key={group.key}>
                      <TR>
                        {/*
                          * A `<th scope="row">` spanning the whole width: the
                          * workbook's bold section rule, and a real row header
                          * for a screen reader rather than a styled cell.
                          *
                          * 🔴 **THE SPAN IS THE LIVE VISIBLE COUNT.**
                          * *SUPERSEDED: `colSpan={COLUMN_COUNT}`*, derived from
                          * the STATIC key list — so the moment a reader could
                          * hide a column the rule ran past the last one and the
                          * browser padded a phantom cell to make the row add up.
                          * It throws nothing and reads as styling. It is now the
                          * same `visibleKeys` the header and the body map over.
                          */}
                        <TH
                          scope="row"
                          colSpan={columns.visibleKeys.length}
                          style={{ top: headHeight }}
                          className="sticky z-[12] bg-surface-sunken font-semibold text-fg shadow-[inset_0_-1px_0_0_var(--hairline)]"
                        >
                          {/* Sticky to the left of the scroll area, so the band's
                              name stays in view when the table is scrolled
                              sideways — it used to slide off with the columns,
                              most visibly beside a frozen column. `inline-flex`
                              matters: a full-width child has no room to stick. */}
                          <span className="sticky left-3 inline-flex max-w-[calc(100vw-4rem)] items-baseline gap-2">
                            <span className="min-w-0 truncate">{group.label}</span>
                            <span className="shrink-0 text-2xs font-normal text-fg-muted tabular">
                              {group.rows.length} {group.rows.length === 1 ? 'row' : 'rows'}
                            </span>
                          </span>
                        </TH>
                      </TR>
                      {group.companyGroups === null
                        ? group.rows.map((row) => (
                            <RowCells
                              key={row.key}
                              row={row}
                              editing={editing}
                              columns={columns}
                              frozenOffsets={frozenOffsets}
                            />
                          ))
                        : group.companyGroups.map((run) => [
                            /*
                             * The company-group sub-header: lighter than the
                             * band and NOT sticky, so it scrolls under the
                             * band heading rather than fighting it for `top`.
                             */
                            <TR key={`h:${run.key}`}>
                              <TH scope="row" colSpan={columns.visibleKeys.length}>
                                {/* Sized on the span, not the cell: `cx` does not
                                    merge, so a text class on `TH` would race the
                                    row header's own `text-sm`. */}
                                <span className="sticky left-3 inline-flex max-w-[calc(100vw-4rem)] items-baseline gap-1 text-xs font-medium text-fg-muted">
                                  <span className="min-w-0 truncate">{run.label}</span>
                                  <span className="shrink-0 tabular">· {run.rows.length}</span>
                                </span>
                              </TH>
                            </TR>,
                            ...run.rows.map((row) => (
                              <RowCells
                                key={row.key}
                                row={row}
                                editing={editing}
                                columns={columns}
                                frozenOffsets={frozenOffsets}
                              />
                            )),
                          ])}
                    </TBody>
                  ))}
                </Table>
              </TableWrap>
            )}
          </div>
        </div>

        {result && (
          <PanelFooter>
            <p className="m-0 text-center text-xs text-fg-muted tabular sm:text-left">
              {total === 0 ? 'No rows to show' : `Showing ${firstRow}–${lastRow} of ${total}`}
              {hiddenByColumns > 0 ? ` · ${hiddenByColumns} hidden by column filters` : ''}
            </p>
            <div className="flex items-center gap-2">
              <span className="hidden text-xs text-fg-subtle tabular sm:inline">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="secondary"
                size="sm"
                className="flex-1 sm:flex-none"
                leadingIcon={<ChevronIcon direction="left" />}
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                className="flex-1 sm:flex-none"
                trailingIcon={<ChevronIcon direction="right" />}
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </PanelFooter>
        )}
      </Panel>
      )}

      {/*
       * ⚠️ Closed at THIS indentation for the same reason the `Panel` above is
       * not re-indented: the alternative is reflowing ~450 untouched lines, in a
       * repo with mixed line endings, to bury a two-line change. Do not "tidy".
       */}
      </InvoiceQuickEdit>
      </QuoteQuickEdit>
    </>
  );
}

/**
 * One table row: the cells the reader has left visible, in the order they have
 * left them, each one's CONTENT from `trackerCell` and its chrome from
 * `COLUMN_CHROME`.
 *
 * ⚠️ Keyed on `row.key` by every caller, never on `row.id` — `id` is `null` on
 * every line nobody has typed against yet, so keying on it would collapse half
 * the table onto one React key.
 *
 * ⚠️ **The verb is decided by `row.id`, in `useTrackerRowWriter`** — `null`
 * means a `PUT` upsert naming the LINE, because there is no overlay row for a
 * `PATCH` to name; only a row with an `id` takes the `PATCH`. A row gains its
 * id from the upsert's response, which is why `onSaved` replaces the row rather
 * than patching it.
 *
 * ⛔ **WHICH IS EXACTLY WHY `row.id` MUST NOT DECIDE WHETHER A ROW IS WRITABLE.**
 * An inclusion row is `id: null` for ever and names a real `line`, so that verb
 * test would upsert against the PARENT LINE and succeed. `populationOf` is read
 * here and handed to `trackerCell`, which withholds every editor on a row
 * `rowIsEditable` refuses — the writer below never learns the difference and
 * does not need to, because nothing on such a row is given the chance to call
 * it.
 *
 * ⚠️ **The writer is still ONE per row, not one per cell.** `useTrackerRowWriter`
 * is called here and handed to every cell, so hiding or reordering columns
 * cannot change how many writers a row has — which it would if the hook had been
 * pushed down into the `map` alongside the cells it serves.
 *
 * **Which cells carry an editor, and why the derived ones must not**, is stated
 * once at `trackerCell` and deliberately not repeated here.
 */
function RowCells({
  row,
  editing,
  columns,
  frozenOffsets,
}: {
  row: ClientDeliverable;
  editing: RowEditing;
  columns: ColumnLayoutController;
  frozenOffsets: Record<string, number>;
}) {
  /*
   * ⚠️ **The writer is built for EVERY row, including the ones that may not be
   * written**, because this is a hook and a hook cannot be called conditionally.
   * That is safe only because nothing on a read-only row is handed it: the gate
   * is `trackerCell` declining to MOUNT a control, not the writer refusing a
   * call. A writer with no control in front of it issues nothing.
   */
  const writer = useDeliverableRowWriter(row, editing);
  const staffOptions = editing.staffOptions;
  const population = populationOf(row);
  const standalone = population === 'standalone';
  // Yellow wins over the neutral standalone step (owner 2026-10-27).
  const dealPlaceholder = isDealPlaceholderRow(row);
  const tint = dealPlaceholder ? DEAL_ROW_TINT : standalone ? STANDALONE_ROW_TINT : undefined;
  const edge = dealPlaceholder ? DEAL_EDGE : standalone ? STANDALONE_EDGE : undefined;
  return (
    /*
     * ⚠️ **`interactive` is WITHHELD from a highlighted row, deliberately.**
     * `TR interactive` paints `hover:bg-surface-hover`, and a `:hover` rule
     * outranks a plain `bg-*` on specificity — so the highlight would wash out
     * under the pointer. That is worst in the dark theme, where `surface-hover`
     * (25% L) and an ordinary row's ground (23.5% L) are all but identical: the
     * row would stop being distinguishable at exactly the moment somebody
     * pointed at it. Answering with a competing `hover:bg-*` of our own is not
     * the fix — two same-specificity hover rules race Tailwind's emitted order,
     * which is the trap the revenue tracker's totals `<th>` records. One rule,
     * not two.
     *
     * ✅ **Nothing is lost by withholding it, because `interactive` here carries
     * NO `onClick`** — the row is not clickable, so its hover was decorative and
     * was pure cost against the highlight. The row keeps every hover that means
     * something, all of it INSIDE the cells: the company link still underlines,
     * and each of the eight editable fields still has its own hover and focus
     * state, which is the affordance that says the value can be changed.
     */
    <TR interactive={tint === undefined} className={tint}>
      {columns.visibleKeys.map((key, index) => {
        const chrome = COLUMN_CHROME[key as TrackerColumnKey];
        const frozen = columns.frozenSideOf(key);
        return (
          <TD
            key={key}
            numeric={chrome.numeric}
            {...(frozen ? { frozen, frozenOffset: frozenOffsets[key] ?? 0 } : {})}
            className={cx(
              chrome.body,
              /*
               * The 3px rule marking a standalone row, on whichever column is
               * FIRST — it marks the row's left edge, not the Client column, so
               * it follows a reorder rather than stranding itself mid-table.
               *
               * ⚠️ **WITHHELD ON A FROZEN CELL, and that is not fussiness.**
               * `box-shadow` is one property, so two `shadow-[…]` utilities on
               * one element do not merge — one wins, and which one is whatever
               * order Tailwind happened to emit them in. A frozen cell's shadow
               * carries its EDGE and the row separator it has to redraw for
               * itself (see `Table.tsx`), so letting this race it would break
               * the freeze to draw a marker. The row still says what it is in
               * words, in the Client cell: colour was never the only signal.
               */
              index === 0 && frozen === undefined && edge,
            )}
          >
            {trackerCell(key, row, writer, staffOptions, population, editing)}
          </TD>
        );
      })}
    </TR>
  );
}

/**
 * One cell's CONTENT, by column key.
 *
 * ⚠️ **The `switch` is exhaustive over `TrackerColumnKey` and the `never` at the
 * end is the point** — a key added to `COLUMN_KEYS` fails to compile here until
 * it has a cell, which is the half `COLUMN_CHROME`'s `Record` cannot enforce (a
 * width and a shimmer are not a value). Between the two, all three render
 * surfaces are pinned by the type checker rather than by a person counting JSX
 * siblings, which is how this page shipped a fourteen-cell skeleton over
 * thirteen columns and nobody saw it.
 *
 * ⚠️ **THE TYPED FIELDS ARE EDITABLE HERE; EVERY DERIVED CELL IS NOT, AND THAT
 * IS THE PATTERN ANY NEW COLUMN FOLLOWS.** Every key of
 * `ClientDeliverableTypedFields` carries an `InlineField`; company, product,
 * intake, project, regions, pending, the documents and the billing status are
 * read through the line at request time and carry no control at all. Adding one
 * would either do nothing or store a copy that drifts against the customer's
 * invoice, with a 200 and nothing failing.
 *
 * ⚠️ **`promised` and `travel` have both left that list, and NEITHER is a
 * counter-example to the rule.** Each is now an OVERRIDE the server reads back —
 * `COALESCE(override, line quantity)` for one, `override ?? derivation` for the
 * other — so the value the cell shows IS the value the control writes, which is
 * the test the rule is really made of.
 *
 * 🔴 **THE EDITOR-HOSTING CELLS HOST THE ONLY IN-PLACE EDITOR FOR THEIR FIELD** —
 * `poc`, `specialist`, `promised`, `completed`, `bookingSpec`, `travel`,
 * `startDate`, `endDate`, `billingNote`, `adsPlan` — and this page has no row
 * detail drawer. Hiding one from the column manager removes the only DESKTOP path
 * to writing it; the sub-`lg` card list still renders every one, which is why
 * none of them is locked. See `LOCKED_COLUMN_KEYS`.
 */
function trackerCell(
  key: string,
  row: ClientDeliverable,
  writer: TrackerRowWriter,
  staffOptions: ComboboxOption[],
  population: RowPopulation,
  editing: RowEditing,
): ReactNode {
  /*
   * 🔴 **ONE GATE, READ ONCE, IN FRONT OF ALL EIGHT EDITORS.** Every typed
   * column below is a ternary on this rather than each deciding for itself —
   * because the failure it prevents is silent and is not a rendering fault: an
   * editor mounted on an inclusion row writes a `PUT` upsert naming the PARENT
   * LINE, which lands on that line's deliverable with a `200`. Eight
   * independent checks is eight chances to add a ninth column without one; this
   * way the gate is impossible to miss and impossible to disagree with itself.
   */
  const editable = rowIsEditable(population);
  // Narrowed ONCE, into a variable the `default` branch can be checked against.
  // `switch (key as TrackerColumnKey)` with a `key as never` in the default
  // would compile for ever — the cast is what the exhaustiveness rests on, so it
  // has to happen here and not at the assignment that is meant to fail.
  const column = key as TrackerColumnKey;
  switch (column) {
    case 'client':
      return <ClientCell row={row} population={population} editing={editing} />;
    case 'companyType':
      return <CompanyTypeCell row={row} />;
    case 'billingStatus':
      return <BillingStatusCell row={row} />;
    case 'regions':
      return <RegionsCell row={row} />;
    /* Read through the LINE at request time, like the regions above it — so there
       is nothing here to write and no editor to gate. An empty cell on a
       standalone row is the data. */
    case 'cities':
      return <CitiesCell row={row} />;
    /* POC — the workbook's own position. Reads `deliveryLead ??
       clientSuccessManager`; only the lead is writable. */
    case 'poc':
      return editable ? (
        <DeliveryLeadField row={row} writer={writer} options={staffOptions} />
      ) : (
        <ReadOnlyPoc row={row} />
      );
    /* The LAE representative — a SHARED field, so on a piece row it reads and
       writes the same value as every sibling piece. */
    case 'specialist':
      return editable ? (
        <SpecialistField
          row={row}
          writer={writer}
          specialists={editing.specialists}
          specialistsError={editing.specialistsError}
        />
      ) : (
        <ReadOnlySpecialist row={row} />
      );
    case 'intake':
      return row.intake ? (
        <span className="block truncate text-xs text-fg-secondary">{row.intake.intakeName}</span>
      ) : (
        <NotRecorded />
      );
    case 'product':
      return row.product ? (
        <span className="block truncate">{row.product.productName}</span>
      ) : (
        <NotRecorded />
      );
    /* Read through the LINE, like Product beside it; nothing here is typed. */
    case 'inclusions':
      return <InclusionsCell row={row} />;
    /*
     * 🔴 NO LONGER DERIVED-ONLY — 2026-09-02. *SUPERSEDED: "Derived: the LINE
     * quantity, never this row's to write."* It is now `COALESCE(overridden,
     * line quantity)`: blank falls through to the line, a number overrides it.
     *
     * That exists because 294 live lines carry a `quantity` contradicting their
     * own description — `quantity = 1` on a line reading "Virtual Masterclasses:
     * 4 Sessions". Correcting the LINE would re-price ~200 issued invoices, and
     * Xero is the book of record for 818 of 819 of them, so the count is
     * corrected here and the document is left alone.
     */
    case 'promised':
      /*
       * ⚠️ On an inclusion row this is the INCLUSION's own count — not the
       * line's quantity and not a share of it — so the figure is rendered
       * exactly as it arrived and is never added to the line's own row. The
       * three promises on one line answer three different questions and their
       * total is not a quantity of anything.
       */
      return editable ? (
        <PromisedField row={row} writer={writer} />
      ) : (
        <ReadOnlyPromised row={row} />
      );
    case 'completed':
      return editable ? <CompletedField row={row} writer={writer} /> : <ReadOnlyCompleted />;
    /* Derived and SIGNED — it moves when Completed is saved. */
    case 'pending':
      return <PendingCell pending={row.pending} />;
    case 'bookingSpec':
      return editable ? (
        <BookingSpecCell row={row} writer={writer} />
      ) : (
        <ReadOnlyBookingSpec row={row} />
      );
    /* An OVERRIDE over a derivation, since 2026-09-09 — the cell shows the
       answer and which of the two it came from. See `TravelField`. */
    case 'travel':
      return editable ? <TravelField row={row} writer={writer} /> : <ReadOnlyTravel row={row} />;
    /* Unbranched: an inclusion row hangs off a real line, so it carries that
       line's quote and invoices and the reader wants them. Nothing here is
       typed, so there is no editor to withhold. */
    case 'documents':
      return <DocumentsCell row={row} />;
    /* Two columns since 2026-10-04, one editor each. On a piece row the editor
       writes the PIECE's own date (InlineTrackerFields routes it). */
    case 'startDate':
      return editable ? (
        <StartDateField row={row} writer={writer} />
      ) : (
        <ReadOnlyDate value={row.startDate} />
      );
    case 'endDate':
      return editable ? (
        <EndDateField row={row} writer={writer} />
      ) : (
        <ReadOnlyDate value={row.endDate} />
      );
    /* The workbook's column P — its own column since 2026-09-02, moved out of
       the Billing Status cell where it had been nested. */
    case 'billingNote':
      return editable ? (
        <BillingNoteField row={row} writer={writer} />
      ) : (
        // The same `text-2xs text-fg-muted` the editor's own display carries.
        <ReadOnlyText
          value={row.billingNote}
          className="text-2xs text-fg-muted [overflow-wrap:anywhere]"
        />
      );
    /* NOT behind `editable`: the note writes through to the source line by
       `key`, so an inclusion row edits its parent line's note. See
       `LineNoteCell`. */
    case 'lineNote':
      return <LineNoteCell row={row} population={population} editing={editing} />;
    case 'adsPlan':
      return editable ? (
        <AdsPlanCell row={row} writer={writer} />
      ) : (
        <ReadOnlyText
          value={row.adsPlan}
          className="text-xs text-fg-secondary [overflow-wrap:anywhere]"
        />
      );
    default: {
      // Exhaustiveness: a new column key lands here as a type error rather than
      // as an empty cell nobody notices.
      const unreached: never = column;
      void unreached;
      return null;
    }
  }
}

/**
 * The Inclusions cell (owner, 2026-10-27) — the line's campaign inclusions as
 * one wrapping line of text, shared by the table and the card list. `—` when
 * there are none or the API predates the key.
 */
function InclusionsCell({ row }: { row: ClientDeliverable }) {
  if (!row.inclusions || row.inclusions.length === 0) return <NotRecorded />;
  return (
    <span className="block text-xs text-fg-secondary [overflow-wrap:anywhere]">
      {inclusionsLabel(row.inclusions)}
    </span>
  );
}

/**
 * The Notes cell — the internal note of the line the row comes from (owner,
 * 2026-10-18), shared by the table and the card list.
 *
 * ⚠️ **Deliberately NOT behind `rowIsEditable`.** That gate exists because an
 * overlay write on an inclusion row would land on the parent line's overlay;
 * this write is keyed by `row.key` and lands on the parent LINE's note, which is
 * exactly what the owner asked an inclusion row to show and edit. Only an
 * `unknown` population — a source this build cannot name — stays read-only.
 */
function LineNoteCell({
  row,
  population,
  editing,
}: {
  row: ClientDeliverable;
  population: RowPopulation;
  editing: RowEditing;
}) {
  if (population === 'unknown') {
    return row.lineNote === undefined ? null : (
      <ReadOnlyText
        value={row.lineNote}
        className="whitespace-pre-wrap text-2xs text-fg-muted [overflow-wrap:anywhere]"
      />
    );
  }
  return (
    <LineNoteTrackerField
      row={row}
      onSaved={editing.onSaved}
      onWritten={editing.onLineNoteWritten}
    />
  );
}

/**
 * The Client cell — the one column that identifies the row, and the one that is
 * locked because of it.
 *
 * ⚠️ **Its `min-w-[18rem]` now lives on `COLUMN_CHROME.client.body`**, with the
 * header's `w-72` and the 288px this column contributes to the table's floor, so
 * the three agree by construction. The note below is why that floor exists at
 * all, and it is kept HERE because it is about this cell's content.
 */
function ClientCell({
  row,
  population,
  editing,
}: {
  row: ClientDeliverable;
  population: RowPopulation;
  editing: RowEditing;
}) {
  return (
    <div className="grid gap-1">
      {/*
       * 🔴 **`min-w`, NOT `max-w`, AND THE NAME IS NO LONGER TRUNCATED** —
       * 2026-09-03, owner's instruction: *"give full width in the table view"*.
       * *SUPERSEDED: `max-w-[16rem]` on the cell and `truncate` on the link.*
       *
       * ⚠️ **A `max-w` was doing the OPPOSITE of what it looks like it was
       * doing, and that is the whole bug.** This table has no `table-fixed` and
       * no colgroup (see the `min-w` note at the `Table`), so auto layout sizes
       * each column from its content's MIN-CONTENT width — and `truncate` sets
       * `overflow: hidden`, which collapses min-content to almost nothing. So
       * the column asked for nearly no width, got nearly none, and the `max-w`
       * capped a width it was never going to reach: `American University of
       * Sharjah` rendered as `American Univer…` in about 90px, on the cell that
       * IDENTIFIES the row. Widening the `max-w` would have changed nothing.
       *
       * `min-w-[18rem]` is what actually claims the width — a floor the
       * algorithm may not go under — and dropping `truncate` lets a long name
       * wrap to a second line instead of being cut. `[overflow-wrap:anywhere]`
       * matches `BookingSpecCell` in this same table and is for the unbroken
       * token (a long URL-ish name) that would otherwise widen the column past
       * its floor.
       *
       * ⚠️ The table's minimum width does NOT need bumping for it: it is a FLOOR
       * and not a sum, so a column that demands more simply grows the table past
       * it. `COLUMN_CHROME.client.width` is that floor's share and is this
       * 18rem in px, so the two agree. Do not carry the quote grid's colgroup
       * arithmetic here.
       */}
      {row.company ? (
        <TextLink
          to={`/companies/${row.company.id}`}
          className="block text-sm [overflow-wrap:anywhere]"
        >
          {row.company.name}
        </TextLink>
      ) : (
        <NotRecorded />
      )}
      {/*
       * The words that say which population this row is in — the tint's meaning
       * on a standalone row, and the ONLY signal on an inclusion one, which
       * carries no tint by the owner's instruction. See `RowMarker`.
       *
       * ⚠️ It sits in the Client cell because that is the cell that identifies
       * the row and the one column that is locked, so the marker is guaranteed
       * to be on screen whatever the reader has hidden or frozen. An inclusion
       * row shares its company, product, intake and document reference with the
       * line it came from, so without this it reads as a duplicate line.
       */}
      <div className="flex flex-wrap items-center gap-1">
        <RowMarker row={row} population={population} />
        <RemovedBadge row={row} />
        <LinkStateBadges row={row} editing={editing} />
        <LinkRowButton row={row} editing={editing} />
        <SplitRowButton row={row} editing={editing} />
        <DeleteRowButton row={row} editing={editing} />
        <RestoreRowButton row={row} editing={editing} />
      </div>
      <PieceMarker row={row} siblings={editing.siblingsOf(row)} />
    </div>
  );
}

/**
 * The sub-`lg` stacked list — the same rows, the same values, one column, and
 * **the same editors**.
 *
 * ⚠️ It is a component per row rather than a `map` returning markup, because
 * each row needs its own `useTrackerRowWriter`. Leaving the cards read-only
 * would make eight fields unreachable on a phone, and this is the tracker's
 * only screen.
 */
function RowCards({ rows, editing }: { rows: ClientDeliverable[]; editing: RowEditing }) {
  return (
    <ul className="m-0 list-none p-0">
      {rows.map((row) => (
        <RowCard key={row.key} row={row} editing={editing} />
      ))}
    </ul>
  );
}

function RowCard({ row, editing }: { row: ClientDeliverable; editing: RowEditing }) {
  const writer = useDeliverableRowWriter(row, editing);
  const population = populationOf(row);
  const standalone = population === 'standalone';
  const dealPlaceholder = isDealPlaceholderRow(row);
  /*
   * ⚠️ **The same gate as the table, read from the same function.** A row that
   * is read-only above `lg` and editable below it would be the worst of both:
   * the phone is this tracker's only screen for many people, so the damaging
   * write would happen exactly where nobody was looking for it.
   */
  const editable = rowIsEditable(population);
  return (
    /*
     * The same highlight below `lg`, tint and marker both. A feature reachable
     * only above a breakpoint is indistinguishable from one that was removed,
     * and this stacked list is the tracker's only screen on a phone. There is no
     * row hover here, so the tint needs no qualification — unlike the table row,
     * which has to withhold `interactive` to keep it.
     */
    <li
      className={cx(
        'border-b border-border px-4 py-3 last:border-b-0',
        dealPlaceholder ? DEAL_ROW_TINT : standalone && STANDALONE_ROW_TINT,
        dealPlaceholder ? DEAL_EDGE : standalone && STANDALONE_EDGE,
      )}
    >
      <div className="min-w-0">
        {row.company ? (
          <Link
            to={`/companies/${row.company.id}`}
            className="block truncate text-sm font-medium text-fg underline-offset-2 decoration-hairline-strong transition-colors duration-150 ease-out hover:underline focus-visible:underline motion-reduce:transition-none"
          >
            {row.company.name}
          </Link>
        ) : (
          <NotRecorded />
        )}
        {row.product && (
          <p className="m-0 truncate text-xs text-fg-muted">{row.product.productName}</p>
        )}
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        {/* First in the row, so it reads before the status rather than after
            three badges — it says what KIND of row this is, which is the thing
            the tint is claiming and the thing a reader needs first. */}
        <RowMarker row={row} population={population} />
        <RemovedBadge row={row} />
        <LinkStateBadges row={row} editing={editing} />
        <Badge tone={BILLING_TONES[row.billing.status]} dot>
          {BILLING_LABELS[row.billing.status]}
        </Badge>
        {/*
         * ⚠️ **Travel LEFT this badge row on 2026-09-09 and is a `<dl>` entry
         * below.** It became editable, and an `InlineField` in a row of read-only
         * status badges reads as a badge — the trigger is the whole affordance,
         * so a control that looks like the two beside it is a control nobody
         * presses. Rendering it in both places was the other option and is worse:
         * two copies of one fact, one of which silently stops being the one the
         * reader acts on.
         */}
        <PendingCell pending={row.pending} />
        <span className="ml-auto flex items-center gap-1">
          <LinkRowButton row={row} editing={editing} />
          <SplitRowButton row={row} editing={editing} />
          <DeleteRowButton row={row} editing={editing} />
          <RestoreRowButton row={row} editing={editing} />
        </span>
      </div>
      {row.piece !== null && (
        <div className="mt-1.5">
          <PieceMarker row={row} siblings={editing.siblingsOf(row)} />
        </div>
      )}

      <dl className="mt-2.5 grid gap-1.5 text-xs">
        <div className="flex items-baseline gap-2">
          <dt className="w-28 shrink-0 text-fg-subtle">Type</dt>
          <dd className="m-0 min-w-0 flex-1">
            <CompanyTypeCell row={row} />
          </dd>
        </div>
        <div className="flex items-baseline gap-2">
          <dt className="w-28 shrink-0 text-fg-subtle">Region of Focus</dt>
          <dd className="m-0 min-w-0 flex-1">
            <RegionsCell row={row} />
          </dd>
        </div>
        {/*
         * Cities follows Region of Focus here as it does in the table, so the two
         * responsive layouts read in the same order — and it is rendered at all
         * for the reason the eight editors are: a value reachable only above a
         * breakpoint is indistinguishable from one that was removed, and this
         * stacked list is the tracker's only screen on a phone.
         *
         * The cities render as wrapping pills here exactly as in the table. The `<dt>`
         * renders unconditionally, like every other row of this `<dl>` — an empty
         * `<dd>` beside a label is what an empty set looks like here, and the
         * label disappearing on some rows would read as a layout fault.
         */}
        <div className="flex items-baseline gap-2">
          <dt className="w-28 shrink-0 text-fg-subtle">Cities</dt>
          <dd className="m-0 min-w-0 flex-1">
            <CitiesCell row={row} />
          </dd>
        </div>
        {/* POC follows Region of Focus here as it does in the table and in the
            workbook, so the two responsive layouts read in the same order. */}
        <div className="flex items-baseline gap-2">
          <dt className="w-28 shrink-0 text-fg-subtle">POC</dt>
          <dd className="m-0 min-w-0 flex-1">
            {editable ? (
              <DeliveryLeadField row={row} writer={writer} options={editing.staffOptions} />
            ) : (
              <ReadOnlyPoc row={row} />
            )}
          </dd>
        </div>
        <div className="flex items-baseline gap-2">
          <dt className="w-28 shrink-0 text-fg-subtle">Specialist</dt>
          <dd className="m-0 min-w-0 flex-1">
            {editable ? (
              <SpecialistField
                row={row}
                writer={writer}
                specialists={editing.specialists}
                specialistsError={editing.specialistsError}
              />
            ) : (
              <ReadOnlySpecialist row={row} />
            )}
          </dd>
        </div>
        <div className="flex items-baseline gap-2">
          <dt className="w-28 shrink-0 text-fg-subtle">Intake</dt>
          <dd className="m-0 min-w-0 flex-1 text-fg-secondary">
            {row.intake ? row.intake.intakeName : <NotRecorded />}
          </dd>
        </div>
        <div className="flex items-baseline gap-2">
          <dt className="w-28 shrink-0 text-fg-subtle">Inclusions</dt>
          <dd className="m-0 min-w-0 flex-1">
            <InclusionsCell row={row} />
          </dd>
        </div>
        {/*
          * "Completed of promised" read as one fact and is two: the left half
          * is typed and the right is the LINE's quantity. Splitting them is
          * what stops a control appearing over a derived figure.
          */}
        <div className="flex items-baseline gap-2">
          <dt className="w-28 shrink-0 text-fg-subtle">Completed</dt>
          <dd className="m-0 min-w-0 flex-1">
            {editable ? <CompletedField row={row} writer={writer} /> : <ReadOnlyCompleted />}
          </dd>
        </div>
        <div className="flex items-baseline gap-2">
          <dt className="w-28 shrink-0 text-fg-subtle">Promised</dt>
          <dd className="m-0 min-w-0 flex-1 text-fg-secondary tabular">
            {editable ? (
              <PromisedField row={row} writer={writer} />
            ) : (
              <ReadOnlyPromised row={row} />
            )}
          </dd>
        </div>
        <div className="flex items-baseline gap-2">
          <dt className="w-28 shrink-0 text-fg-subtle">Booking</dt>
          <dd className="m-0 min-w-0 flex-1">
            {editable ? (
              <BookingSpecCell row={row} writer={writer} />
            ) : (
              <ReadOnlyBookingSpec row={row} />
            )}
          </dd>
        </div>
        {/* Travel follows Booking here as it does in the table, so the two
            responsive layouts read in the same order. A feature reachable only
            above a breakpoint is indistinguishable from one that was removed,
            and this stacked list is the tracker's only screen on a phone. */}
        <div className="flex items-baseline gap-2">
          <dt className="w-28 shrink-0 text-fg-subtle">Travel</dt>
          <dd className="m-0 min-w-0 flex-1">
            {editable ? <TravelField row={row} writer={writer} /> : <ReadOnlyTravel row={row} />}
          </dd>
        </div>
        <div className="flex items-baseline gap-2">
          <dt className="w-28 shrink-0 text-fg-subtle">Billing note</dt>
          <dd className="m-0 min-w-0 flex-1">
            {editable ? (
              <BillingNoteField row={row} writer={writer} />
            ) : (
              <ReadOnlyText
                value={row.billingNote}
                className="text-2xs text-fg-muted [overflow-wrap:anywhere]"
              />
            )}
          </dd>
        </div>
        {row.lineNote !== undefined && (
          <div className="flex items-baseline gap-2">
            <dt className="w-28 shrink-0 text-fg-subtle">Notes</dt>
            <dd className="m-0 min-w-0 flex-1">
              <LineNoteCell row={row} population={population} editing={editing} />
            </dd>
          </div>
        )}
        <div className="flex items-baseline gap-2">
          <dt className="w-28 shrink-0 text-fg-subtle">Invoice/Quote</dt>
          <dd className="m-0 min-w-0 flex-1">
            <DocumentsCell row={row} />
          </dd>
        </div>
        <div className="flex items-baseline gap-2">
          <dt className="w-28 shrink-0 text-fg-subtle">Start date</dt>
          <dd className="m-0 min-w-0 flex-1">
            {editable ? (
              <StartDateField row={row} writer={writer} />
            ) : (
              <ReadOnlyDate value={row.startDate} />
            )}
          </dd>
        </div>
        <div className="flex items-baseline gap-2">
          <dt className="w-28 shrink-0 text-fg-subtle">End date</dt>
          <dd className="m-0 min-w-0 flex-1">
            {editable ? (
              <EndDateField row={row} writer={writer} />
            ) : (
              <ReadOnlyDate value={row.endDate} />
            )}
          </dd>
        </div>
        <div className="flex items-baseline gap-2">
          <dt className="w-28 shrink-0 text-fg-subtle">Ads plan</dt>
          <dd className="m-0 min-w-0 flex-1">
            {editable ? (
              <AdsPlanCell row={row} writer={writer} />
            ) : (
              <ReadOnlyText
                value={row.adsPlan}
                className="text-xs text-fg-secondary [overflow-wrap:anywhere]"
              />
            )}
          </dd>
        </div>
      </dl>
    </li>
  );
}

/**
 * The header row: one cell per VISIBLE column, in the reader's own order.
 *
 * The columns that carry `sortKey` in `TRACKER_COLUMNS` render sort controls;
 * the rest get a funnel and no arrows, because the API cannot order by them — an
 * `aria-sort` on a column the server cannot sort promises a control that is
 * deliberately not rendered.
 *
 * ⚠️ **This row is what `useFrozenOffsets` MEASURES**, so its cells must be
 * exactly the visible keys, in order, one per key — the hook indexes
 * `row.children` positionally. A decorative extra `<th>` here (a spacer, a
 * select-all) would shift every offset by one column and the only symptom would
 * be frozen columns overlapping.
 */
function TrackerTableHead({
  headerFor,
  columns,
  frozenOffsets,
  headRowRef,
}: {
  headerFor: (key: string) => FilterableTHProps;
  columns: ColumnLayoutController;
  frozenOffsets: Record<string, number>;
  headRowRef: RefObject<HTMLTableRowElement | null>;
}) {
  return (
    <THead>
      {/*
       * `useFrozenOffsets` measures the RENDERED width of each header cell — it
       * has to, because this table is auto layout, where a `w-*` is a request the
       * content can outvote, so a frozen column's offset is knowable only from
       * the DOM. That needs a ref on this row.
       *
       * ⚠️ *SUPERSEDED: a hand-rolled `<tr>` carrying `TR`'s class string copied
       * out verbatim, because `TRProps` declared no `ref` and React 19 function
       * components only accept one when their own props do.* **`TR` takes a `ref`
       * now**, so the copy is gone — it was a second definition of a shared
       * component's styling that nothing would have kept in step, and the
       * divergence would have been invisible.
       */}
      <TR ref={headRowRef}>
        {columns.visibleKeys.map((key) => {
          const chrome = COLUMN_CHROME[key as TrackerColumnKey];
          const frozen = columns.frozenSideOf(key);
          return (
            /*
             * ⚠️ *SUPERSEDED: a local `TrackerHeaderCell`, which re-implemented
             * this component's body for the frozen case only, because
             * `FilterableTHProps` had no `frozen`/`frozenOffset`.* **It forwards
             * them to `TH` now**, so the second renderer — and the `aria-sort`
             * mapping it had to keep in step by hand — is deleted.
             *
             * ⚠️ **An unfrozen cell must carry NO `frozen` and NO
             * `frozenOffset`**, so the pair is spread rather than passed as
             * `undefined`: `TH` emits a `left`/`right` inline style whenever
             * `frozen` is set, and a positioned cell paints over the table's
             * collapsed borders for no reason. Same idiom as the body cells.
             */
            <FilterableTH
              key={key}
              {...headerFor(key)}
              numeric={chrome.numeric}
              className={chrome.head}
              {...(frozen ? { frozen, frozenOffset: frozenOffsets[key] ?? 0 } : {})}
            />
          );
        })}
      </TR>
    </THead>
  );
}

function EmptyStateSlot({
  search,
  filtered,
  columnsHidAll,
  onClearColumns,
  onClear,
  hasError,
}: {
  search: string;
  filtered: boolean;
  columnsHidAll: boolean;
  onClearColumns: () => void;
  onClear: () => void;
  hasError: boolean;
}) {
  if (hasError) {
    return (
      <EmptyState
        icon={<TrackerIcon />}
        title="Nothing to show"
        description="See the message above."
      />
    );
  }

  /*
   * The server did return rows — the column filters hid all of them. Saying "no
   * rows match" here would send the reader to change the search or the filters
   * above, neither of which is the cause. The headers go with the table, so the
   * way out has to be offered here.
   */
  if (columnsHidAll) {
    return (
      <EmptyState
        icon={<TrackerIcon />}
        title="Every row on this page is filtered out"
        description="Column filters apply to this page only. Clear them to see it again."
        action={
          <Button variant="secondary" size="sm" onClick={onClearColumns}>
            Clear column filters
          </Button>
        }
      />
    );
  }

  const narrowed = Boolean(search) || filtered;

  return (
    <EmptyState
      icon={<TrackerIcon />}
      title={narrowed ? 'No rows match' : 'The tracker is empty'}
      description={
        /*
         * ⚠️ NEITHER SENTENCE MAY IMPLY DATA IS ON ITS WAY. The tracker is built
         * from live quote and invoice line items, and EMERGE holds very few of
         * those — so an empty tracker is the ordinary state at launch, not a
         * load that has not finished. It says where a row COMES from, which is
         * the actionable half, and promises nothing about when.
         */
        narrowed
          ? 'No line item matches these filters. Clear them to see the whole tracker.'
          : 'A row appears for every line item on a quote or an invoice.'
      }
      action={
        narrowed ? (
          <Button variant="secondary" size="sm" onClick={onClear}>
            Clear search and filters
          </Button>
        ) : null
      }
    />
  );
}
